import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Zap, Image as ImageIcon, X, Loader, Download, Plus, AlertCircle, ArrowUp, Maximize2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageModal from './ImageModal';
import DynamicFormField from './DynamicFormField';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateFieldName, translateCommon, hasTranslation } from '../translations';
import { getBonusWord } from '../utils/textUtils';
import { playSuccessSound } from '../utils/soundUtils';
import { downloadMedia } from '../utils/downloadMedia';
import { normalizeStaticUrl } from '../utils/urlUtils';
import { captureDropPayload, collectDropUrls, localDroppedFiles, resolveDroppedMedia } from '../utils/browserDrop';
import './ModelPlayground.css';

// Функция для применения параметров пресета к модели
// Теперь пресет содержит только базовые поля, остальные параметры применяются на бэкенде
const applyPresetToModel = (preset, model) => {
  const params = { ...model.default_parameters };
  // Параметры пресета (resolution, aspect_ratio, hidden_prompt) применяются на бэкенде
  return params;
};

// Вспомогательная функция для определения типа медиа
const isElevenLabsDialogueModel = (model) => {
  if (!model) return false;
  if (model.model_id === 'elevenlabs/v3/text-to-speech') return false;
  if (model.model_id === 'elevenlabs/text-to-dialogue-v3') return true;
  if (model.input_parameters?.dialogue?.type === 'dialogue') return true;
  return model.slug === 'elevenlabs-dialogue-v3';
};

const isVideoFile = (url) => {
  if (!url || typeof url !== 'string') return false;
  const urlLower = url.toLowerCase();
  if (urlLower.startsWith('data:video/')) return true;
  return urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.webm') || urlLower.includes('.gif') || urlLower.includes('.m4v') || urlLower.includes('.qt') || urlLower.includes('video/quicktime');
};

/** Chrome не воспроизводит data:video/quicktime. Для .mov отдаём тот же файл как video/mp4. */
const createPlayableVideoPreviewUrl = (file) => {
  const name = String(file?.name || '').toLowerCase();
  const type = String(file?.type || '').toLowerCase();
  const isQuickTime = name.endsWith('.mov') || name.endsWith('.qt') || type === 'video/quicktime' || type === 'video/mov';
  if (isQuickTime) {
    return URL.createObjectURL(file.slice(0, file.size, 'video/mp4'));
  }
  return URL.createObjectURL(file);
};

const formVideoMime = (url, fileName) => {
  const probe = `${fileName || ''} ${typeof url === 'string' ? url : ''}`.toLowerCase();
  if (probe.includes('.webm') || probe.includes('video/webm')) return 'video/webm';
  return 'video/mp4';
};

const revokeMediaPreview = (item) => {
  if (!item || typeof item !== 'object') return;
  const seen = new Set();
  for (const key of ['preview', 'url']) {
    const value = item[key];
    if (typeof value === 'string' && value.startsWith('blob:') && !seen.has(value)) {
      seen.add(value);
      URL.revokeObjectURL(value);
    }
  }
};

const isAudioFile = (url) => {
  if (!url) return false;
  const path = url.toLowerCase().split('?')[0];
  return ['.mp3', '.wav', '.m4a', '.ogg', '.aac', '.flac'].some((ext) => path.endsWith(ext) || path.includes(`${ext}?`));
};

const jobToResult = (job) => ({
  id: job.public_id,
  status: job.status === 'failed' ? 'completed' : job.status,
  output_image_url: job.output_image_url,
  audio_tracks: Array.isArray(job.audio_tracks) && job.audio_tracks.length > 0 ? job.audio_tracks : null,
  created_at: job.created_at,
  processing_time_seconds: job.processing_time_seconds,
  bonuses_spent: job.bonuses_spent,
  parameters: job.parameters || {},
});

const seedanceDraftUpscaleCost = (result) => {
  const seconds = Number(result?.parameters?.video_duration ?? result?.parameters?.duration);
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return Math.round(Math.max(1, Math.ceil(seconds)) * getSeedance25TokensPerSecond('text-to-video', '1080p') * 100) / 100;
};

const isSeedance480Draft = (params) => {
  if (!params) return false;
  const isDraft = params.seedance_draft === true || params.draft === true;
  if (!isDraft || !params.evolink_task_id) return false;
  if (String(params.generation_mode || '') === 'draft-to-video') return false;
  const resolution = String(params.resolution || '').toLowerCase();
  // 720p и 1080p уже финальные. Кнопка догенерации только у черновика 480p.
  if (resolution && resolution !== '480p') return false;
  return true;
};

const seedanceDraftCanUpscale = (result) => {
  const params = result?.parameters;
  if (!isSeedance480Draft(params)) return false;
  const expires = params.draft_expires_at;
  if (expires) {
    const ts = typeof expires === 'number'
      ? (expires < 1e12 ? expires * 1000 : expires)
      : Date.parse(String(expires));
    if (Number.isFinite(ts) && ts <= Date.now()) return false;
  }
  return true;
};

/** MIME для blob результата: без type браузер в «Сохранить как...» предлагает .txt */
const guessMediaMimeType = (url, contentTypeHeader) => {
    if (contentTypeHeader) {
    const type = contentTypeHeader.split(';')[0].trim().toLowerCase();
    if (type.startsWith('image/') || type.startsWith('video/') || type.startsWith('audio/')) return type;
  }
  const path = (url || '').toLowerCase().split('?')[0];
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.gif')) return 'image/gif';
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
  if (path.endsWith('.mp4')) return 'video/mp4';
  if (path.endsWith('.webm')) return 'video/webm';
  if (path.endsWith('.mov')) return 'video/quicktime';
  if (path.endsWith('.mp3')) return 'audio/mpeg';
  if (path.endsWith('.wav')) return 'audio/wav';
  if (path.endsWith('.m4a')) return 'audio/mp4';
  if (path.endsWith('.ogg')) return 'audio/ogg';
  return 'image/png';
};

const createTypedMediaBlob = (partsOrBlob, url, response) => {
  const type = guessMediaMimeType(url, response?.headers?.get?.('Content-Type'));
  const source = partsOrBlob instanceof Blob ? [partsOrBlob] : partsOrBlob;
  const currentType = partsOrBlob instanceof Blob ? partsOrBlob.type : '';
  if (currentType && currentType !== 'text/plain' && currentType !== 'application/octet-stream') {
    return partsOrBlob instanceof Blob ? partsOrBlob : new Blob(source, { type: currentType });
  }
  return new Blob(source, { type });
};

/** Сегменты mirror-промпта с подсветкой @тегов и текущего selection (textarea остаётся transparent) */
const buildPromptMirrorParts = (text, selStart = 0, selEnd = 0) => {
  const re = /@(Video|Image|Element|Audio)(\d+)|@(start_image|end_image)/gi;
  const base = [];
  let lastIndex = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      base.push({ type: 'text', content: text.slice(lastIndex, match.index), start: lastIndex });
    }
    const startEnd = match[3];
    const kind = startEnd ? 'image' : (match[1] || '').toLowerCase();
    const num = startEnd ? (startEnd === 'start_image' ? 1 : 2) : parseInt(match[2], 10);
    const colorOffset =
      kind === 'video' ? 0 : kind === 'audio' ? 30 : kind === 'image' ? 10 : 20;
    const colorIndex = colorOffset + (num - 1);
    const colorClass = KLING_ELEMENT_COLORS[colorIndex % KLING_ELEMENT_COLORS.length] || 'kling-el-sky';
    base.push({ type: 'element', content: match[0], className: colorClass, start: match.index });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    base.push({ type: 'text', content: text.slice(lastIndex), start: lastIndex });
  }

  const hasSel = selEnd > selStart;
  if (!hasSel) {
    return base.map((p) => ({ ...p, selected: false }));
  }

  const out = [];
  for (const part of base) {
    const pStart = part.start;
    const pEnd = pStart + part.content.length;
    if (selEnd <= pStart || selStart >= pEnd) {
      out.push({ ...part, selected: false });
      continue;
    }
    const cutA = Math.max(selStart, pStart);
    const cutB = Math.min(selEnd, pEnd);
    if (cutA > pStart) {
      out.push({
        type: part.type,
        content: text.slice(pStart, cutA),
        className: part.className,
        selected: false,
      });
    }
    out.push({
      type: part.type,
      content: text.slice(cutA, cutB),
      className: part.className,
      selected: true,
    });
    if (cutB < pEnd) {
      out.push({
        type: part.type,
        content: text.slice(cutB, pEnd),
        className: part.className,
        selected: false,
      });
    }
  }
  return out;
};

const isVideoModel = (model) => (
  model?.category === 'video_generation' || model?.category === 'video_editing'
);

const generationCostLabel = (model) => {
  if (model?.category === 'music_generation') return 'за трек';
  if (model?.category === 'speech_generation') return 'за озвучку';
  return 'за каждое изображение';
};

const isNanoBanana21Model = (model) => (
  model?.slug === 'nano-banana-2-1'
  || model?.slug === 'nano-banana-2.1'
  || Boolean(model?.model_id?.includes('nano-banana-2.1'))
  || Boolean(model?.model_id?.includes('nano-banana-2-1'))
);

const isNanoBanana2Model = (model) => (
  !isNanoBanana21Model(model) && (
    model?.slug === 'nano-banana-2'
    || Boolean(model?.model_id?.includes('nano-banana-2'))
  )
);

const isFalNanoBananaModel = (model) => (
  isNanoBanana21Model(model)
  || isNanoBanana2Model(model)
  || model?.slug === 'nano-banana-pro'
  || model?.slug === 'nano-banana-pro-v2'
  || Boolean(model?.model_id?.includes('nano-banana-pro'))
);

const hasActiveSubscription = (user) => {
  if (!user?.subscription_expires_at) return false;
  const expiresAt = new Date(user.subscription_expires_at);
  return !Number.isNaN(expiresAt.getTime()) && expiresAt > new Date();
};

// Вспомогательная функция для определения типа файлового поля по имени и описанию
const getFileFieldType = (fieldName, fieldConfig) => {
  // Сначала проверяем имя поля
  if (fieldName === 'video' || fieldName === 'video_url' || fieldName === 'video_urls') {
    return { isVideo: true, isAudio: false };
  }
  if (fieldName === 'audio' || fieldName === 'audio_urls') return { isVideo: false, isAudio: true };
  
  // Затем проверяем расширения файлов в описании
  const description = (fieldConfig?.description || '').toLowerCase();
  const hasVideoExtensions = /\.(mp4|mov|webm|avi|mkv|gif)/i.test(description);
  const hasAudioExtensions = /\.(mp3|wav|m4a|ogg|aac|flac)/i.test(description);
  const hasImageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)/i.test(description);
  
  // Если есть только видео расширения (без изображений и аудио) - это видео
  if (hasVideoExtensions && !hasImageExtensions && !hasAudioExtensions) {
    return { isVideo: true, isAudio: false };
  }
  
  // Если есть только аудио расширения (без изображений и видео) - это аудио
  if (hasAudioExtensions && !hasImageExtensions && !hasVideoExtensions) {
    return { isVideo: false, isAudio: true };
  }
  
  // По умолчанию - изображение
  return { isVideo: false, isAudio: false };
};

// Проверка размера файла по URL (HEAD). Если сервер отдаёт Content-Length и он больше лимита — бросает.
const checkUrlFileSize = async (url, maxSizeMB, fieldLabel = '') => {
  if (!url || typeof url !== 'string' || (!url.startsWith('http://') && !url.startsWith('https://'))) return;
  const maxBytes = maxSizeMB * 1024 * 1024;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  try {
    const res = await fetch(url, { method: 'HEAD', signal: controller.signal });
    const cl = res.headers.get('Content-Length');
    if (cl) {
      const size = parseInt(cl, 10);
      if (!Number.isNaN(size) && size > maxBytes) {
        const label = fieldLabel ? ` (${fieldLabel})` : '';
        throw new Error(`Файл по ссылке слишком большой: ${(size / 1024 / 1024).toFixed(1)} МБ. Максимум ${maxSizeMB} МБ${label}`);
      }
    }
  } catch (e) {
    if (e.message && e.message.includes('слишком большой')) throw e;
    // Сеть/CORS/таймаут — не блокируем, бэкенд проверит размер при обработке
  } finally {
    clearTimeout(timeoutId);
  }
};

// Скрытое поле: не показываем в UI, значение подставляет бэкенд (например end_user_id)
const isHiddenParam = (fieldConfig) => fieldConfig && fieldConfig.hidden === true;

// Является ли поле файловым (для загрузки изображений/видео): file === true в схеме или type === 'array' для image_urls
const isFileParam = (field, fieldName) => {
  if (!field) return false;
  if (field.file === true) return true;
  if (field.type === 'array') {
    const name = (field.name || '').toLowerCase();
    const desc = (field.description || '').toLowerCase();
    if (
      fieldName === 'image_urls' ||
      fieldName === 'image' ||
      fieldName === 'video_urls' ||
      fieldName === 'audio_urls'
    ) {
      return true;
    }
  }
  return false;
};

const isSeedance2Model = (model) =>
  model?.slug === 'seedance-2' || Boolean(model?.model_id?.includes('seedance-2.0'));

const seedance25ImageAspectError = (width, height) => {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  const ratio = w / h;
  if (ratio < 0.4) {
    return `Референсное изображение слишком узкое (${w}×${h}). Соотношение ширины к высоте ${ratio.toFixed(2)}, допустимо от 0.4 до 2.5. Загрузите изображение более широкого формата.`;
  }
  if (ratio > 2.5) {
    return `Референсное изображение слишком широкое (${w}×${h}). Соотношение ширины к высоте ${ratio.toFixed(2)}, допустимо от 0.4 до 2.5. Загрузите изображение менее широкого формата.`;
  }
  return null;
};

const readImageSize = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    resolve({ width: img.naturalWidth, height: img.naturalHeight });
    URL.revokeObjectURL(url);
  };
  img.onerror = () => {
    resolve(null);
    URL.revokeObjectURL(url);
  };
  img.src = url;
});

const isSeedance25Model = (model) =>
  model?.slug === 'seedance-2-5' || model?.model_id === 'bytedance/seedance-2.5/text-to-video';

const SEEDANCE25_VIDEO_MAX_MB = 100;
const SEEDANCE25_VIDEO_FORMAT_ERROR =
  'Видео только в MP4 или MOV, до 100 МБ. WebM и другие файлы, скачанные из браузера, часто собраны не полностью — пересохраните ролик в MP4 или MOV.';

const isSeedance25VideoField = (model, fieldName) =>
  isSeedance25Model(model) && (fieldName === 'video_urls' || fieldName === 'video_url' || fieldName === 'video');

const seedance25VideoFileError = (file) => {
  const name = String(file?.name || '').toLowerCase();
  const type = String(file?.type || '').toLowerCase();
  const extOk = name.endsWith('.mp4') || name.endsWith('.mov');
  const webContainer = type.includes('webm') || name.endsWith('.webm') || name.endsWith('.web');
  if (!extOk || webContainer) return SEEDANCE25_VIDEO_FORMAT_ERROR;
  return null;
};

const seedance25VideoUrlError = (url) => {
  if (typeof url !== 'string' || url.startsWith('blob:') || url.startsWith('data:')) return null;
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  const name = path.rsplit('/', 1).pop() || '';
  if (!name.includes('.')) return null;
  if (name.endsWith('.mp4') || name.endsWith('.mov')) return null;
  return SEEDANCE25_VIDEO_FORMAT_ERROR;
};

const fileInputAccept = (model, fieldName, fieldConfig) => {
  const { isVideo, isAudio } = getFileFieldType(fieldName, fieldConfig);
  if (isVideo && isSeedance25VideoField(model, fieldName)) return 'video/mp4,video/quicktime,.mp4,.mov';
  if (isVideo) return 'video/*';
  if (isAudio) return 'audio/*';
  return 'image/*';
};

const isGptImage25Model = (model) =>
  model?.slug === 'gpt-image-2-5' || Boolean(model?.model_id?.includes('gpt-image-2.5'));

const isGptImage2Model = (model) =>
  !isGptImage25Model(model) && (
    model?.slug === 'gpt-image-2' || Boolean(model?.model_id?.includes('gpt-image-2'))
  );

const getSeedance2GenerationMode = (model, dynamicParams) =>
  dynamicParams?.generation_mode ?? model?.default_parameters?.generation_mode ?? 'text-to-video';

const isSeedance2ReferenceMode = (mode) => String(mode).includes('reference-to-video');

const isSeedance25VideoEditMode = (model, mode) =>
  isSeedance25Model(model) && String(mode) === 'video-edit';

const isSeedance2FastMode = (mode) => String(mode).startsWith('fast/');

const isSeedance2ImageOnlyMode = (mode) => {
  const m = String(mode);
  return m.endsWith('image-to-video') && !isSeedance2ReferenceMode(m);
};

const isSeedance2TextOnlyMode = (mode) => {
  const m = String(mode);
  return m.endsWith('text-to-video') && !isSeedance2ReferenceMode(m);
};

const getSeedance2ResolutionOptions = (mode) =>
  isSeedance2FastMode(mode) ? ['480p', '720p'] : ['480p', '720p', '1080p', '4k'];

const getSeedanceResolutionOptions = (model, mode) =>
  isSeedance25Model(model) ? ['480p', '720p', '1080p'] : getSeedance2ResolutionOptions(mode);

/** Токены за 1 сек (должно совпадать с seedance_2_tokens_per_second на бэкенде). */
const getSeedance2TokensPerSecond = (mode, resolution) => {
  void mode;
  const res = String(resolution || '720p').toLowerCase();
  if (res === '480p') return 4;
  if (res === '1080p') return 20;
  if (res === '4k') return 40;
  return 10;
};

/** Токены за 1 сек (должно совпадать с seedance_25_tokens_per_second на бэкенде). */
const getSeedance25TokensPerSecond = (mode, resolution) => {
  const m = String(mode || 'text-to-video');
  const res = String(resolution || '720p').toLowerCase();
  const premium = m.includes('reference-to-video') || m === 'video-edit';
  if (premium) {
    if (res === '480p') return 9.6;
    if (res === '1080p') return 48;
    return 21;
  }
  if (res === '480p') return 5.3;
  if (res === '1080p') return 28;
  return 11.6;
};

const seedance2FileFieldHasValue = (val) => {
  if (!val) return false;
  if (Array.isArray(val)) return val.some((x) => x && (x.url || x.preview || x.file));
  return !!(val.url || val.preview || val.file);
};

const AUDIO_FILE_EXT_RE = /\.(mp3|wav|m4a|ogg|aac|flac)$/i;

const formatDurationLimitSeconds = (seconds) => {
  const value = Number(seconds);
  if (!isFinite(value)) return seconds;
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
};

const durationLimitLabel = (fieldConfig, fallback = 'Референс') =>
  fieldConfig?.durationLimitLabel || fieldConfig?.name || fallback;

const buildDurationLimitError = (fieldConfig, limitType, seconds) => {
  const label = durationLimitLabel(fieldConfig);
  const formatted = formatDurationLimitSeconds(seconds);
  if (limitType === 'min') {
    return `${label} должен быть не короче ${formatted} сек.`;
  }
  return `${label} должен быть не более ${formatted} сек.`;
};

const isValidAudioFileFieldEntry = (entry) => {
  if (!entry) return true;
  if (entry.file) {
    return entry.file.type?.startsWith('audio/') || AUDIO_FILE_EXT_RE.test(entry.file.name || '');
  }
  const url = String(entry.url || entry.preview || '');
  if (!url || url.startsWith('blob:')) return true;
  if (url.startsWith('data:audio/')) return true;
  if (url.startsWith('data:image/')) return false;
  return AUDIO_FILE_EXT_RE.test(url.split('?')[0]);
};

const promptMediaFileItems = (val) => {
  if (!val) return [];
  const arr = Array.isArray(val) ? val : [val];
  return arr.filter((x) => x && (x.url || x.preview || x.file));
};

/** Теги @Image/@Video/@Audio/@Element для автодополнения в промпте (по одному на каждый загруженный файл). */
const buildPromptMediaTagOptions = (
  fileFieldsList,
  fileFields,
  klingElements = null,
  { lowercaseMediaTags = false } = {},
) => {
  let vNum = 0;
  let iNum = 0;
  let aNum = 0;
  const opts = [];
  const vidLabel = lowercaseMediaTags ? 'video' : 'Video';
  const imgLabel = lowercaseMediaTags ? 'image' : 'Image';
  const audLabel = lowercaseMediaTags ? 'audio' : 'Audio';
  fileFieldsList.forEach(({ name, config }) => {
    const { isVideo, isAudio } = getFileFieldType(name, config);
    const items = promptMediaFileItems(fileFields[name]);

    if (isVideo) {
      items.forEach(() => {
        vNum += 1;
        opts.push({ tag: `@${vidLabel}${vNum}`, type: 'Video', num: vNum });
      });
      return;
    }
    if (isAudio) {
      items.forEach(() => {
        aNum += 1;
        opts.push({ tag: `@${audLabel}${aNum}`, type: 'Audio', num: aNum });
      });
      return;
    }

    if (name === 'start_image_url' && items.length > 0) {
      opts.push({ tag: '@start_image', type: 'Image', num: 1 });
      return;
    }
    if (name === 'end_image_url' && items.length > 0) {
      opts.push({ tag: '@end_image', type: 'Image', num: 2 });
      return;
    }
    if (
      name === 'image_url' &&
      fileFieldsList.some((f) => f.name === 'end_image_url') &&
      items.length > 0
    ) {
      opts.push({ tag: '@start_image', type: 'Image', num: 1 });
      return;
    }

    items.forEach(() => {
      iNum += 1;
      opts.push({ tag: `@${imgLabel}${iNum}`, type: 'Image', num: iNum });
    });
  });
  if (klingElements?.length) {
    klingElements.forEach((_, i) =>
      opts.push({ tag: `@Element${i + 1}`, type: 'Element', num: i + 1 })
    );
  }
  return opts;
};

// Модели Kling v3 / o3 с поддержкой элементов (elements): в промпте @Element1, @Element2 и т.д. + video_url для edit.
const KLING_MOTION_CONTROL_SLUGS = new Set(['kling-v3-motion-control', 'kling-v2-6-motion-control']);
const isKlingMotionControlModel = (model) => KLING_MOTION_CONTROL_SLUGS.has(model?.slug);

const isKlingWithElements = (model) => {
  if (!model) return false;
  const slug = model.slug || '';
  const modelId = model.model_id || '';
  return (
    slug === 'kling-o3-i2v' ||
    modelId === 'fal-ai/kling-video/o3/image-to-video' ||
    slug === 'kling-o3-v2v' ||
    modelId === 'fal-ai/kling-video/o3/video-to-video/edit' ||
    slug === 'kling-v3-i2v' ||
    slug === 'kling-v3-t2v' ||
    modelId.includes('kling-v3.0') ||
    (modelId.includes('kling-video/v3/') && !modelId.includes('o3'))
  );
};

// Цвета для подсветки элементов в промпте (по индексу 1-based)
const KLING_ELEMENT_COLORS = [
  'kling-el-sky',   // Element1
  'kling-el-rose',  // Element2
  'kling-el-green', // Element3
  'kling-el-amber', // Element4
  'kling-el-violet',// Element5
  'kling-el-cyan',  // Element6
];

// Получаем название поля: из конфига (name) или перевод из справочника
// Эта функция используется внутри компонента, где доступен language

function ModelPlayground({ model, onBack, currentBalance, onBalanceUpdate, initialPresetId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [prompt, setPrompt] = useState('');
  // Универсальное состояние для всех file-полей: ключ - имя поля, значение - массив или объект
  const [fileFields, setFileFields] = useState({});
  // Элементы для Kling v3/o3: массив { frontal_image_url, reference_image_urls, video_url } (значения — объект { url, file, preview } или строка URL)
  const [klingElements, setKlingElements] = useState([]);
  // Состояние для хранения длительности медиа (видео или аудио) для моделей, где стоимость зависит от длительности
  const [videoDuration, setVideoDuration] = useState(null);
  // Состояние для хранения размеров исходного изображения для crystal-upscaler (для пересчета при изменении scale)
  const [sourceImageDimensions, setSourceImageDimensions] = useState(null);
  
  // Получаем название поля: приоритет переводу из справочника
  const getFieldDisplayName = useCallback((fieldName, fieldConfig) => {
    // Приоритет: перевод из справочника > name из БД (если не совпадает с fieldName) > fieldName
    if (hasTranslation(fieldName, language)) {
      // Если есть перевод в справочнике, используем его
      return translateFieldName(fieldName, language, fieldName);
    } else if (fieldConfig?.name && fieldConfig.name !== fieldName) {
      // Если name из БД существует и не равно fieldName (уже переведено в БД), используем его
      return fieldConfig.name;
    } else {
      // Используем name из БД, если есть, иначе fieldName
      return fieldConfig?.name || fieldName;
    }
  }, [language]);
  const [isProcessing, setIsProcessing] = useState(false); // Используется только для отображения индикатора загрузки
  const [activeJobId, setActiveJobId] = useState(null);
  const [falQueueStatus, setFalQueueStatus] = useState(null);
  const [result, setResultRaw] = useState(null);
  // Обёртка: нормализует output_image_url (абсолютный URL → относительный путь)
  // чтобы запросы шли через прокси dev-сервера, а не напрямую на backend_url
  const setResult = useCallback((value) => {
    if (value && value.output_image_url) {
      setResultRaw({ ...value, output_image_url: normalizeStaticUrl(value.output_image_url) });
    } else {
      setResultRaw(value);
    }
  }, []);
  const [errorMessage, setErrorMessage] = useState(null); // Сообщение об ошибке для отображения на странице
  const [viewingImage, setViewingImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Состояние для защиты от повторных нажатий
  const isSubmittingRef = React.useRef(false); // Ref для защиты от повторных нажатий (синхронная проверка)
  const [isViewingMode, setIsViewingMode] = useState(false); // Режим просмотра существующей генерации
  const [activePreset, setActivePreset] = useState(null); // Активный пресет
  const [presetInputs, setPresetInputs] = useState({}); // Значения полей из пресета (текстовые поля)
  const [presetFixedInputs, setPresetFixedInputs] = useState({}); // Фиксированные входы из пресета (не показываются пользователю)
  const [presetModels, setPresetModels] = useState({}); // Модели из многошагового пресета (slug -> model data)
  const videoRef = React.useRef(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageLoadProgress, setImageLoadProgress] = useState(0); // прогресс загрузки 0-100
  const [imageBlobUrl, setImageBlobUrl] = useState(null); // blob URL загруженного изображения
  const [isDownloading, setIsDownloading] = useState(false);
  const resultImageRef = React.useRef(null);
  const imageRetryCountRef = React.useRef(0); // для одной автоматической повторной загрузки при обрыве
  const imageFetchControllerRef = React.useRef(null); // AbortController для fetch загрузки
  const presetLoadInFlightRef = React.useRef(null);

  // useEffect для загрузки результата изображения через fetch с прогрессом
  useEffect(() => {
    // Определяем URL изображения из результата
    const imageUrl = result?.output_image_url;
    
    // Если нет результата, нет URL, или это видео — очищаем и выходим
    if (!imageUrl || imageUrl === 'None' || imageUrl === 'null' || imageUrl.trim() === '' || isVideoFile(imageUrl) || isAudioFile(imageUrl)) {
      // Очищаем предыдущий blob URL
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
        setImageBlobUrl(null);
      }
      setImageLoading(false);
      setImageLoadError(false);
      setImageLoadProgress(0);
      return;
    }

    // Отменяем предыдущую загрузку если была
    if (imageFetchControllerRef.current) {
      imageFetchControllerRef.current.abort();
    }

    // Очищаем предыдущий blob URL
    if (imageBlobUrl) {
      URL.revokeObjectURL(imageBlobUrl);
      setImageBlobUrl(null);
    }

    const controller = new AbortController();
    imageFetchControllerRef.current = controller;
    let retryCount = 0;
    const maxRetries = 2;

    const fetchImage = async (url) => {
      setImageLoading(true);
      setImageLoadError(false);
      setImageLoadProgress(0);

      try {
        const response = await fetch(url, {
          signal: controller.signal,
          cache: 'no-cache',
        });

        if (!response.ok) {
          const err = new Error(`HTTP ${response.status}`);
          err.status = response.status;
          throw err;
        }

        const contentLength = response.headers.get('Content-Length');
        const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;

        // Если браузер поддерживает ReadableStream и есть Content-Length — показываем прогресс
        if (response.body && totalBytes > 0) {
          const reader = response.body.getReader();
          const chunks = [];
          let receivedBytes = 0;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            receivedBytes += value.length;
            const progress = Math.round((receivedBytes / totalBytes) * 100);
            setImageLoadProgress(progress);
          }

          // Собираем все чанки в один Blob (с MIME — иначе «Сохранить как...» даёт .txt)
          const blob = createTypedMediaBlob(chunks, url, response);
          
          // Проверяем что загрузка не была отменена
          if (controller.signal.aborted) return;

          // Проверяем, что размер совпадает (защита от обрыва)
          if (blob.size < totalBytes) {
            throw new Error(`Incomplete download: ${blob.size}/${totalBytes} bytes`);
          }

          const blobUrl = URL.createObjectURL(blob);
          setImageBlobUrl(blobUrl);
          setImageLoading(false);
          setImageLoadProgress(100);
          imageRetryCountRef.current = 0;
        } else {
          // Fallback: нет Content-Length или нет ReadableStream — грузим как blob целиком
          const rawBlob = await response.blob();
          if (controller.signal.aborted) return;
          const blob = createTypedMediaBlob(rawBlob, url, response);
          
          const blobUrl = URL.createObjectURL(blob);
          setImageBlobUrl(blobUrl);
          setImageLoading(false);
          setImageLoadProgress(100);
          imageRetryCountRef.current = 0;
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // Отменено пользователем, не ошибка

        console.error('Ошибка загрузки изображения:', url, err);

        if (retryCount < maxRetries && !(err.status >= 400 && err.status < 500)) {
          // Автоматическая повторная попытка (не ретраим 4xx — файла нет на сервере)
          retryCount++;
          const retryUrl = url + (url.includes('?') ? '&' : '?') + '_retry=' + Date.now();
          setTimeout(() => {
            if (!controller.signal.aborted) {
              fetchImage(retryUrl);
            }
          }, 1500 * retryCount); // Увеличивающая задержка
        } else {
          setImageLoading(false);
          setImageLoadError(true);
        }
      }
    };

    fetchImage(imageUrl);

    // Cleanup
    return () => {
      controller.abort();
      imageFetchControllerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.output_image_url]);

  // Очищаем blob URL при размонтировании компонента
  useEffect(() => {
    return () => {
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const promptTextareaRef = useRef(null);
  const adjustPromptTextareaHeight = useCallback((el = promptTextareaRef.current) => {
    if (!el) return;
    const minHeight = 100;
    el.style.transition = 'none';
    if (!el.value) {
      el.style.height = `${minHeight}px`;
      requestAnimationFrame(() => {
        el.style.transition = '';
      });
      return;
    }
    // Сохраняем позицию курсора перед сбросом высоты — иначе reflow двигает курсор
    const selStart = el.selectionStart;
    const selEnd = el.selectionEnd;
    // Сброс к минимуму перед измерением — иначе scrollHeight не уменьшается при удалении
    el.style.height = `${minHeight}px`;
    const newHeight = Math.max(el.scrollHeight, minHeight);
    el.style.height = `${newHeight}px`;
    // Восстанавливаем позицию курсора после изменения высоты
    el.setSelectionRange(selStart, selEnd);
    requestAnimationFrame(() => {
      el.style.transition = '';
    });
  }, []);
  const [elementSuggestions, setElementSuggestions] = useState({ show: false, start: 0, query: '', selectedIndex: 0 });
  const [promptSel, setPromptSel] = useState({ start: 0, end: 0 });
  const updatePromptSel = useCallback((el) => {
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setPromptSel((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);
  
  // Динамические параметры (все кроме prompt, image_input, image, input_image и deprecated полей)
  const [dynamicParams, setDynamicParams] = useState(() => {
    const initialParams = {};
    
    // ВАЖНО: Сначала устанавливаем все значения из БД (default_parameters)
    // Это гарантирует, что значения из БД имеют приоритет
    if (model.default_parameters) {
      Object.keys(model.default_parameters).forEach(key => {
        // Пропускаем только file поля и prompt (определяем динамически)
        const isFile = isFileParam(model.input_parameters?.[key], key);
        if (isHiddenParam(model.input_parameters?.[key])) {
          return;
        }
        if (key !== 'prompt' && !isFile) {
          initialParams[key] = model.default_parameters[key];
        }
      });
    }
    
    if (model.input_parameters) {
      Object.entries(model.input_parameters).forEach(([key, config]) => {
        // Пропускаем prompt и все поля для загрузки файлов - у них кастомная отрисовка (определяем динамически)
        const isFile = isFileParam(config, key);
        if (key === 'prompt' || isFile) {
          return;
        }
        if (isHiddenParam(config)) {
          return;
        }
        // Пропускаем устаревшие (deprecated) поля
        const description = config?.description || '';
        const isDeprecated = description.toLowerCase().includes('deprecated') || 
                           description.toLowerCase().includes('устарело') ||
                           (description.toLowerCase().includes('используйте') && description.toLowerCase().includes('вместо'));
        if (isDeprecated) {
          return;
        }
        
        // Если значение уже установлено из БД (в строках 33-46), не перезаписываем его
        if (key in initialParams) {
          // Применяем только бизнес-правила для значений из БД
          if (key === 'seed' && initialParams[key] === 0) {
            initialParams[key] = -1;
          }
          return; // Пропускаем дальнейшую обработку
        }
        
        // Если значения нет в БД, берем из config.default (Replicate)
        let defaultValue = config.default;
        
        // Бизнес-правила (применяются только если значение не задано в БД):
        // Для seed: по умолчанию -1 вместо 0
        if (key === 'seed' && defaultValue === 0) {
          defaultValue = -1;
        }
        
        // Для boolean полей (speed, nsfw и т.д.): по умолчанию false
        if (config.type === 'boolean' && defaultValue === undefined) {
          defaultValue = false;
        }
        
        // Устанавливаем значение из config.default (если не было в БД)
        if (defaultValue !== undefined && defaultValue !== null) {
          initialParams[key] = defaultValue;
        }
      });
    }
    return initialParams;
  });
  const [seedanceDraft, setSeedanceDraft] = useState(false);

  // Состояние для ошибок валидации
  const [validationErrors, setValidationErrors] = useState({});

  // Handler для изменения динамических параметров
  const handleDynamicParamChange = useCallback((paramName, value) => {
    setDynamicParams(prev => ({
      ...prev,
      [paramName]: value
    }));
  }, []);

  // Handler для изменения ошибок валидации
  const handleValidationChange = useCallback((fieldName, error) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[fieldName] = error;
      } else {
        delete newErrors[fieldName];
      }
      return newErrors;
    });
  }, []);

  // Проверка наличия ошибок валидации
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  // Функция для проверки, является ли поле deprecated
  const isFieldDeprecated = useCallback((field) => {
    if (!field) return true;
    const description = field.description?.toLowerCase() || '';
    return description.includes('deprecated') || 
           description.includes('устарело') ||
           (description.includes('используйте') && description.includes('вместо'));
  }, []);

  const isFileField = useCallback((field, fieldName) => isFileParam(field, fieldName), []);

  // Получаем все file-поля из input_parameters динамически (без хардкода)
  // Порядок соответствует порядку в input_parameters из БД
  const getFileFields = useCallback(() => {
    if (!model.input_parameters) return [];
    
    const fileFieldsList = [];
    
    // Обрабатываем все file-поля из input_parameters в том порядке, как они заданы в БД
    for (const [fieldName, field] of Object.entries(model.input_parameters)) {
      if (isFileField(field, fieldName) && !isFieldDeprecated(field)) {
        fileFieldsList.push({ name: fieldName, config: field });
      }
    }
    
    // Порядок сохраняется таким, как в input_parameters из БД (без сортировки)
    return fileFieldsList;
  }, [model.input_parameters, isFieldDeprecated, isFileField]);

  // Seedance 2.0: в режиме «только текст» не показываем и не отправляем поля изображений
  // Multi-step пресет: показываем только поля из userInputs (остальные подставляет бэкенд)
  const getVisibleFileFields = useCallback(() => {
    const list = getFileFields();

    // Для multi-step пресета: показываем только файловые поля из userInputs
    if (activePreset?.isMultiStep && activePreset?.userInputs?.length > 0) {
      const userFileFieldNames = activePreset.userInputs
        .filter(input => input.type === 'file')
        .map(input => input.fieldName || input.name);
      return list.filter(({ name }) => userFileFieldNames.includes(name));
    }

    if (isSeedance2Model(model) || isSeedance25Model(model)) {
      const mode = getSeedance2GenerationMode(model, dynamicParams);
      if (isSeedance2TextOnlyMode(mode)) {
        return list.filter(
          ({ name }) =>
            !['image_url', 'end_image_url', 'image_urls', 'video_urls', 'audio_urls'].includes(name)
        );
      }
      if (isSeedance2ImageOnlyMode(mode)) {
        return list.filter(
          ({ name }) => !['image_urls', 'video_urls', 'audio_urls'].includes(name)
        );
      }
      if (isSeedance25VideoEditMode(model, mode)) {
        const byName = Object.fromEntries(list.map((item) => [item.name, item]));
        return ['video_urls', 'image_urls', 'audio_urls'].flatMap((name) => {
          const item = byName[name];
          if (!item) return [];
          if (name !== 'video_urls') return [item];
          return [{
            name,
            config: {
              ...item.config,
              required: true,
              name: 'Видео для редактирования',
              description:
                'Первое видео — то, которое правим (@video1). Дополнительные ролики — референс движения. Только MP4 или MOV, каждое 4–30 сек, суммарно до 30 сек, до 10 файлов, до 100 МБ.',
            },
          }];
        });
      }
      if (isSeedance2ReferenceMode(mode)) {
        return list.filter(({ name }) => !['image_url', 'end_image_url'].includes(name));
      }
    }
    return list;
  }, [getFileFields, activePreset, model, dynamicParams.generation_mode, model.default_parameters?.generation_mode]);

  const isSeedance2Unified = isSeedance2Model(model) || isSeedance25Model(model);
  const seedance2Mode = getSeedance2GenerationMode(model, dynamicParams);
  const seedance2PromptRefs =
    isSeedance2Unified && (
      isSeedance2ReferenceMode(seedance2Mode) || isSeedance25VideoEditMode(model, seedance2Mode)
    );

  // Live-подсветка выделения в mirror: у textarea select часто только на mouseup
  useEffect(() => {
    if (!(isKlingWithElements(model) || seedance2PromptRefs)) return undefined;
    const onSelectionChange = () => {
      const el = promptTextareaRef.current;
      if (!el || document.activeElement !== el) return;
      updatePromptSel(el);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [model, seedance2PromptRefs, updatePromptSel]);

  // Nano Banana 2: 0.5K снят, старое значение переключаем на 1K
  useEffect(() => {
    if (!isNanoBanana2Model(model)) return;
    const current = dynamicParams.resolution ?? model.default_parameters?.resolution;
    if (current && String(current).toLowerCase().includes('0.5')) {
      handleDynamicParamChange('resolution', '1K');
    }
  }, [model.slug, model.model_id, dynamicParams.resolution]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seedance 2/2.5: при смене режима сбрасываем недопустимое разрешение (Fast без 1080p; 2.5 — 480p/720p/1080p)
  useEffect(() => {
    if (!isSeedance2Unified) return;
    const allowed = getSeedanceResolutionOptions(model, seedance2Mode);
    const current = dynamicParams.resolution ?? model.default_parameters?.resolution;
    if (current && !allowed.includes(current)) {
      handleDynamicParamChange('resolution', allowed.includes('720p') ? '720p' : allowed[0]);
    }
    if (isSeedance25Model(model) && isSeedance2ImageOnlyMode(seedance2Mode)) {
      const aspect = dynamicParams.aspect_ratio ?? model.default_parameters?.aspect_ratio;
      if (aspect && aspect !== 'auto') {
        handleDynamicParamChange('aspect_ratio', 'auto');
      }
    }
  }, [isSeedance2Unified, seedance2Mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Функция для определения длительности видео
  const getVideoDuration = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const name = String(file?.name || '').toLowerCase();
      const looksLikeVideo = file.type.startsWith('video/') || name.endsWith('.mp4') || name.endsWith('.mov');
      if (!looksLikeVideo) {
        resolve(null);
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Не удалось загрузить видео'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  }, []);

  // Функция для получения размеров видео (ширина x высота)
  const getVideoDimensions = useCallback((file) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('video/')) {
        resolve(null);
        return;
      }
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Не удалось загрузить видео'));
      };
      video.src = URL.createObjectURL(file);
    });
  }, []);

  // Функция для определения длительности аудио
  const getAudioDuration = useCallback((file) => {
    return new Promise((resolve, reject) => {
      // Проверяем, является ли файл аудио
      if (!file.type.startsWith('audio/')) {
        resolve(null);
        return;
      }

      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      
      audio.onloadedmetadata = () => {
        window.URL.revokeObjectURL(audio.src);
        resolve(audio.duration);
      };
      
      audio.onerror = () => {
        window.URL.revokeObjectURL(audio.src);
        reject(new Error('Не удалось загрузить аудио'));
      };
      
      audio.src = URL.createObjectURL(file);
    });
  }, []);

  // Функция для получения размеров изображения из File или URL
  const getImageDimensions = useCallback((fileOrUrl) => {
    return new Promise((resolve, reject) => {
      // Если это File объект
      if (fileOrUrl instanceof File) {
        // Проверяем, является ли файл изображением
        if (!fileOrUrl.type.startsWith('image/')) {
          resolve(null);
          return;
        }

        const img = document.createElement('img');
        img.onload = () => {
          window.URL.revokeObjectURL(img.src);
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        
        img.onerror = () => {
          window.URL.revokeObjectURL(img.src);
          reject(new Error('Не удалось загрузить изображение'));
        };
        
        img.src = URL.createObjectURL(fileOrUrl);
      } else if (typeof fileOrUrl === 'string') {
        // Если это URL (строка)
        const img = document.createElement('img');
        img.crossOrigin = 'anonymous'; // Для CORS
        
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        
        img.onerror = () => {
          reject(new Error('Не удалось загрузить изображение по URL'));
        };
        
        img.src = fileOrUrl;
      } else {
        resolve(null);
      }
    });
  }, []);

  // Универсальный обработчик загрузки файлов для file-поля
  const handleFileSelect = useCallback(async (fieldName, files, supportsMultiple = false, maxCount = 1) => {
    const fileArray = Array.from(files);
    
    // Определяем, поддерживает ли поле множественную загрузку динамически из конфига
    const fieldConfig = model.input_parameters?.[fieldName];
    const isMultiple = supportsMultiple || (fieldConfig && (fieldConfig.type === 'array' || fieldConfig.multiple === true));
    // Максимальное количество файлов: из конфига или по умолчанию
    let maxFiles = fieldConfig?.maxItems || fieldConfig?.max || (isMultiple ? 10 : 1);
    
    const currentFiles = fileFields[fieldName] || [];
    const currentArray = Array.isArray(currentFiles) ? currentFiles : (currentFiles ? [currentFiles] : []);
    
    if (currentArray.length + fileArray.length > maxFiles) {
      toast.error(`Максимум ${maxFiles} файл${maxFiles === 1 ? '' : 'ов'} для этого поля`);
      return;
    }

    // Проверяем размер файлов в зависимости от типа поля
    // Определяем тип файла по имени поля и расширениям в описании
    // Проверяем тип поля с помощью вспомогательной функции
    const { isVideo: isVideoField, isAudio: isAudioField } = getFileFieldType(fieldName, fieldConfig);
    
    // Максимальный размер файла: из конфига поля (maxFileSize в МБ), из конфига модели (model.max_file_size_mb), или по умолчанию
    let maxFileSizeMB = fieldConfig?.maxFileSize || model.max_file_size_mb;
    if (!maxFileSizeMB) {
      // Значения по умолчанию в зависимости от типа поля
      maxFileSizeMB = isVideoField ? 100 : isAudioField ? 50 : 20;
    }
    if (isSeedance25VideoField(model, fieldName)) {
      maxFileSizeMB = SEEDANCE25_VIDEO_MAX_MB;
    }
    const maxFileSize = maxFileSizeMB * 1024 * 1024; // Конвертируем МБ в байты
    const minDurationSeconds = Number(fieldConfig?.minDurationSeconds);
    const maxDurationSeconds = Number(fieldConfig?.maxDurationSeconds);
    const hasMinDuration = isFinite(minDurationSeconds) && minDurationSeconds > 0;
    const hasMaxDuration = isFinite(maxDurationSeconds) && maxDurationSeconds > 0;
    const durationLimitScope = fieldConfig?.durationLimitScope || 'each';
    let pendingDurationSeconds = 0;
    
    for (const file of fileArray) {
      let fileDurationSeconds = null;
      if (file.size > maxFileSize) {
        toast.error(`Файл ${file.name} слишком большой (макс. ${maxFileSizeMB}MB)`);
        continue;
      }

      if (isAudioField) {
        const isAudioMime = file.type.startsWith('audio/');
        const isAudioExt = /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(file.name);
        if (!isAudioMime && !isAudioExt) {
          toast.error('В «Референсное аудио» загружайте только MP3 или WAV, не изображения.');
          continue;
        }
      }
      if (isAudioField && (hasMinDuration || hasMaxDuration)) {
        try {
          fileDurationSeconds = await getAudioDuration(file);
          if (fileDurationSeconds && isFinite(fileDurationSeconds)) {
            const currentDurationSeconds = currentArray.reduce(
              (sum, item) => sum + (Number(item?.durationSeconds) || 0),
              0
            );
            const checkedDurationSeconds =
              durationLimitScope === 'sum'
                ? currentDurationSeconds + pendingDurationSeconds + fileDurationSeconds
                : fileDurationSeconds;
            if (hasMinDuration && durationLimitScope !== 'sum' && checkedDurationSeconds < minDurationSeconds) {
              toast.error(buildDurationLimitError(fieldConfig, 'min', minDurationSeconds));
              continue;
            }
            if (hasMaxDuration && checkedDurationSeconds > maxDurationSeconds) {
              toast.error(buildDurationLimitError(fieldConfig, 'max', maxDurationSeconds));
              continue;
            }
            pendingDurationSeconds += fileDurationSeconds;
          }
        } catch (error) {
          console.error('Ошибка определения длительности аудио:', error);
        }
      }
      if (
        isSeedance25VideoEditMode(model, getSeedance2GenerationMode(model, dynamicParams)) &&
        fieldName === 'audio_urls' &&
        fileDurationSeconds &&
        fileDurationSeconds < 2
      ) {
        toast.error('Референсное аудио должно быть не короче 2 сек.');
        continue;
      }
      if (isSeedance25VideoField(model, fieldName)) {
        const formatError = seedance25VideoFileError(file);
        if (formatError) {
          toast.error(formatError);
          continue;
        }
      } else if (isVideoField && !file.type.startsWith('video/')) {
        toast.error('Загрузите видеофайл (MP4, MOV и т.д.).');
        continue;
      }
      if (isVideoField && (hasMinDuration || hasMaxDuration)) {
        try {
          fileDurationSeconds = await getVideoDuration(file);
          if (fileDurationSeconds && isFinite(fileDurationSeconds)) {
            const currentDurationSeconds = currentArray.reduce(
              (sum, item) => sum + (Number(item?.durationSeconds) || 0),
              0
            );
            const checkedDurationSeconds =
              durationLimitScope === 'sum'
                ? currentDurationSeconds + pendingDurationSeconds + fileDurationSeconds
                : fileDurationSeconds;
            if (hasMinDuration && durationLimitScope !== 'sum' && checkedDurationSeconds < minDurationSeconds) {
              toast.error(buildDurationLimitError(fieldConfig, 'min', minDurationSeconds));
              continue;
            }
            if (hasMaxDuration && checkedDurationSeconds > maxDurationSeconds) {
              toast.error(buildDurationLimitError(fieldConfig, 'max', maxDurationSeconds));
              continue;
            }
            pendingDurationSeconds += fileDurationSeconds;
          }
        } catch (error) {
          console.error('Ошибка определения длительности видео:', error);
        }
      }
      if (
        isSeedance25VideoEditMode(model, getSeedance2GenerationMode(model, dynamicParams)) &&
        fieldName === 'video_urls' &&
        fileDurationSeconds
      ) {
        if (fileDurationSeconds < 4) {
          toast.error('Видео для редактирования должно быть не короче 4 сек.');
          continue;
        }
        if (fileDurationSeconds > 30) {
          toast.error('Видео для редактирования должно быть не длиннее 30 сек.');
          continue;
        }
      }
      if (!isVideoField && !isAudioField && !file.type.startsWith('image/')) {
        toast.error('Загрузите файл изображения (JPEG, PNG, WebP).');
        continue;
      }
      if (
        isSeedance25Model(model) &&
        !isVideoField &&
        !isAudioField &&
        (fieldName === 'image_url' || fieldName === 'end_image_url' || fieldName === 'image_urls')
      ) {
        const size = await readImageSize(file);
        const aspectError = seedance25ImageAspectError(size?.width, size?.height);
        if (aspectError) {
          toast.error(aspectError);
          continue;
        }
      }

      // Для моделей, где стоимость зависит от длительности медиа, определяем длительность при загрузке файла
      const needsVideoDuration = (model.slug === 'pixverse-lipsync' || 
                                   model.slug === 'runway-gen4-aleph' || 
                                   model.model_id?.toLowerCase().includes('pixverse/lipsync') ||
                                   model.model_id?.toLowerCase().includes('gen4-aleph'));
      
      const needsAudioDuration = (model.slug === 'omni-human' || 
                                  model.model_id?.toLowerCase().includes('omni-human') ||
                                  model.model_id?.toLowerCase().includes('bytedance/omni-human'));
      
      // Для kling motion-control (v2.6 / v3) проверяем длительность видео в зависимости от character_orientation
      const isKlingMotionControl = isKlingMotionControlModel(model);
      
      // Kling O3 video-to-video edit: стоимость от длительности video_url и валидация мин. размера (720px по высоте)
      const isKlingO3V2v = model.slug === 'kling-o3-v2v' || model.model_id?.toLowerCase().includes('video-to-video/edit');
      if (isKlingO3V2v && fieldName === 'video_url' && file.type.startsWith('video/')) {
        try {
          const dimensions = await getVideoDimensions(file);
          const minHeight = 720;
          if (dimensions && dimensions.height != null && dimensions.height < minHeight) {
            toast.error(
              `Видео слишком маленькое. Минимальная высота: ${minHeight} px, у загруженного: ${dimensions.height} px. Загрузите видео с большим разрешением.`
            );
            continue;
          }
          const duration = await getVideoDuration(file);
          if (duration && !isNaN(duration) && isFinite(duration)) {
            setVideoDuration(duration);
          }
        } catch (error) {
          console.error('Ошибка определения длительности/размера видео:', error);
        }
      }
      
      // Определяем длительность видео для моделей, которые используют видео
      if ((needsVideoDuration || isKlingMotionControl) && fieldName === 'video' && file.type.startsWith('video/')) {
        try {
          const duration = await getVideoDuration(file);
          if (duration && !isNaN(duration) && isFinite(duration)) {
            // Для kling motion-control (v2.6 / v3) валидируем длительность в зависимости от character_orientation
            if (isKlingMotionControl) {
              const characterOrientation = dynamicParams.character_orientation || model.default_parameters?.character_orientation || 'image';
              const maxDuration = characterOrientation === 'image' ? 10 : 30;
              
              if (duration > maxDuration) {
                toast.error(
                  `Длительность видео (${Math.round(duration)} сек) превышает максимальную для режима "${characterOrientation === 'image' ? 'image' : 'video'}" (${maxDuration} сек). ` +
                  `Измените режим "Ориентация персонажей" или выберите видео короче ${maxDuration} секунд.`
                );
                continue; // Пропускаем этот файл
              }
              
              // Устанавливаем длительность для расчета цены
              setVideoDuration(duration);
            }
            
            if (needsVideoDuration) {
              setVideoDuration(duration);
            }
          }
        } catch (error) {
          console.error('Ошибка определения длительности видео:', error);
        }
      }
      
      // Определяем длительность аудио для моделей, которые используют аудио (например, omni-human)
      if (needsAudioDuration && fieldName === 'audio' && file.type.startsWith('audio/')) {
        try {
          const duration = await getAudioDuration(file);
          if (duration && !isNaN(duration) && isFinite(duration)) {
            setVideoDuration(duration); // Используем то же состояние для длительности
          }
        } catch (error) {
          console.error('Ошибка определения длительности аудио:', error);
        }
      }

      if (isVideoField) {
        const preview = createPlayableVideoPreviewUrl(file);
        const fileData = {
          file,
          preview,
          url: preview,
          name: file.name,
          durationSeconds: fileDurationSeconds,
        };
        setFileFields(prev => {
          const current = prev[fieldName] || [];
          const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
          if (isMultiple) {
            return { ...prev, [fieldName]: [...currentArray, fileData] };
          }
          currentArray.forEach(revokeMediaPreview);
          return { ...prev, [fieldName]: fileData };
        });
        continue;
      }

      // Для Crystal Upscaler: вычисляем мегапиксели итогового изображения при загрузке
      const isCrystalUpscaler = model.slug === 'crystal-upscaler' || model.model_id?.toLowerCase().includes('crystal-upscaler');
      if (isCrystalUpscaler && file.type.startsWith('image/')) {
        try {
          const dimensions = await getImageDimensions(file);
          if (dimensions && dimensions.width && dimensions.height) {
            // Сохраняем размеры исходного изображения для пересчета при изменении scale
            setSourceImageDimensions(dimensions);
            
            // Получаем значение scale из dynamicParams или default_parameters
            const scale = dynamicParams.scale || dynamicParams.scale_factor || model.default_parameters?.scale || model.default_parameters?.scale_factor || 2;
            const scaleValue = parseFloat(scale) || 2;
            
            // Вычисляем размеры итогового изображения
            const finalWidth = dimensions.width * scaleValue;
            const finalHeight = dimensions.height * scaleValue;
            
            // Вычисляем мегапиксели: (width * height) / 1,000,000
            const megapixels = (finalWidth * finalHeight) / 1000000;
            
            // Устанавливаем мегапиксели в dynamicParams для предварительного расчета стоимости
            setDynamicParams(prev => ({
              ...prev,
              megapixels: megapixels
            }));
          }
        } catch (error) {
          console.error('Ошибка определения размеров изображения:', error);
        }
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const fileData = {
          file,
          preview: event.target.result,
          url: event.target.result,
          name: file.name,
          durationSeconds: fileDurationSeconds
        };
        
        setFileFields(prev => {
          const current = prev[fieldName] || [];
          const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
          
          if (isMultiple) {
            return { ...prev, [fieldName]: [...currentArray, fileData] };
          } else {
            return { ...prev, [fieldName]: fileData };
          }
        });
      };
      reader.readAsDataURL(file);
    }
  }, [fileFields, model.slug, model.model_id, model.default_parameters, dynamicParams, getVideoDuration, getAudioDuration, getFileFieldType, getImageDimensions, getVideoDimensions]);

  // Картинка из другой вкладки: у Яндекса ссылка без расширения, а text/plain часто
  // указывает на страницу поиска. Берём файл по байтам или src из <img> и скачиваем его.
  const handleUploadAreaDrop = useCallback(async (event, fieldName, fieldConfig, isMultiple, maxFiles) => {
    event.preventDefault();
    event.stopPropagation();

    const payload = captureDropPayload(event.dataTransfer);
    if (payload.reorder) return;

    const { isVideo, isAudio } = getFileFieldType(fieldName, fieldConfig);
    const kind = isVideo ? 'video' : isAudio ? 'audio' : 'image';

    let toastId = null;
    try {
      const localFiles = await localDroppedFiles(payload, kind);
      if (localFiles.length > 0) {
        handleFileSelect(fieldName, localFiles, isMultiple, maxFiles);
        return;
      }

      const remoteUrls = collectDropUrls(payload).filter((item) => !/^file:/i.test(item.url));
      toastId = remoteUrls.length > 0 ? toast.loading('Добавляем файл…') : null;
      const resolved = await resolveDroppedMedia({ ...payload, files: [], entries: [] }, kind);
      if (toastId) toast.dismiss(toastId);
      toastId = null;

      if (resolved.fileProtocol) {
        toast.error(
          'Нельзя перетаскивать локальные пути к файлам. ' +
          'Пожалуйста, выберите файл через диалог выбора файлов или перетащите сам файл из файлового менеджера.'
        );
        return;
      }

      if (resolved.files.length > 0) {
        handleFileSelect(fieldName, resolved.files, isMultiple, maxFiles);
        return;
      }

      if (resolved.fallbackUrl) {
        const current = fileFields[fieldName];
        const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
        if (currentArray.length >= maxFiles) {
          toast.error(`Максимум ${maxFiles} файл${maxFiles === 1 ? '' : 'ов'} для этого поля`);
          return;
        }
        const resultItem = {
          id: Date.now(),
          url: resolved.fallbackUrl,
          preview: resolved.fallbackUrl,
          file: null,
        };
        setFileFields((prev) => {
          const latest = prev[fieldName];
          const latestArray = Array.isArray(latest) ? latest : (latest ? [latest] : []);
          if (latestArray.length >= maxFiles) return prev;
          if (isMultiple) {
            return { ...prev, [fieldName]: [...latestArray, resultItem] };
          }
          return { ...prev, [fieldName]: resultItem };
        });
        const addedLabel = resolved.fallbackSource === 'json'
          ? 'Результат добавлен в поле'
          : (kind === 'video' ? 'Видео добавлено' : kind === 'audio' ? 'Аудио добавлено' : 'Изображение добавлено');
        toast.success(addedLabel);
        return;
      }

      if (payload.files.length || payload.html || payload.uriList || payload.plain || payload.json || payload.moz) {
        toast.error(
          kind === 'image'
            ? 'Не удалось добавить это изображение. Сохраните его и выберите файлом.'
            : 'Не удалось добавить этот файл.'
        );
      }
    } catch (err) {
      if (toastId) toast.dismiss(toastId);
      console.error('Ошибка обработки перетаскивания:', err);
      toast.error('Не удалось добавить файл');
    }
  }, [fileFields, handleFileSelect]);

  // Универсальный обработчик удаления файла
  const handleFileRemove = useCallback((fieldName, index = null) => {
    setFileFields(prev => {
      const current = prev[fieldName];
      if (!current) return prev;
      
      if (Array.isArray(current)) {
        if (index !== null) {
          revokeMediaPreview(current[index]);
          const newArray = current.filter((_, i) => i !== index);
          return newArray.length > 0 ? { ...prev, [fieldName]: newArray } : { ...prev, [fieldName]: undefined };
        } else {
          current.forEach(revokeMediaPreview);
          // Если удаляем все файлы из поля video или audio, сбрасываем длительность
          const needsMediaDuration = (model.slug === 'pixverse-lipsync' || 
                                       model.slug === 'runway-gen4-aleph' || 
                                       model.slug === 'omni-human' ||
                                       isKlingMotionControlModel(model) ||
                                       model.model_id?.toLowerCase().includes('pixverse/lipsync') ||
                                       model.model_id?.toLowerCase().includes('gen4-aleph') ||
                                       model.model_id?.toLowerCase().includes('omni-human'));
          if (needsMediaDuration && (fieldName === 'video' || fieldName === 'audio')) {
            setVideoDuration(null);
          }
          const isKlingO3V2vRemove = model.slug === 'kling-o3-v2v' || model.model_id?.toLowerCase().includes('video-to-video/edit');
          if (isKlingO3V2vRemove && fieldName === 'video_url') {
            setVideoDuration(null);
          }
          
          // Для Crystal Upscaler: если удаляем изображение, сбрасываем размеры
          const isCrystalUpscaler = model.slug === 'crystal-upscaler' || model.model_id?.toLowerCase().includes('crystal-upscaler');
          const imageFields = ['input_image', 'image_input', 'image'];
          if (isCrystalUpscaler && imageFields.includes(fieldName)) {
            setSourceImageDimensions(null);
          }
          
          return { ...prev, [fieldName]: undefined };
        }
      } else {
        revokeMediaPreview(current);
        // Если удаляем файл из поля video или audio, сбрасываем длительность
        const needsMediaDuration = (model.slug === 'pixverse-lipsync' || 
                                     model.slug === 'runway-gen4-aleph' || 
                                     model.slug === 'omni-human' ||
                                     isKlingMotionControlModel(model) ||
                                     model.model_id?.toLowerCase().includes('pixverse/lipsync') ||
                                     model.model_id?.toLowerCase().includes('gen4-aleph') ||
                                     model.model_id?.toLowerCase().includes('omni-human'));
        if (needsMediaDuration && (fieldName === 'video' || fieldName === 'audio')) {
          setVideoDuration(null);
        }
        const isKlingO3V2vRemove = model.slug === 'kling-o3-v2v' || model.model_id?.toLowerCase().includes('video-to-video/edit');
        if (isKlingO3V2vRemove && fieldName === 'video_url') {
          setVideoDuration(null);
        }
        
        // Для Crystal Upscaler: если удаляем изображение, сбрасываем размеры
        const isCrystalUpscaler = model.slug === 'crystal-upscaler' || model.model_id?.toLowerCase().includes('crystal-upscaler');
        const imageFields = ['input_image', 'image_input', 'image'];
        if (isCrystalUpscaler && imageFields.includes(fieldName)) {
          setSourceImageDimensions(null);
        }
        
        return { ...prev, [fieldName]: undefined };
      }
    });
  }, [model.slug, model.model_id]);

  // Обработчик изменения порядка файлов через drag & drop
  const handleFileReorder = useCallback((fieldName, dragIndex, dropIndex) => {
    setFileFields(prev => {
      const current = prev[fieldName];
      if (!current || !Array.isArray(current)) return prev;
      
      const newArray = [...current];
      const [draggedItem] = newArray.splice(dragIndex, 1);
      newArray.splice(dropIndex, 0, draggedItem);
      
      return { ...prev, [fieldName]: newArray };
    });
  }, []);

  // Валидация и установка видео для элемента Kling (макс. 200 МБ, 720–2160 px, 3–10 сек)
  const handleKlingVideoSelect = useCallback((file, onSuccess, onReject) => {
    const MAX_SIZE = 200 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error('Видео: макс. 200 МБ');
      onReject();
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const w = video.videoWidth;
      const h = video.videoHeight;
      const dur = video.duration;
      if (w < 720 || h < 720 || w > 2160 || h > 2160) {
        toast.error('Видео: разрешение 720–2160 px по каждой стороне');
        onReject();
        return;
      }
      if (dur < 3 || dur > 10.05) {
        toast.error('Видео: длительность 3–10 сек');
        onReject();
        return;
      }
      onSuccess({ id: Date.now(), file, preview: URL.createObjectURL(file) });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error('Не удалось загрузить видео');
      onReject();
    };
    video.src = url;
  }, []);

  // Вычисляем стоимость в зависимости от параметров модели
  const calculatedCost = React.useMemo(() => {
    // Если у пресета есть total_cost из API, используем его (уже с учетом параметров пресета и cost_multiplier)
    if (activePreset && activePreset.totalCost !== null && activePreset.totalCost !== undefined) {
      return activePreset.totalCost;
    }
    
    // Если есть активный пресет (не многошаговый), применяем коэффициент стоимости
    let presetCostMultiplier = 1.0;
    if (activePreset && activePreset.costMultiplier !== undefined && activePreset.costMultiplier !== null) {
      const parsed = parseFloat(activePreset.costMultiplier);
      presetCostMultiplier = isNaN(parsed) ? 1.0 : parsed;  // Сохраняем 0, если он равен 0
    }
    
    // Если коэффициент равен 0, стоимость бесплатна
    if (presetCostMultiplier === 0) {
      return 0;
    }

    // Suno v6 — фиксированная цена. ElevenLabs — 1 токен за 200 символов, минимум 1.
    if (model.slug === 'suno-v6' || model.model_id === 'ai-music-api/generate') {
      return Math.round(5 * presetCostMultiplier * 100) / 100;
    }
    if (
      model.category === 'speech_generation' ||
      model.slug === 'elevenlabs-tts-v2' ||
      model.slug === 'elevenlabs-dialogue-v3'
    ) {
      let chars = 0;
      if (isElevenLabsDialogueModel(model)) {
        const lines = dynamicParams.dialogue || model.default_parameters?.dialogue || [];
        if (Array.isArray(lines)) {
          chars = lines.reduce((sum, line) => sum + String(line?.text || '').length, 0);
        }
      } else {
        chars = (prompt || '').length;
      }
      const tokens = chars > 0 ? Math.ceil(chars / 200) : 1;
      return Math.round(tokens * presetCostMultiplier * 100) / 100;
    }
    
    // Nano Banana 2.1: 1K=3, 2K=4, 4K=6.
    if (isNanoBanana21Model(model)) {
      const resolution = dynamicParams.resolution || model.default_parameters?.resolution || '1K';
      const resolutionLower = String(resolution).toLowerCase();
      let baseCost = 3;
      if (resolutionLower.includes('4k')) baseCost = 6;
      else if (resolutionLower.includes('2k')) baseCost = 4;
      return Math.round(baseCost * presetCostMultiplier * 100) / 100;
    }

    // Nano Banana 2: 1K=2, 2K=3, 4K=4. 0.5K снят.
    if (isNanoBanana2Model(model)) {
      const resolution = dynamicParams.resolution || model.default_parameters?.resolution || '1K';
      const resolutionLower = String(resolution).toLowerCase();
      let baseCost = 2;
      if (resolutionLower.includes('4k')) baseCost = 4;
      else if (resolutionLower.includes('2k')) baseCost = 3;
      return Math.round(baseCost * presetCostMultiplier * 100) / 100;
    }

    // Nano Banana Pro: 1K=2, 2K=2, 4K=5
    if (model.slug === 'nano-banana-pro' || model.slug === 'nano-banana-pro-v2' || model.model_id?.includes('nano-banana-pro')) {
      const resolution = dynamicParams.resolution || model.default_parameters?.resolution || '1K';
      const resolutionLower = String(resolution).toLowerCase();
      const baseCost = resolutionLower.includes('4k') ? 5 : 2;
      return Math.round(baseCost * presetCostMultiplier * 100) / 100;
    }
    
    // GPT Image 2.5: quality × 1K/2K/4K. Flare/Sunburst на цену не влияет.
    if (isGptImage25Model(model)) {
      const qualityRaw = String(dynamicParams.quality || model.default_parameters?.quality || 'high').toLowerCase();
      const quality = ['low', 'medium', 'high', 'xhigh', 'max'].includes(qualityRaw) ? qualityRaw : 'high';
      const imageSize = dynamicParams.image_size ?? model.default_parameters?.image_size;
      let sizeTier = '1k';
      if (typeof imageSize === 'string') {
        const s = imageSize.trim().toLowerCase();
        if (s === '2k') sizeTier = '2k';
        else if (s === '4k') sizeTier = '4k';
      } else if (imageSize && typeof imageSize === 'object') {
        const w = parseInt(imageSize.width, 10);
        const h = parseInt(imageSize.height, 10);
        const maxSide = Math.max(w || 0, h || 0);
        if (maxSide >= 2800) sizeTier = '4k';
        else if (maxSide >= 1800) sizeTier = '2k';
      }
      const matrix = {
        '1k': { low: 1, medium: 2, high: 4, xhigh: 5, max: 9 },
        '2k': { low: 1.5, medium: 3, high: 5, xhigh: 7, max: 10 },
        '4k': { low: 2, medium: 4, high: 7, xhigh: 9, max: 12 },
      };
      const sizeCosts = matrix[sizeTier] || matrix['1k'];
      const baseCost = sizeCosts[quality] ?? sizeCosts.high;
      return Math.round(baseCost * presetCostMultiplier * 100) / 100;
    }

    // GPT Image 2: стоимость зависит только от quality и image_size (1K/2K/4K).
    if (isGptImage2Model(model)) {
      const quality = String(dynamicParams.quality || model.default_parameters?.quality || 'medium').toLowerCase();
      const imageSize = dynamicParams.image_size ?? model.default_parameters?.image_size;
      let sizeTier = 'default';
      if (typeof imageSize === 'string') {
        const s = imageSize.trim().toLowerCase();
        if (s === '1k' || s === 'default') sizeTier = 'default';
        else if (s === '2k') sizeTier = '2k';
        else if (s === '4k') sizeTier = '4k';
      } else if (imageSize && typeof imageSize === 'object') {
        const w = parseInt(imageSize.width, 10);
        const h = parseInt(imageSize.height, 10);
        const maxSide = Math.max(w || 0, h || 0);
        if (maxSide >= 4096) sizeTier = '4k';
        else if (maxSide >= 2048) sizeTier = '2k';
      }
      const matrix = {
        default: { low: 1, medium: 2, high: 8 },
        '2k': { low: 1.5, medium: 6, high: 14 },
        '4k': { low: 2, medium: 9, high: 18 },
      };
      const sizeCosts = matrix[sizeTier] || matrix.default;
      const baseCost = sizeCosts[quality] ?? sizeCosts.medium;
      return Math.round(baseCost * presetCostMultiplier * 100) / 100;
    }

    // Seedream 5 Pro: t2i 1K=3, 2K=6; Edit всегда 7 (в новой таблице нет)
    if (
      model.slug === 'seedream-5-pro'
      || model.model_id?.includes('seedream/v5/pro')
      || model.model_id?.includes('seedream-v5.0-pro')
    ) {
      const imageSize = dynamicParams.image_size ?? model.default_parameters?.image_size ?? 'auto_2K';
      let sizeTier = '2k';
      if (imageSize && typeof imageSize === 'object') {
        const w = parseInt(imageSize.width, 10) || 0;
        const h = parseInt(imageSize.height, 10) || 0;
        const mp = w > 0 && h > 0 ? (w * h) / 1_000_000 : 0;
        sizeTier = mp > 0 && mp <= 2.36 ? '1k' : '2k';
      } else {
        const s = String(imageSize).trim().toLowerCase();
        if (s === 'auto_1k' || s === '1k' || s === 'square') sizeTier = '1k';
      }
      const imageField = fileFields?.image_urls;
      const hasImages = Array.isArray(imageField)
        ? imageField.length > 0
        : Boolean(imageField);
      let baseCost = 7;
      if (!hasImages) baseCost = sizeTier === '1k' ? 3 : 6;
      return Math.round(baseCost * presetCostMultiplier * 100) / 100;
    }
    
    // Kling Motion Control (slug kling-v3-motion-control): Standard = cost_in_bonuses/сек, Pro ×5/3
    if (isKlingMotionControlModel(model)) {
      if (!videoDuration || isNaN(videoDuration) || !isFinite(videoDuration) || videoDuration <= 0) {
        return 0;
      }
      const mode = dynamicParams.mode || model.default_parameters?.mode || 'std';
      const modeMultiplier = mode === 'pro' ? 5 / 3 : 1;
      const totalCost = model.cost_in_bonuses * modeMultiplier * videoDuration;
      return Math.round(totalCost * presetCostMultiplier * 100) / 100;
    }
    
    // Для Crystal Upscaler стоимость зависит от мегапикселей
    if (model.slug === 'crystal-upscaler' || model.model_id?.toLowerCase().includes('crystal-upscaler')) {
      const megapixels = dynamicParams.megapixels || model.default_parameters?.megapixels;
      
      let baseCost;
      // Если мегапиксели не указаны, используем базовую стоимость
      if (!megapixels) {
        baseCost = model.cost_in_bonuses;
      } else {
        // Расчет стоимости в зависимости от мегапикселей (в токенах)
        const mp = parseFloat(megapixels);
        if (mp <= 4) {
          baseCost = 5.3;
        } else if (mp <= 8) {
          baseCost = 10.6;
        } else if (mp <= 16) {
          baseCost = 21.2;
        } else if (mp <= 25) {
          baseCost = 42.3;
        } else if (mp <= 50) {
          baseCost = 84.6;
        } else if (mp <= 100) {
          baseCost = 169.3;
        } else { // 100-200 MP
          baseCost = 338.5;
        }
      }
      
      // Применяем коэффициент стоимости пресета
      return Math.round(baseCost * presetCostMultiplier * 100) / 100;
    }
    
    // Kling O3 video-to-video edit: стоимость = длительность × (cost_in_bonuses для Standard, × 11.1/8.3 для Pro)
    const isKlingO3V2v = model.slug === 'kling-o3-v2v' || model.model_id?.toLowerCase().includes('video-to-video/edit');
    if (isKlingO3V2v) {
      const baseCost = model.cost_in_bonuses;
      const tier = (dynamicParams.tier || model.default_parameters?.tier || 'standard').toLowerCase();
      const rate = tier === 'pro' ? baseCost * (11.1 / 8.3) : baseCost;
      const rawSec = (videoDuration && !isNaN(videoDuration) && isFinite(videoDuration) && videoDuration > 0) ? videoDuration : 1;
      const durationSec = Math.floor(rawSec); // тарификация по целым секундам (8.9 сек → 8 сек)
      const totalCost = durationSec * rate * presetCostMultiplier;
      return Math.round(totalCost * 100) / 100;
    }
    
    // Для моделей с посекундной тарификацией медиа (видео или аудио): стоимость = длительность × cost_in_bonuses
    const perSecondMediaModels = [
      'pixverse-lipsync',
      'runway-gen4-aleph',
      'omni-human',
      'pixverse/lipsync',
      'runwayml/gen4-aleph',
      'gen4-aleph',
      'bytedance/omni-human'
    ];
    
    const isPerSecondMediaModel = perSecondMediaModels.some(modelId => 
      model.slug === modelId || model.model_id?.toLowerCase().includes(modelId.toLowerCase())
    );
    
    if (isPerSecondMediaModel) {
      const costPerSecond = model.cost_in_bonuses;
      
      // Если длительность медиа (видео или аудио) определена, используем её
      if (videoDuration && !isNaN(videoDuration) && isFinite(videoDuration) && videoDuration > 0) {
        const totalCost = videoDuration * costPerSecond * presetCostMultiplier;
        return Math.round(totalCost * 100) / 100; // Округляем до 2 знаков
      }
      
      // Если длительность не определена, используем базовую стоимость (за 1 секунду)
      return Math.round(costPerSecond * presetCostMultiplier * 100) / 100;
    }
    
    // Для видео моделей стоимость зависит от длительности и наличия аудио
    if (model.category === 'video_generation') {
      if (
        model.slug === 'kling-v3-i2v' ||
        model.slug === 'kling-v3-t2v' ||
        (model.model_id || '').includes('kling-v3.0')
      ) {
        let duration = dynamicParams.duration ?? model.default_parameters?.duration ?? 5;
        duration = typeof duration === 'string' ? parseInt(duration, 10) : duration;
        if (!Number.isFinite(duration) || duration < 1) duration = 5;
        let resolution = dynamicParams.resolution ?? model.default_parameters?.resolution;
        if (!resolution) {
          const tier = (dynamicParams.tier || model.default_parameters?.tier || 'standard').toLowerCase();
          resolution = tier === 'pro' ? '1080p' : '720p';
        }
        resolution = String(resolution).toLowerCase();
        const generateAudio = dynamicParams.generate_audio !== undefined
          ? dynamicParams.generate_audio
          : (model.default_parameters?.generate_audio ?? false);
        const base = model.cost_in_bonuses;
        let ratePerSec = base;
        if (generateAudio) {
          ratePerSec = base * (resolution === '1080p' ? 7 / 3 : 5 / 3);
        } else if (resolution === '1080p') {
          ratePerSec = base * (4 / 3);
        }
        return Math.round(ratePerSec * duration * presetCostMultiplier * 100) / 100;
      }

      if (
        model.slug === 'kling-2-6-pro-i2v'
        || model.model_id === 'fal-ai/kling-video/v2.6/pro/image-to-video'
        || model.model_id === 'kwaivgi/kling-v2.6-pro/image-to-video'
      ) {
        let duration = dynamicParams.duration ?? model.default_parameters?.duration ?? 5;
        duration = typeof duration === 'string' ? parseInt(duration, 10) : duration;
        if (!Number.isFinite(duration) || duration < 1) duration = 5;
        const generateAudio = dynamicParams.generate_audio !== undefined
          ? dynamicParams.generate_audio
          : (model.default_parameters?.generate_audio ?? false);
        const ratePerSec = model.cost_in_bonuses * (generateAudio ? (25 / 12) : 1);
        const baseCost = ratePerSec * duration;
        return Math.round(baseCost * presetCostMultiplier * 100) / 100;
      }

      if (model.slug === 'veo-3-1') {
        // Atlas Veo 3.1 Lite: cost_in_bonuses = тариф за 8 сек (720p/1080p одинаково).
        let duration = dynamicParams.duration ?? model.default_parameters?.duration ?? 8;
        if (typeof duration === 'string' && duration.trim().toLowerCase() === 'auto') {
          duration = 8;
        } else {
          duration = typeof duration === 'string' ? parseInt(duration, 10) : duration;
        }
        if (!Number.isFinite(duration) || duration < 1) duration = 8;
        const baseCost = model.cost_in_bonuses * (duration / 8);
        return Math.round(baseCost * presetCostMultiplier * 100) / 100;
      }

      // Определяем тип модели (один раз)
      const isKlingO3 = model.slug === 'kling-o3-i2v' || model.model_id === 'fal-ai/kling-video/o3/image-to-video';
      const isKling = model.slug?.toLowerCase().includes('kling') || model.model_id?.toLowerCase().includes('kling');
      const isSeedance = model.slug?.toLowerCase().includes('seedance') || model.model_id?.toLowerCase().includes('seedance');
      const isWan = model.slug?.toLowerCase().includes('wan') || model.model_id?.toLowerCase().includes('wan');
      
      // Для Seedance, Kling, WAN базовая длительность 5 сек, для остальных 4 сек
      const defaultDuration = (isSeedance || isKling || isWan) ? 5 : 4;
      let duration = dynamicParams.duration || model.default_parameters?.duration || defaultDuration;
      // Преобразуем duration в число, если это строка (в т.ч. "auto" → оценка по умолчанию)
      if (typeof duration === 'string' && duration.trim().toLowerCase() === 'auto') {
        duration = defaultDuration;
      } else {
        duration = typeof duration === 'string' ? parseInt(duration, 10) : duration;
      }
      if (!Number.isFinite(duration) || duration < 1) {
        duration = defaultDuration;
      }
      
      // Проверяем наличие аудио (generate_audio)
      const generateAudio = dynamicParams.generate_audio !== undefined 
        ? dynamicParams.generate_audio 
        : (model.default_parameters?.generate_audio !== undefined 
            ? model.default_parameters.generate_audio 
            : false);
      
      // Базовая стоимость (для Kling O3 и v3 зависит от tier: Pro дороже)
      let baseCost = model.cost_in_bonuses;
      const isSeedance2 = isSeedance2Model(model);
      const isSeedance25 = isSeedance25Model(model);
      if (isSeedance2 || isSeedance25) {
        const mode = getSeedance2GenerationMode(model, dynamicParams);
        const seedanceResolution = String(
          dynamicParams.resolution ?? model.default_parameters?.resolution ?? '720p'
        ).toLowerCase();
        baseCost = isSeedance25
          ? getSeedance25TokensPerSecond(mode, seedanceResolution)
          : getSeedance2TokensPerSecond(mode, seedanceResolution);
        if (isSeedance25 && seedanceDraft) {
          baseCost = getSeedance25TokensPerSecond(mode, '480p');
        }
        if (isSeedance25VideoEditMode(model, mode)) {
          const source = promptMediaFileItems(fileFields.video_urls)[0];
          const sourceSeconds = Number(source?.durationSeconds);
          const savedSeconds = Number(dynamicParams.video_duration);
          if (Number.isFinite(sourceSeconds) && sourceSeconds > 0) {
            duration = Math.max(1, Math.ceil(sourceSeconds));
          } else if (Number.isFinite(savedSeconds) && savedSeconds > 0) {
            duration = Math.max(1, Math.ceil(savedSeconds));
          } else {
            duration = 0;
          }
        }
      }
      if (isKlingO3) {
        const tier = (dynamicParams.tier || model.default_parameters?.tier || 'standard').toLowerCase();
        if (tier === 'pro') {
          baseCost = baseCost * 1.333; // $0.224 vs $0.168 per sec
        }
      }
      
      // Коэффициент в зависимости от длительности
      let multiplier = 1;
      if (isKlingO3) {
        // Kling O3 / Kling v3: cost_in_bonuses за 1 сек — умножаем на выбранную длительность (в ценнике сразу сумма)
        multiplier = duration;
      } else if (isKling || isSeedance || isWan) {
        // Kling (не O3), Seedance, WAN
        if (isSeedance) {
          multiplier = duration;
        } else {
          if (duration === 10) {
            multiplier = 2;
          } else if (duration === 5) {
            multiplier = 1;
          } else {
            multiplier = duration / 5;
          }
        }
      } else {
        if (duration === 6) {
          multiplier = 1.5;
        } else if (duration === 8) {
          multiplier = 2;
        } else if (duration === 4) {
          multiplier = 1;
        } else {
          multiplier = duration / 4;
        }
      }
      
      const isSeedance2Pricing = isSeedance2 || isSeedance25;
      if (isSeedance && generateAudio && !isSeedance2Pricing) {
        multiplier = multiplier * 2.0;
      }
      // Kling O3: множитель за аудио (Standard 0.224/0.168 ≈ 1.333, Pro 0.28/0.224 = 1.25)
      if (isKlingO3 && generateAudio) {
        const tier = (dynamicParams.tier || model.default_parameters?.tier || 'standard').toLowerCase();
        multiplier = multiplier * (tier === 'pro' ? 1.25 : 1.333);
      } else if (isKling && generateAudio) {
        multiplier = multiplier * 2.0;
      }
      
      return Math.round(baseCost * multiplier * presetCostMultiplier * 100) / 100;
    }
    
    
    // Для остальных моделей используем базовую стоимость
    return model.cost_in_bonuses;
  }, [
    model.cost_in_bonuses, 
    model.category, 
    model.slug, 
    model.model_id,
    prompt,
    dynamicParams.dialogue, 
    dynamicParams.resolution, 
    dynamicParams.duration, 
    dynamicParams.generate_audio,
    dynamicParams.tier,
    dynamicParams.generation_mode,
    dynamicParams.video_duration,
    seedanceDraft,
    fileFields.video_urls,
    dynamicParams.megapixels,
    dynamicParams.mode,
    dynamicParams.character_orientation,
    dynamicParams.scale,
    dynamicParams.scale_factor,
    dynamicParams.quality,
    dynamicParams.image_size,
    dynamicParams.aspect_ratio,
    model.default_parameters?.resolution, 
    model.default_parameters?.duration,
    model.default_parameters?.generate_audio,
    model.default_parameters?.tier,
    model.default_parameters?.megapixels,
    model.default_parameters?.mode,
    model.default_parameters?.character_orientation,
    model.default_parameters?.scale,
    model.default_parameters?.scale_factor,
    model.default_parameters?.quality,
    model.default_parameters?.image_size,
    model.default_parameters?.aspect_ratio,
    fileFields?.image_urls,
    videoDuration,
    activePreset,
    activePreset?.costMultiplier,
    activePreset?.totalCost,
    presetModels
  ]);

  // Завершённая джоба из истории: в шапке показываем факт списания; иначе — оценочную стоимость следующего запуска
  const headerCostDisplay = React.useMemo(() => {
    if (
      result &&
      result.status === 'completed' &&
      result.bonuses_spent != null &&
      result.bonuses_spent !== ''
    ) {
      const raw = result.bonuses_spent;
      const num = typeof raw === 'number' ? raw : parseFloat(String(raw), 10);
      if (Number.isFinite(num)) {
        const amount = Math.round(num * 100) / 100;
        return { mode: 'spent', amount };
      }
    }
    return { mode: 'estimate', amount: calculatedCost };
  }, [result, calculatedCost]);

  // Вычисляем суммарное время генерации для многошаговых пресетов
  const estimatedTime = React.useMemo(() => {
    // Если это многошаговый пресет, суммируем время всех моделей
    if (activePreset && activePreset.isMultiStep && activePreset.stepsConfig && activePreset.stepsConfig.length > 0) {
      let totalTime = 0;
      for (const stepConfig of activePreset.stepsConfig) {
        const modelSlug = stepConfig.model_slug;
        if (modelSlug && presetModels[modelSlug] && presetModels[modelSlug].estimated_time_seconds) {
          totalTime += presetModels[modelSlug].estimated_time_seconds;
        }
      }
      return totalTime > 0 ? totalTime : model.estimated_time_seconds;
    }
    
    // Для обычных моделей используем время модели
    return model.estimated_time_seconds || 0;
  }, [
    model.estimated_time_seconds,
    activePreset,
    presetModels
  ]);

  // Скролл в топ при открытии страницы модели
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [model.id]);

  // Автоматическое изменение высоты textarea для промпта
  useEffect(() => {
    adjustPromptTextareaHeight();
  }, [prompt, adjustPromptTextareaHeight, seedance2PromptRefs, model?.id]);

  // Устанавливаем nsfw=true для не-Pro моделей при монтировании
  useEffect(() => {
    if (model.input_parameters) {
      Object.entries(model.input_parameters).forEach(([paramName, paramConfig]) => {
        // Для nsfw: показываем чекбокс только для Pro моделей, для остальных всегда true (активная цензура)
        if ((paramName === 'nsfw' || paramName.toLowerCase().includes('nsfw')) && 
            !model.slug?.includes('pro') && 
            !model.model_id?.includes('pro')) {
          // Для не-Pro моделей: всегда true (активная цензура)
          if (dynamicParams[paramName] !== true) {
            setDynamicParams(prev => ({
              ...prev,
              [paramName]: true
            }));
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.id, model.slug, model.model_id]);

  // Для Crystal Upscaler: пересчитываем мегапиксели при изменении scale или при удалении изображения
  useEffect(() => {
    const isCrystalUpscaler = model.slug === 'crystal-upscaler' || model.model_id?.toLowerCase().includes('crystal-upscaler');
    if (!isCrystalUpscaler) {
      return;
    }

    // Находим загруженное изображение (может быть в разных полях: input_image, image_input, image)
    const imageFields = ['input_image', 'image_input', 'image'];
    let imageUrl = null;
    let imageFile = null;
    
    for (const fieldName of imageFields) {
      const fieldValue = fileFields[fieldName];
      if (fieldValue) {
        // Если это массив, берем первый элемент
        const imageData = Array.isArray(fieldValue) ? fieldValue[0] : fieldValue;
        if (imageData) {
          imageUrl = imageData.url || imageData.preview;
          imageFile = imageData.file;
          break;
        }
      }
    }

    // Если изображение удалено, сбрасываем размеры и мегапиксели
    if (!imageUrl && !imageFile) {
      setSourceImageDimensions(null);
      setDynamicParams(prev => {
        const newParams = { ...prev };
        delete newParams.megapixels;
        return newParams;
      });
      return;
    }

    // Если размеры еще не вычислены, пытаемся их получить
    if (!sourceImageDimensions && (imageFile || imageUrl)) {
      const source = imageFile || imageUrl;
      getImageDimensions(source)
        .then(dimensions => {
          if (dimensions && dimensions.width && dimensions.height) {
            setSourceImageDimensions(dimensions);
            
            // Получаем значение scale из dynamicParams или default_parameters
            const scale = dynamicParams.scale || dynamicParams.scale_factor || model.default_parameters?.scale || model.default_parameters?.scale_factor || 2;
            const scaleValue = parseFloat(scale) || 2;
            
            // Вычисляем размеры итогового изображения
            const finalWidth = dimensions.width * scaleValue;
            const finalHeight = dimensions.height * scaleValue;
            
            // Вычисляем мегапиксели: (width * height) / 1,000,000
            const megapixels = (finalWidth * finalHeight) / 1000000;
            
            // Обновляем мегапиксели в dynamicParams
            setDynamicParams(prev => ({
              ...prev,
              megapixels: megapixels
            }));
          }
        })
        .catch(error => {
          console.error('Ошибка определения размеров изображения:', error);
        });
    }

    // Если есть размеры исходного изображения и изменился scale, пересчитываем мегапиксели
    if (sourceImageDimensions && sourceImageDimensions.width && sourceImageDimensions.height) {
      const scale = dynamicParams.scale || dynamicParams.scale_factor || model.default_parameters?.scale || model.default_parameters?.scale_factor || 2;
      const scaleValue = parseFloat(scale) || 2;
      
      // Вычисляем размеры итогового изображения
      const finalWidth = sourceImageDimensions.width * scaleValue;
      const finalHeight = sourceImageDimensions.height * scaleValue;
      
      // Вычисляем мегапиксели: (width * height) / 1,000,000
      const megapixels = (finalWidth * finalHeight) / 1000000;
      
      // Обновляем мегапиксели в dynamicParams
      setDynamicParams(prev => ({
        ...prev,
        megapixels: megapixels
      }));
    }
  }, [dynamicParams.scale, dynamicParams.scale_factor, sourceImageDimensions, fileFields, model.slug, model.model_id, model.default_parameters, getImageDimensions]);

  // Загружаем job или пресет из URL или prop при открытии страницы
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jobPublicId = params.get('id');
    // initialPresetId (из Dashboard) имеет приоритет над ?preset= и над /presets/:id
    const pathPresetMatch = location.pathname.match(/^\/presets\/([^/]+)/);
    const presetId =
      initialPresetId ||
      params.get('preset') ||
      (pathPresetMatch ? pathPresetMatch[1] : null);

    // Загружаем существующую генерацию только один раз при монтировании, если нет активной генерации
    if (jobPublicId && !isProcessing && !result && !activeJobId) {
      // Загружаем джоб только если явно указан id в URL (например, при переходе из истории)
      loadJobFromUrl(jobPublicId);
    } else if (presetId && !isProcessing && !result && !activeJobId) {
      // Загружаем пресет
      if (presetLoadInFlightRef.current === presetId) {
        return;
      }
      presetLoadInFlightRef.current = presetId;
      loadPreset(presetId).catch(err => {
        console.error('Error loading preset:', err);
      }).finally(() => {
        presetLoadInFlightRef.current = null;
      });
    }
    // initialPresetId / путь: при открытии /presets/:id без ?preset= подтягиваем пресет после появления model
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPresetId, location.pathname, model?.id]);

  const loadJobFromUrl = async (publicId) => {
    try {
      setIsProcessing(true);
      // Сбрасываем все file-поля перед загрузкой новой задачи
      // Это важно, чтобы перетащенные результаты не остались в состоянии
      setFileFields({});
      // НЕ включаем режим просмотра - форма должна быть редактируемой
      // setIsViewingMode(true);
      const response = await axios.get(`/api/services/jobs/by-id/${publicId}`);
      const job = response.data;
      
      console.log('Загружена задача из API:', {
        public_id: job.public_id,
        input_image_urls: job.input_image_urls,
        parameters: job.parameters,
        preset_id: job.preset_id
      });
      
      // Если задача была создана через пресет, загружаем пресет ПЕРЕД загрузкой параметров
      // Это нужно, чтобы скрытые поля не показывались
      let loadedPreset = null;
      if (job.preset_id) {
        try {
          const presetResponse = await axios.get(`/api/presets/${job.preset_id}`);
          const presetData = presetResponse.data;
          loadedPreset = {
            id: presetData.preset_id,
            title: presetData.title,
            userInputs: presetData.user_inputs || [],
            totalCost: presetData.total_cost !== undefined && presetData.total_cost !== null ? presetData.total_cost : null
          };
          // Устанавливаем активный пресет
          setActivePreset(loadedPreset);
        } catch (err) {
          console.error('Error loading preset from job:', err);
          // Продолжаем загрузку задачи даже если пресет не найден
        }
      }

      // Загружаем параметры из задачи (только если нет активного пресета или они нужны)
      // Если есть активный пресет, промпт уже установлен из пресета
      if (job.prompt && !loadedPreset) {
        setPrompt(job.prompt);
      }
      
      // Применяем параметры пресета (resolution, aspectRatio и т.д. применяются на бэкенде)
      if (loadedPreset) {
        const presetParams = applyPresetToModel(loadedPreset, model);
        setDynamicParams(prev => ({
          ...prev,
          ...presetParams
        }));
        
        // Инициализируем presetInputs пустыми значениями
        const initialInputs = {};
        loadedPreset.userInputs.forEach(input => {
          if (input.type === 'text') {
            initialInputs[input.name] = '';
          }
        });
        setPresetInputs(initialInputs);
        
        setPresetFixedInputs({});
      }
      
      // Загружаем значения из задачи в presetInputs (для текстовых полей пресета)
      if (loadedPreset && job.parameters) {
        const presetInputValues = {};
        loadedPreset.userInputs.forEach(input => {
          if (input.type === 'text') {
            const inputName = input.name;
            let value = null;
            
            // Сначала пробуем загрузить из _preset_values (новый способ сохранения)
            if (job.parameters._preset_values && job.parameters._preset_values[inputName] !== undefined) {
              value = job.parameters._preset_values[inputName];
            } else {
              // Для обратной совместимости пробуем загрузить из прямого параметра
              // Сначала пробуем fieldName (это имя поля модели), затем name (это имя поля пресета)
              const fieldName = input.fieldName || input.name;
              
              // Пробуем найти значение по fieldName (как оно сохранено в parameters)
              value = job.parameters[fieldName];
              
              // Если не нашли, пробуем по name
              if (!value) {
                value = job.parameters[inputName];
              }
            }
            
            // Сохраняем значение (даже если пустая строка)
            if (value !== null && value !== undefined) {
              presetInputValues[inputName] = value;
            }
          }
        });
        if (Object.keys(presetInputValues).length > 0) {
          setPresetInputs(prev => ({ ...prev, ...presetInputValues }));
        }
      }
      
      // Загружаем file-поля из job
      const loadedFileFields = {};
      
      // Сначала загружаем file-поля из parameters (они имеют приоритет, так как более специфичны)
      // Но если есть активный пресет, загружаем только те параметры, которые не скрыты пресетом
      if (job.parameters && model.input_parameters) {
        let paramsToLoad = { ...(job.parameters || {}) };
        delete paramsToLoad.draft;
        delete paramsToLoad.seedance_draft;
        delete paramsToLoad.evolink_task_id;
        delete paramsToLoad.draft_expires_at;
        delete paramsToLoad.source_task_id;
        delete paramsToLoad.fal_queue_status;
        delete paramsToLoad.fal_queue_position;
        if (loadedPreset) {
          const presetUserInputFieldNames = (loadedPreset.userInputs || []).map(input => input.fieldName || input.name);
          const allModelFieldNames = Object.keys(model.input_parameters || {});
          const hiddenFieldNames = allModelFieldNames.filter(fieldName => !presetUserInputFieldNames.includes(fieldName));
          hiddenFieldNames.forEach(fieldName => {
            delete paramsToLoad[fieldName];
          });
        }
        Object.keys(model.input_parameters).forEach((key) => {
          if (isHiddenParam(model.input_parameters[key])) {
            delete paramsToLoad[key];
          }
        });
        setDynamicParams(prev => ({ ...prev, ...paramsToLoad }));
        
        // Ищем file-поля в parameters, используя model.input_parameters напрямую
        // Но если есть пресет, загружаем только поля из userInputs пресета
        // ИСКЛЮЧАЕМ fixedInputs из пресета, чтобы они не отображались пользователю
        for (const [fieldName, fieldConfig] of Object.entries(model.input_parameters)) {
          // Если есть пресет, пропускаем поля, которые не в userInputs пресета
          if (loadedPreset) {
            const presetUserInputFieldNames = (loadedPreset.userInputs || []).map(input => input.fieldName || input.name);
            if (!presetUserInputFieldNames.includes(fieldName)) {
              continue; // Пропускаем скрытые поля
            }
            
            // Также проверяем, не является ли это поле одним из полей, где хранятся fixedInputs
            // fixedInputs могут храниться в полях с именами из fixedInputs объекта
            const fixedInputsMap = loadedPreset.fixedInputs || {};
            const fileFieldsList = getFileFields();
            
            // Проверяем, используется ли это поле для fixedInputs
            // Нужно проверить маппинг fixedInputs на поля модели
            let isFixedInputField = false;
            if (Object.keys(fixedInputsMap).length > 0) {
              // Проверяем прямое совпадение
              if (fixedInputsMap[fieldName]) {
                isFixedInputField = true;
              } else {
                // Проверяем маппинг через fileFieldsList (как в loadPreset)
                Object.entries(fixedInputsMap).forEach(([presetFieldName, url]) => {
                  let targetFieldName = presetFieldName;
                  const fieldExists = fileFieldsList.find(f => f.name === presetFieldName);
                  if (!fieldExists && fileFieldsList.length > 1) {
                    targetFieldName = fileFieldsList[1].name;
                  } else if (!fieldExists && fileFieldsList.length === 1) {
                    targetFieldName = fileFieldsList[0].name;
                  }
                  if (targetFieldName === fieldName) {
                    isFixedInputField = true;
                  }
                });
              }
            }
            
            if (isFixedInputField) {
              continue; // Пропускаем поле, если в нем хранится fixedInput
            }
          }
          
          // Проверяем, является ли поле file-полем и не deprecated
          if (fieldConfig && isFileParam(fieldConfig, fieldName) && !isFieldDeprecated(fieldConfig)) {
            // Загружаем из job.parameters (не из paramsToLoad, так как file-поля могут быть в оригинальных параметрах)
            if (job.parameters[fieldName]) {
              let paramValue = job.parameters[fieldName];
              
              // ВАЖНО: Проверяем на file:// URL (локальные пути) - они недопустимы
              const validateParamUrl = (url) => {
                if (typeof url === 'string' && url.toLowerCase().startsWith('file://')) {
                  console.error(`Обнаружен недопустимый file:// URL в параметре ${fieldName}: ${url}`);
                  toast.error(
                    `Обнаружен локальный путь к файлу в параметре ${fieldName}. ` +
                    'Пожалуйста, выберите файл заново через диалог выбора файлов.'
                  );
                  return false;
                }
                return true;
              };
              
              // Проверяем, если это массив URL
              if (Array.isArray(paramValue)) {
                paramValue = paramValue.filter(url => validateParamUrl(url));
                if (paramValue.length === 0) {
                  continue; // Пропускаем поле, если все URL невалидны
                }
              } else if (typeof paramValue === 'string') {
                if (!validateParamUrl(paramValue)) {
                  continue; // Пропускаем поле, если URL невалиден
                }
              }
              
              // Дополнительно фильтруем fixedInputs по URL (если они все еще присутствуют)
              if (loadedPreset && loadedPreset.fixedInputs && Object.keys(loadedPreset.fixedInputs).length > 0) {
                const fixedUrls = Object.values(loadedPreset.fixedInputs);
                
                // Нормализуем URLs для сравнения (убираем протокол, хост, query параметры)
                const normalizeUrl = (url) => {
                  if (!url) return '';
                  return url.replace(/^https?:\/\/[^\/]+/, '').split('?')[0].replace(/^\//, '');
                };
                
                if (Array.isArray(paramValue)) {
                  // Фильтруем fixedInputs из массива
                  paramValue = paramValue.filter(url => {
                    const normalizedUrl = normalizeUrl(url);
                    return !fixedUrls.some(fixedUrl => {
                      const normalizedFixed = normalizeUrl(fixedUrl);
                      return normalizedUrl === normalizedFixed || url === fixedUrl;
                    });
                  });
                  // Если после фильтрации ничего не осталось, пропускаем поле
                  if (paramValue.length === 0) {
                    continue;
                  }
                } else if (typeof paramValue === 'string') {
                  // Если это строка и она совпадает с fixedInput, пропускаем
                  const normalizedUrl = normalizeUrl(paramValue);
                  const isFixed = fixedUrls.some(fixedUrl => {
                    const normalizedFixed = normalizeUrl(fixedUrl);
                    return normalizedUrl === normalizedFixed || paramValue === fixedUrl;
                  });
                  if (isFixed) {
                    continue; // Пропускаем поле, если это fixedInput
                  }
                }
              }
              
              if (Array.isArray(paramValue)) {
                loadedFileFields[fieldName] = paramValue.map((url, idx) => ({
                  id: idx,
                  url: url,
                  preview: url,
                  file: null
                }));
              } else if (typeof paramValue === 'string' && paramValue.trim() !== '') {
                // Обрабатываем как обычный URL или base64 data URL
                loadedFileFields[fieldName] = {
                  id: 0,
                  url: paramValue,
                  preview: paramValue, // base64 data URL можно использовать напрямую в img src
                  file: null
                };
              }
            }
          }
        }
      }
      
      // Затем загружаем из input_image_urls (для обратной совместимости)
      // Поддерживаем новый формат: [{ field: "start_image", url: "..." }] или старый формат: ["url1", "url2"]
      if (job.input_image_urls && job.input_image_urls.length > 0) {
        console.log('Загрузка input_image_urls:', job.input_image_urls);
        
        // Проверяем, является ли это новый формат (массив объектов с полями field и url)
        const isNewFormat = job.input_image_urls[0] && typeof job.input_image_urls[0] === 'object' && 
                           'field' in job.input_image_urls[0] && 'url' in job.input_image_urls[0];
        
        console.log('Определен формат:', isNewFormat ? 'новый' : 'старый');
        
        if (isNewFormat) {
          // Новый формат: группируем изображения по полям
          const imagesByField = {};
          const fileFieldsList = getFileFields();
          const availableFieldNames = fileFieldsList.map(f => f.name);
          
          console.log('Доступные поля в модели:', availableFieldNames);
          
          for (const item of job.input_image_urls) {
            if (item && typeof item === 'object' && item.field && item.url) {
              let fieldName = item.field;
              const url = item.url;
              
              // ВАЖНО: Проверяем на file:// URL (локальные пути) - они недопустимы
              if (typeof url === 'string' && url.toLowerCase().startsWith('file://')) {
                console.error(`Обнаружен недопустимый file:// URL в сохраненной задаче: ${url}`);
                toast.error(
                  'Обнаружен локальный путь к файлу в сохраненной задаче. ' +
                  'Пожалуйста, выберите файл заново через диалог выбора файлов.'
                );
                // Пропускаем этот файл
                continue;
              }
              
              // Проверяем, существует ли поле в модели (используем только данные из model.input_parameters)
              if (!availableFieldNames.includes(fieldName)) {
                console.warn(`Поле ${fieldName} не найдено в модели. Доступные поля:`, availableFieldNames);
                // Пробуем найти похожее поле по названию (без хардкода)
                const similarField = availableFieldNames.find(f => 
                  f.includes(fieldName.toLowerCase()) || fieldName.toLowerCase().includes(f)
                );
                if (similarField) {
                  console.log(`Используем похожее поле: ${fieldName} -> ${similarField}`);
                  fieldName = similarField;
                } else {
                  console.warn(`Пропускаем поле ${fieldName}, так как оно не найдено в модели`);
                  continue;
                }
              }
              
              if (!imagesByField[fieldName]) {
                imagesByField[fieldName] = [];
              }
              imagesByField[fieldName].push({
                url: url,
                preview: url,
                file: null
              });
            }
          }
          
          console.log('Сгруппированные изображения по полям:', imagesByField);
          
          // Загружаем сгруппированные изображения в соответствующие поля
          for (const [fieldName, images] of Object.entries(imagesByField)) {
            // Загружаем только если поле еще не заполнено из parameters
            if (!loadedFileFields[fieldName]) {
              // Если одно изображение - объект, если несколько - массив
              if (images.length === 1) {
                loadedFileFields[fieldName] = {
                  id: 0,
                  ...images[0]
                };
              } else {
                loadedFileFields[fieldName] = images.map((img, idx) => ({
                  id: idx,
                  ...img
                }));
              }
              console.log(`Загружено изображение в поле ${fieldName}:`, loadedFileFields[fieldName]);
            } else {
              // Если поле уже заполнено из parameters, логируем для отладки
              console.log(`Поле ${fieldName} уже заполнено из parameters, пропускаем input_image_urls`);
            }
          }
        } else {
          // Старый формат (обратная совместимость): массив строк
          const fileFieldsList = getFileFields();
          
          // Определяем поле для input_image_urls, используя приоритет полей (как на бэкенде)
          // Приоритет: input_image > start_image > image_input > image
          const priorityFields = ["input_image", "start_image", "image_input", "image"];
          let targetField = null;
          
          // Ищем первое доступное поле по приоритету
          for (const fieldName of priorityFields) {
            const field = fileFieldsList.find(f => f.name === fieldName);
            if (field) {
              targetField = field;
              break;
            }
          }
          
          // Если не нашли в приоритетных полях, используем первое file-поле
          if (!targetField && fileFieldsList.length > 0) {
            targetField = fileFieldsList[0];
          }
          
          // Загружаем изображения в найденное поле, только если оно еще не заполнено из parameters
          if (targetField && !loadedFileFields[targetField.name]) {
            // ВАЖНО: Фильтруем file:// URLs
            const validUrls = job.input_image_urls.filter(url => {
              if (typeof url === 'string' && url.toLowerCase().startsWith('file://')) {
                console.error(`Обнаружен недопустимый file:// URL в сохраненной задаче: ${url}`);
                toast.error(
                  'Обнаружен локальный путь к файлу в сохраненной задаче. ' +
                  'Пожалуйста, выберите файл заново через диалог выбора файлов.'
                );
                return false;
              }
              return true;
            });
            
            if (validUrls.length > 0) {
              loadedFileFields[targetField.name] = validUrls.map((url, idx) => ({
                id: idx,
                url: url,
                preview: url,
                file: null
              }));
            }
          } else if (targetField && loadedFileFields[targetField.name]) {
            // Если поле уже заполнено из parameters, логируем для отладки
            console.log(`Поле ${targetField.name} уже заполнено из parameters, пропускаем input_image_urls`);
          }
        }
      }
      
      // У ролика из истории нет durationSeconds: длина измерялась только при загрузке файла.
      // Для Seedance 2.5 она сохранена в video_duration — иначе цена редактирования становится 0.
      const savedVideoDuration = Number(job.parameters?.video_duration);
      if (isSeedance25Model(model) && Number.isFinite(savedVideoDuration) && savedVideoDuration > 0) {
        const videos = loadedFileFields.video_urls;
        const list = Array.isArray(videos) ? videos : (videos ? [videos] : []);
        if (list[0] && !list[0].durationSeconds) {
          const withDuration = { ...list[0], durationSeconds: savedVideoDuration };
          loadedFileFields.video_urls = Array.isArray(videos)
            ? [withDuration, ...list.slice(1)]
            : withDuration;
        }
      }
      if (typeof job.parameters?.draft === 'boolean') {
        setSeedanceDraft(job.parameters.draft === true);
      }

      // Устанавливаем загруженные file-поля
      console.log('Устанавливаем file-поля:', loadedFileFields);
      setFileFields(loadedFileFields);

      // Kling v3/o3: восстанавливаем элементы из job.parameters.elements
      if (job.parameters && Array.isArray(job.parameters.elements) && job.parameters.elements.length > 0) {
        const restored = job.parameters.elements.map((el) => ({
          frontal: el.frontal_image_url ? { url: el.frontal_image_url, preview: el.frontal_image_url } : null,
          reference: (el.reference_image_urls || []).map((u) => ({ url: u, preview: u })),
          video: el.video_url ? { url: el.video_url, preview: el.video_url } : null
        }));
        setKlingElements(restored);
      }
      
      // Логируем для отладки
      if (Object.keys(loadedFileFields).length > 0) {
        console.log('✅ Загружены file-поля из job:', Object.keys(loadedFileFields), loadedFileFields);
      } else {
        console.warn('⚠️ Не найдено file-полей в job:', {
          hasInputImageUrls: !!(job.input_image_urls && job.input_image_urls.length > 0),
          inputImageUrlsType: job.input_image_urls ? typeof job.input_image_urls : 'null',
          inputImageUrlsLength: job.input_image_urls ? job.input_image_urls.length : 0,
          inputImageUrlsFirstItem: job.input_image_urls && job.input_image_urls.length > 0 ? job.input_image_urls[0] : null,
          hasParameters: !!job.parameters,
          parametersKeys: job.parameters ? Object.keys(job.parameters) : []
        });
      }

      // Если job завершен - показываем результат
      // Проверяем, что output_image_url валидный (не null, не "None", не пустой)
      const isValidOutput = job.output_image_url && 
                            job.output_image_url !== 'None' && 
                            job.output_image_url !== 'null' &&
                            job.output_image_url.trim() !== '';
      
      if (job.status === 'completed' && isValidOutput) {
      setResult(jobToResult(job));
      // Сбрасываем состояния загрузки изображения при новом результате
      setImageLoading(true);
      setImageLoadError(false);
      imageRetryCountRef.current = 0;
        setIsProcessing(false);
      } else if (job.status === 'failed' && isValidOutput) {
        // Результат уже сохранён, несмотря на failed (network timeout после скачивания)
        setResult(jobToResult(job));
        setImageLoading(true);
        setImageLoadError(false);
        imageRetryCountRef.current = 0;
        setIsProcessing(false);
      } else if (job.status === 'failed') {
        // Показываем понятное сообщение об ошибке из бэкенда
        const errorMessage = job.user_friendly_error || job.error_message || 'Произошла ошибка при генерации. Пожалуйста, попробуйте еще раз.';
        setErrorMessage(errorMessage); // Сохраняем ошибку для отображения на странице
        toast.error(errorMessage);
        setIsProcessing(false);
      } else {
        // Job еще выполняется - запускаем polling
        pollJobStatus(publicId);
      }
      
      // НЕ устанавливаем activeJobId на старый ID - при новой генерации будет создан новый ID
      // setActiveJobId(publicId); // Убрано - чтобы при новой генерации создавался новый ID
    } catch (error) {
      console.error('Error loading job from URL:', error);
      toast.error('Не удалось загрузить генерацию');
      setIsProcessing(false);
      // Не устанавливаем isViewingMode - форма должна быть редактируемой
    }
  };

  const loadPreset = useCallback(async (presetId) => {
    try {
      const response = await axios.get(`/api/presets/${presetId}`);
      const presetData = response.data;
      
      // Преобразуем данные из БД в формат, ожидаемый компонентом
      const preset = {
        id: presetData.preset_id,
        title: presetData.title,
        userInputs: presetData.user_inputs || [],
        totalCost: presetData.total_cost !== undefined && presetData.total_cost !== null ? presetData.total_cost : null
      };

      if (!preset.id) {
        toast.error('Пресет не найден');
        return;
      }

      setPresetModels({});
      setActivePreset(preset);

      // Инициализируем значения полей из пресета
      if (preset.userInputs) {
        const initialInputs = {};
        preset.userInputs.forEach(input => {
          if (input.type === 'text') {
            initialInputs[input.name] = '';
          }
        });
        setPresetInputs(initialInputs);
      }

      // Применяем параметры пресета
      const presetParams = applyPresetToModel(preset, model);
      
      // Скрытый промпт и другие параметры пресета применяются на бэкенде
      // Устанавливаем динамические параметры
      setDynamicParams(prev => {
        const next = { ...prev, ...presetParams };

        // Для "Эффектов": по умолчанию aspect_ratio = match_input_image (Replicate) или auto (Nano Banana Pro / Nano Banana 2)
        const aspectCfg = model.input_parameters?.aspect_ratio;
        const enumValues = Array.isArray(aspectCfg?.enum) ? aspectCfg.enum : [];
        if (enumValues.includes('match_input_image') || enumValues.includes('auto')) {
          const isFalNanoBanana = isFalNanoBananaModel(model);
          const current = next.aspect_ratio;
          if (
            current === undefined ||
            current === null ||
            current === '' ||
            current === model.default_parameters?.aspect_ratio
          ) {
            next.aspect_ratio = isFalNanoBanana ? 'auto' : 'match_input_image';
          } else if (isFalNanoBanana && current === 'match_input_image') {
            // Пресет мог подставить match_input_image — Nano Banana принимает auto
            next.aspect_ratio = 'auto';
          }
        }

        return next;
      });

      setPresetFixedInputs({});
    } catch (error) {
      console.error('Error loading preset:', error);
      toast.error('Ошибка загрузки пресета');
    }
  }, [model]);

  const pollJobStatus = (publicId) => {
    let hasShownResult = false;
    let pollCount = 0;
    const isLongJob = model.category === 'video_generation' || model.category === 'music_generation';
    const MAX_POLLS = isLongJob ? 240 : 120; // музыка и видео дольше, речь и картинки — до 10 мин
    
    const checkInterval = setInterval(async () => {
      pollCount++;
      
      // Останавливаем polling после максимального количества попыток
      if (pollCount > MAX_POLLS) {
        clearInterval(checkInterval);
        setIsProcessing(false);
        setActiveJobId(null); // Очищаем ID активной генерации
        toast.error('Превышено время ожидания. Проверьте историю генераций.');
        return;
      }
      
      try {
        const statusResponse = await axios.get(`/api/services/jobs/by-id/${publicId}`);
        const updatedJob = statusResponse.data;
        const fq = updatedJob.parameters?.fal_queue_status;
        if (fq) {
          setFalQueueStatus(fq);
        }

        const isValidOutput = updatedJob.output_image_url &&
                              updatedJob.output_image_url !== 'None' &&
                              updatedJob.output_image_url !== 'null' &&
                              updatedJob.output_image_url.trim() !== '';

        if (updatedJob.status === 'completed') {
          clearInterval(checkInterval);
          
          if (!hasShownResult && isValidOutput) {
            setResult(jobToResult(updatedJob));
            // Сбрасываем состояния загрузки изображения при новом результате
            setImageLoading(true);
            setImageLoadError(false);
            setIsProcessing(false);
            setActiveJobId(null); // Очищаем ID активной генерации, чтобы разрешить новую генерацию
            
            // Обновляем URL с новым ID генерации; сохраняем ?preset= и ?from= (иначе «Назад» уходит в /models).
            const urlParams = new URLSearchParams(location.search);
            const fromParam = urlParams.get('from');
            const presetParamKeep = urlParams.get('preset');
            
            const newUrlParams = new URLSearchParams();
            newUrlParams.set('id', updatedJob.public_id);
            if (fromParam) {
              newUrlParams.set('from', fromParam);
            }
            if (presetParamKeep) {
              newUrlParams.set('preset', presetParamKeep);
            }
            
            navigate(`${location.pathname}?${newUrlParams.toString()}`, { replace: true });
            
            if (onBalanceUpdate) {
              onBalanceUpdate();
            }
            
            // Воспроизводим звук успешной генерации
            playSuccessSound();
            
            toast.success('Готово!');
            hasShownResult = true;
          } else if (!isValidOutput) {
            // Если статус completed, но нет валидного результата - показываем ошибку
            clearInterval(checkInterval);
            const errorMsg = 'Генерация завершена, но результат отсутствует. Проверьте историю генераций.';
            setErrorMessage(errorMsg); // Сохраняем ошибку для отображения на странице
            toast.error(errorMsg);
            setIsProcessing(false);
            setActiveJobId(null); // Очищаем ID активной генерации, чтобы разрешить новую генерацию
          }
        } else if (updatedJob.status === 'failed' && isValidOutput) {
          clearInterval(checkInterval);
          setResult(jobToResult(updatedJob));
          setImageLoading(true);
          setImageLoadError(false);
          setIsProcessing(false);
          setActiveJobId(null);
          if (onBalanceUpdate) {
            onBalanceUpdate();
          }
          playSuccessSound();
          toast.success('Готово!');
          hasShownResult = true;
        } else if (updatedJob.status === 'failed') {
          clearInterval(checkInterval);
          // Показываем понятное сообщение об ошибке из бэкенда
          const errorMessage = updatedJob.user_friendly_error || updatedJob.error_message || 'Произошла ошибка при генерации. Пожалуйста, попробуйте еще раз.';
          setErrorMessage(errorMessage); // Сохраняем ошибку для отображения на странице
          toast.error(errorMessage);
          setIsProcessing(false);
          setActiveJobId(null); // Очищаем ID активной генерации, чтобы разрешить новую генерацию
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        // При ошибке не останавливаем сразу, продолжаем попытки
        if (error.response?.status === 404 || error.response?.status === 401) {
          clearInterval(checkInterval);
          setIsProcessing(false);
          setActiveJobId(null); // Очищаем ID активной генерации
        }
      }
    }, 5000); // Увеличено с 3 до 5 секунд для уменьшения нагрузки
  };

  // Обратная совместимость: старые функции для первого file-поля (для совместимости со старым кодом)
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    // Используем первое file-поле из списка
    const fileFieldsList = getFileFields();
    const firstField = fileFieldsList[0];
    if (firstField) {
      const fieldConfig = firstField.config;
      const isMultiple = model.supports_multiple_images || (fieldConfig.type === 'array' || fieldConfig.multiple === true);
      const maxFiles = fieldConfig?.maxItems || fieldConfig?.max || (isMultiple ? 10 : 1);
      handleFileSelect(firstField.name, files, isMultiple, maxFiles);
    }
  };

  const removeImage = (index) => {
    // Используем первое file-поле из списка
    const fileFieldsList = getFileFields();
    const firstField = fileFieldsList[0];
    if (firstField) {
      handleFileRemove(firstField.name, index);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Защита от повторных нажатий: проверяем ref сразу (синхронно)
    if (isSubmittingRef.current) {
      console.log('Запрос уже отправляется, игнорируем повторное нажатие');
      return;
    }

    // Защита от повторных нажатий: проверяем, не идет ли уже обработка
    if (isProcessing) {
      toast.error('Генерация уже выполняется. Дождитесь завершения.');
      return;
    }

    // Проверяем, есть ли активная генерация
    if (activeJobId) {
      toast.error('Дождитесь завершения текущей генерации перед запуском новой.');
      return;
    }

    if (!hasActiveSubscription(user)) {
      toast.error('Подписка закончилась. Оформите подписку для продолжения.');
      navigate('/topup');
      return;
    }

    // Устанавливаем флаг отправки сразу (синхронно, до async операций)
    // Это предотвратит повторные нажатия на мобильных устройствах
    isSubmittingRef.current = true;
    setIsSubmitting(true); // Также устанавливаем состояние для визуальной блокировки кнопки

    // Проверяем баланс с учетом рассчитанной стоимости (пропускаем проверку, если стоимость = 0)
    if (calculatedCost > 0 && currentBalance < calculatedCost) {
      toast.error(`Недостаточно токенов. Требуется: ${calculatedCost}, у вас: ${currentBalance}`);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    // Валидация
    if (hasValidationErrors) {
      toast.error('Исправьте ошибки валидации перед генерацией');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    // Если есть активный пресет, промпт будет применен на бэкенде из hidden_prompt
    // Валидация промпта не требуется для пресетов
    // Проверяем, обязателен ли промпт из конфига модели
    const pathPresetMatch = location.pathname.match(/^\/presets\/([^/]+)/);
    const presetIdFromUrl =
      initialPresetId ||
      new URLSearchParams(window.location.search).get('preset') ||
      (pathPresetMatch ? pathPresetMatch[1] : null);
    const presetContext =
      !!activePreset || !!presetIdFromUrl;

    const promptRequired = model.input_parameters?.prompt?.required === true;
    if (model.supports_prompt && promptRequired && !prompt.trim() && !presetContext) {
      toast.error('Введите описание для генерации');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    if (model.slug === 'suno-v6' && !presetContext) {
      const custom = dynamicParams.custom_mode === true;
      const instrumental = dynamicParams.instrumental === true;
      if (!custom && !prompt.trim()) {
        toast.error('Опишите, какой трек нужен');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      if (custom && !String(dynamicParams.title || '').trim()) {
        toast.error('Укажите название трека');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      if (custom && !String(dynamicParams.style || '').trim()) {
        toast.error('Укажите стиль');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      if (custom && !instrumental && !prompt.trim() && !String(dynamicParams.lyrics || '').trim()) {
        toast.error('Добавьте текст песни или описание');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }

    if (isElevenLabsDialogueModel(model) && !presetContext) {
      const lines = Array.isArray(dynamicParams.dialogue) ? dynamicParams.dialogue : [];
      const texts = lines.map((line) => String(line?.text || '').trim()).filter(Boolean);
      const total = lines.reduce((sum, line) => sum + String(line?.text || '').length, 0);
      if (texts.length === 0) {
        toast.error('Добавьте хотя бы одну реплику');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      if (total > 5000) {
        toast.error('Весь диалог вместе не длиннее 5000 символов');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }

    // Проверка maxLength для любых полей из input_parameters (БД: prompt, negative_prompt и т.д.)
    if (model.input_parameters) {
      for (const [fieldName, fieldConfig] of Object.entries(model.input_parameters)) {
        if (!fieldConfig || fieldConfig.hidden) continue;
        if (fieldConfig.type !== 'string' || fieldConfig.maxLength == null) continue;
        const value = fieldName === 'prompt' ? (prompt || '') : (dynamicParams[fieldName] ?? '');
        if (typeof value !== 'string') continue;
        if (value.length > fieldConfig.maxLength) {
          const msg = fieldConfig.maxLengthMessage || `Не больше ${fieldConfig.maxLength} знаков`;
          toast.error(msg);
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          return;
        }
      }
    }

    // Проверяем обязательные file-поля
    // Для пресета обязательны только поля из userInputs; остальная внутренняя конфигурация применяется на бэкенде.
    const fileFieldsList = getVisibleFileFields();
    if (activePreset?.userInputs?.length > 0) {
      // Валидируем file-поля из userInputs напрямую через fileFields state
      const requiredFileInputs = activePreset.userInputs.filter(
        input => input.type === 'file' && input.required
      );
      for (const input of requiredFileInputs) {
        const fieldName = input.fieldName || input.name;
        const fieldValue = fileFields[fieldName];
        if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
          toast.error(`Загрузите ${input.label || input.description || fieldName}`);
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          return;
        }
      }
    }
    const requiredFields = activePreset
      ? []  // уже проверили выше
      : fileFieldsList.filter(f => f.config.required);

    const genModeSubmit = getSeedance2GenerationMode(model, dynamicParams);
    if (isSeedance2Unified && isSeedance2ImageOnlyMode(genModeSubmit)) {
      if (!seedance2FileFieldHasValue(fileFields.image_url)) {
        toast.error('Загрузите стартовое изображение для режима «Изображение в видео»');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }
    if (isSeedance2Unified && isSeedance2ReferenceMode(genModeSubmit)) {
      const hasRefs =
        seedance2FileFieldHasValue(fileFields.image_urls) ||
        seedance2FileFieldHasValue(fileFields.video_urls);
      if (!hasRefs) {
        toast.error('Загрузите хотя бы одно референсное изображение или видео');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }
    if (isSeedance25VideoEditMode(model, genModeSubmit)) {
      if (!seedance2FileFieldHasValue(fileFields.video_urls)) {
        toast.error('Загрузите видео для редактирования');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }

    const audioFieldItems = promptMediaFileItems(fileFields.audio_urls);
    if (audioFieldItems.some((x) => !isValidAudioFileFieldEntry(x))) {
      toast.error('В «Референсное аудио» загружайте только MP3 или WAV — не изображения.');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }
    
    for (const field of requiredFields) {
      const fieldValue = fileFields[field.name];
      if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
        toast.error(`Загрузите ${field.config.description || field.name}`);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }

    // Валидация размера файлов по URL (HEAD): сразу показываем ошибку, если файл больше лимита
    for (const field of fileFieldsList) {
      const fieldValue = fileFields[field.name];
      if (!fieldValue) continue;
      const { isVideo, isAudio } = getFileFieldType(field.name, field.config);
      const defaultMB = isVideo || isAudio ? 50 : 10;
      let maxFileSizeMB = field.config?.maxFileSize ?? model.max_file_size_mb ?? defaultMB;
      if (isSeedance25VideoField(model, field.name)) {
        maxFileSizeMB = SEEDANCE25_VIDEO_MAX_MB;
      }
      const items = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
      for (const item of items) {
        const url = typeof item === 'string' ? item : (item?.url || item?.preview);
        if (isSeedance25VideoField(model, field.name)) {
          const formatError = seedance25VideoUrlError(url);
          if (formatError) {
            toast.error(formatError);
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            return;
          }
        }
        if (typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
          try {
            await checkUrlFileSize(url, maxFileSizeMB, field.config?.name || field.name);
          } catch (err) {
            toast.error(err.message);
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            return;
          }
        }
      }
    }

    try {
      // НЕ блокируем форму - пользователь может продолжать редактировать
      // Используем отдельное состояние для отслеживания активных генераций
      setResult(null);
      setErrorMessage(null); // Очищаем ошибку при новой генерации
      setFalQueueStatus(null);
      setImageLoading(true);
      setImageLoadError(false);
      imageRetryCountRef.current = 0;

      // Функция для загрузки файла на сервер
      const uploadFile = async (file) => {
        if (!file) return null;
        
        // ВАЖНО: Проверяем на file:// URL (локальные пути) - они недопустимы
        const validateUrl = (url) => {
          if (typeof url === 'string' && url.toLowerCase().startsWith('file://')) {
            throw new Error(
              'Обнаружен локальный путь к файлу. Пожалуйста, выберите файл через диалог выбора файлов, ' +
              'а не используйте прямой путь к файлу на вашем компьютере.'
            );
          }
        };
        
        // Если это уже URL (не File объект), проверяем и возвращаем его
        if (typeof file === 'string') {
          validateUrl(file);
          return file;
        }
        
        // Если это объект с url/preview, проверяем, является ли это base64 или URL
        if (file.url || file.preview) {
          const url = file.url || file.preview;
          
          // ВАЖНО: Проверяем на file:// URL перед обработкой
          validateUrl(url);
          
          // Если это base64 data URL, загружаем на сервер
          if (url.startsWith('data:')) {
            // Конвертируем base64 в Blob и загружаем
            const response = await fetch(url);
            const blob = await response.blob();
            const formData = new FormData();
            formData.append('file', blob, file.name || 'image.png');
            // Не задаём Content-Type вручную — axios сам подставит multipart/form-data с boundary
            const uploadResponse = await axios.post('/api/services/upload', formData);
            return uploadResponse.data.url;
          }
          // Относительный /static/results|uploads после drag результата:
          // перезаливаем через upload, чтобы в process ушёл нормальный uploads URL
          if (typeof url === 'string' && url.startsWith('/static/')) {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Не удалось прочитать файл результата (${response.status})`);
            }
            const blob = await response.blob();
            const formData = new FormData();
            const fileName = file.name || url.split('/').pop() || 'image.png';
            formData.append('file', blob, fileName);
            const uploadResponse = await axios.post('/api/services/upload', formData);
            return uploadResponse.data.url;
          }
          // Если это уже URL, возвращаем его
          return url;
        }
        
        // Если это File объект, загружаем его
        if (file instanceof File) {
          const formData = new FormData();
          formData.append('file', file);
          // Не задаём Content-Type вручную — axios сам подставит multipart/form-data с boundary
          const uploadResponse = await axios.post('/api/services/upload', formData);
          return uploadResponse.data.url;
        }
        
        return null;
      };

      // Загружаем все файлы на сервер и получаем URL
      const uploadedFileUrls = {};
      
      // Сначала обрабатываем поля из модели
      for (const field of fileFieldsList) {
        const fieldValue = fileFields[field.name];
        if (fieldValue) {
          try {
            if (Array.isArray(fieldValue)) {
              const urls = await Promise.all(
                fieldValue.map(async (img) => {
                  try {
                    if (img.file) {
                      return await uploadFile(img.file);
                    } else if (img.url || img.preview) {
                      return await uploadFile(img);
                    }
                    return null;
                  } catch (error) {
                    toast.error(`Ошибка загрузки файла для поля ${field.name}: ${error.message}`);
                    throw error; // Не продолжаем отправку без загруженного файла
                  }
                })
              );
              uploadedFileUrls[field.name] = urls.filter(url => url !== null);
            } else {
              if (fieldValue.file) {
                uploadedFileUrls[field.name] = await uploadFile(fieldValue.file);
              } else if (fieldValue.url || fieldValue.preview) {
                uploadedFileUrls[field.name] = await uploadFile(fieldValue);
              }
            }
          } catch (error) {
            toast.error(`Ошибка загрузки файла для поля ${field.name}: ${error.message}`);
            throw error; // Прерываем отправку формы
          }
        }
      }
      
      // Затем обрабатываем поля из пресета, которых нет в модели
      if (activePreset && activePreset.userInputs) {
        console.log('🔍 Обрабатываем поля из пресета:', activePreset.userInputs);
        for (const userInput of activePreset.userInputs) {
          if (userInput.type === 'file') {
            const fieldName = userInput.fieldName || userInput.name;
            console.log(`  🔍 Обрабатываем поле пресета: name=${userInput.name}, fieldName=${fieldName}`);
            // Пропускаем поля, которые уже обработаны выше (из модели)
            if (!uploadedFileUrls[fieldName]) {
              const fieldValue = fileFields[fieldName];
              console.log(`    Значение в fileFields[${fieldName}]:`, fieldValue);
              if (fieldValue) {
                try {
                  if (Array.isArray(fieldValue)) {
                    const urls = await Promise.all(
                      fieldValue.map(async (img) => {
                        try {
                          if (img.file) {
                            return await uploadFile(img.file);
                          } else if (img.url || img.preview) {
                            return await uploadFile(img);
                          }
                          return null;
                        } catch (error) {
                          toast.error(`Ошибка загрузки файла для поля ${fieldName}: ${error.message}`);
                          throw error;
                        }
                      })
                    );
                    uploadedFileUrls[fieldName] = urls.filter(url => url !== null);
                    console.log(`    ✅ Загружено ${uploadedFileUrls[fieldName].length} URL для ${fieldName}`);
                  } else {
                    if (fieldValue.file) {
                      uploadedFileUrls[fieldName] = await uploadFile(fieldValue.file);
                      console.log(`    ✅ Загружен файл для ${fieldName}:`, uploadedFileUrls[fieldName]);
                    } else if (fieldValue.url || fieldValue.preview) {
                      uploadedFileUrls[fieldName] = await uploadFile(fieldValue);
                      console.log(`    ✅ Загружен URL для ${fieldName}:`, uploadedFileUrls[fieldName]);
                    }
                  }
                } catch (error) {
                  toast.error(`Ошибка загрузки файла для поля ${fieldName}: ${error.message}`);
                  throw error; // Прерываем отправку формы
                }
              } else {
                console.log(`    ⚠️ Нет значения для поля ${fieldName} в fileFields`);
              }
            } else {
              console.log(`    ⚠️ Поле ${fieldName} уже обработано выше (из модели)`);
            }
          }
        }
      }

      // Загружаем изображения с информацией о полях (новый формат)
      // Формат: [{ field: "start_image", url: "..." }, { field: "last_frame", url: "..." }]
      const imageUrls = [];
      
      // Определяем приоритет полей (как на бэкенде)
      const priorityFields = ["input_image", "start_image", "image_input", "image"];
      
      // Сначала добавляем поля по приоритету
      for (const priorityFieldName of priorityFields) {
        if (uploadedFileUrls[priorityFieldName]) {
          const fieldValue = uploadedFileUrls[priorityFieldName];
          const fieldArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
          for (const url of fieldArray) {
            if (url) {
              imageUrls.push({ field: priorityFieldName, url: url });
            }
          }
        }
      }
      
      // Затем добавляем остальные поля (не в приоритетных)
      for (const [fieldName, urls] of Object.entries(uploadedFileUrls)) {
        if (!priorityFields.includes(fieldName)) {
          const fieldArray = Array.isArray(urls) ? urls : [urls];
          for (const url of fieldArray) {
            if (url) {
              imageUrls.push({ field: fieldName, url: url });
            }
          }
        }
      }

      // Подготавливаем parameters - только пользовательские изменения параметров
      // НЕ включаем: prompt (отправляется отдельно), file-поля (в image_urls), 
      // параметры из пресета (применяются на бэкенде), fixed_inputs (применяются на бэкенде)
      const parameters = {};
      
      // Получаем список полей пресета, которые не нужно отправлять в parameters
      const presetFieldNames = new Set();
      if (activePreset && activePreset.userInputs) {
        activePreset.userInputs.forEach(input => {
          const fieldName = input.fieldName || input.name;
          presetFieldNames.add(fieldName);
          presetFieldNames.add(input.name);
        });
      }
      
      // Получаем список file-полей, которые уже в image_urls
      const fileFieldsInImageUrls = new Set();
      imageUrls.forEach(item => {
        if (item.field) {
          fileFieldsInImageUrls.add(item.field);
            }
      });
      
      // Добавляем только пользовательские изменения параметров (не из пресета, не file-поля)
      for (const [paramName, paramValue] of Object.entries(dynamicParams)) {
        // Пропускаем prompt, file-поля и поля пресета
        if (paramName === 'prompt') continue;
        if (
          paramName === 'draft' ||
          paramName === 'seedance_draft' ||
          paramName === 'evolink_task_id' ||
          paramName === 'draft_expires_at' ||
          paramName === 'source_task_id' ||
          paramName === 'fal_queue_status' ||
          paramName === 'fal_queue_position'
        ) {
          continue;
        }
        if (fileFieldsInImageUrls.has(paramName)) continue;
        if (presetFieldNames.has(paramName)) continue;
        
        // Пропускаем параметры, которые есть в пресете (они применяются на бэкенде)
        if (activePreset) {
          // Пропускаем resolution, aspect_ratio и другие параметры пресета
          // resolution применится на бэкенде из пресета; aspect_ratio оставляем (пользователь может выбрать)
          if (paramName === 'resolution') {
            continue;
          }
        }
        
        const showWhen = model.input_parameters?.[paramName]?.show_when;
        if (showWhen && typeof showWhen === 'object') {
          const visible = Object.entries(showWhen).every(([key, expected]) => {
            const current = dynamicParams[key] !== undefined
              ? dynamicParams[key]
              : model.default_parameters?.[key];
            return current === expected;
          });
          if (!visible) continue;
        }

        if (paramValue !== null && paramValue !== undefined && paramValue !== '') {
          let value = paramValue;
          // Nano Banana Pro и Nano Banana 2 не принимают match_input_image — только auto или конкретные соотношения
          if (paramName === 'aspect_ratio' && value === 'match_input_image') {
            const isFalNanoBanana = isFalNanoBananaModel(model);
            if (isFalNanoBanana) value = 'auto';
          }
          parameters[paramName] = value;
        }
      }

      // Kling v3 / o3: валидация элементов и загрузка медиа
      if (isKlingWithElements(model) && klingElements.length > 0) {
        const elementsWithVideo = klingElements.filter(el => el.video && (el.video.preview || el.video.url));
        if (elementsWithVideo.length > 1) {
          toast.error('Только один элемент может содержать видео.');
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          return;
        }
        for (let i = 0; i < klingElements.length; i++) {
          const el = klingElements[i];
          const refs = Array.isArray(el.reference) ? el.reference : [];
          const hasFrontal = !!(el.frontal && (el.frontal.preview || el.frontal.url));
          const hasVideo = !!(el.video && (el.video.preview || el.video.url));
          const hasRefs = refs.length > 0;
          if (!hasRefs && !hasVideo) {
            toast.error(`Элемент ${i + 1}: добавьте референс — либо изображения (1–3), либо видео.`);
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            return;
          }
        }
        const resolveItemUrl = async (item) => {
          if (!item) return null;
          if (typeof item === 'string') return item;
          if (item.url && !item.file) return item.url;
          try {
            return await uploadFile(item.file || item);
          } catch (err) {
            toast.error(`Ошибка загрузки файла элемента: ${err.message}`);
            return null;
          }
        };
        const elementsPayload = [];
        for (const el of klingElements) {
          const frontal_url = await resolveItemUrl(el.frontal ?? null);
          const refs = Array.isArray(el.reference) ? el.reference : [];
          const reference_urls = await Promise.all(refs.map(r => resolveItemUrl(r)));
          const video_url = await resolveItemUrl(el.video ?? null);
          const hasContent = frontal_url || reference_urls.some(Boolean) || video_url;
          if (hasContent) {
            elementsPayload.push({
              frontal_image_url: frontal_url || null,
              reference_image_urls: reference_urls.filter(Boolean),
              video_url: video_url || null
            });
          }
        }
        if (elementsPayload.length > 0) {
          parameters.elements = elementsPayload;
        }
      }

      // ВАЖНО: Для kling motion-control (v2.6 / v3) добавляем video_duration в parameters
      const isKlingMotionControl = isKlingMotionControlModel(model);
      if (isKlingMotionControl && videoDuration && !isNaN(videoDuration) && isFinite(videoDuration) && videoDuration > 0) {
        parameters.video_duration = videoDuration;
        console.log(`🎬 kling-motion-control: добавляю video_duration=${videoDuration} в parameters`);
      }
      const isKlingO3V2v = model.slug === 'kling-o3-v2v' || model.model_id?.toLowerCase().includes('video-to-video/edit');
      if (isKlingO3V2v && videoDuration && !isNaN(videoDuration) && isFinite(videoDuration) && videoDuration > 0) {
        parameters.video_duration = Math.floor(videoDuration);
        console.log(`🎬 kling-o3-v2v: добавляю video_duration=${parameters.video_duration} (целые сек) в parameters`);
      }
      if (isSeedance25Model(model)) {
        delete parameters.seedance_draft;
        delete parameters.evolink_task_id;
        delete parameters.draft_expires_at;
        delete parameters.source_task_id;
        parameters.draft = seedanceDraft;
        if (seedanceDraft) {
          parameters.resolution = '480p';
        }
      }
      if (isSeedance25VideoEditMode(model, getSeedance2GenerationMode(model, dynamicParams))) {
        const source = promptMediaFileItems(fileFields.video_urls)[0];
        const sourceSeconds = Number(source?.durationSeconds);
        parameters.generation_mode = 'video-edit';
        if (Number.isFinite(sourceSeconds) && sourceSeconds > 0) {
          parameters.video_duration = Math.max(1, Math.ceil(sourceSeconds));
        }
      } else if (isSeedance25Model(model) && seedanceDraft) {
        let seconds = dynamicParams.duration ?? model.default_parameters?.duration ?? 5;
        if (typeof seconds === 'string' && seconds.trim().toLowerCase() === 'auto') {
          seconds = 5;
        }
        seconds = typeof seconds === 'string' ? parseInt(seconds, 10) : Number(seconds);
        parameters.video_duration = Number.isFinite(seconds) && seconds > 0 ? Math.max(1, Math.ceil(seconds)) : 5;
      }
      
      // Валидация для модели kling-2-6-pro-i2v
      // end_image_url не поддерживается, когда generate_audio = true
      const isKling26ProI2V = model.slug === 'kling-2-6-pro-i2v'
        || model.model_id === 'fal-ai/kling-video/v2.6/pro/image-to-video'
        || model.model_id === 'kwaivgi/kling-v2.6-pro/image-to-video';
      if (isKling26ProI2V) {
        // Проверяем значение generate_audio (из dynamicParams или default_parameters)
        const generateAudio = dynamicParams.generate_audio !== undefined 
          ? dynamicParams.generate_audio 
          : (model.default_parameters?.generate_audio !== undefined 
              ? model.default_parameters.generate_audio 
              : false);
        
        // Нормализуем значение generate_audio (может быть true, false, "true", "false")
        const isGenerateAudioEnabled = generateAudio === true || generateAudio === "true" || generateAudio === 1;
        
        if (isGenerateAudioEnabled) {
          // Проверяем наличие end_image_url в разных местах
          let hasEndImageUrl = false;
          
          // 1. Проверяем в imageUrls (новый формат с полем "field")
          hasEndImageUrl = imageUrls.some(item => item.field === 'end_image_url' && item.url);
          
          // 2. Проверяем в uploadedFileUrls (если был загружен напрямую)
          if (!hasEndImageUrl && uploadedFileUrls['end_image_url']) {
            const endImageValue = uploadedFileUrls['end_image_url'];
            hasEndImageUrl = Array.isArray(endImageValue) 
              ? endImageValue.length > 0 && endImageValue.some(url => url)
              : Boolean(endImageValue);
          }
          
          // 3. Проверяем в fileFields (если файл еще не загружен, но выбран)
          if (!hasEndImageUrl && fileFields['end_image_url']) {
            const endImageField = fileFields['end_image_url'];
            hasEndImageUrl = Array.isArray(endImageField)
              ? endImageField.length > 0 && endImageField.some(item => item && (item.file || item.url || item.preview))
              : Boolean(endImageField && (endImageField.file || endImageField.url || endImageField.preview));
          }
          
          // 4. Проверяем в parameters (если был передан напрямую)
          if (!hasEndImageUrl && parameters['end_image_url']) {
            hasEndImageUrl = Boolean(parameters['end_image_url']);
          }
          
          if (hasEndImageUrl) {
            toast.error(
              'Конечное изображение (end_image_url) не поддерживается при включенной генерации аудио. ' +
              'Пожалуйста, отключите генерацию аудио для использования конечного изображения, ' +
              'или удалите конечное изображение для генерации аудио.'
            );
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            return;
          }
        }
      }
      
      // Очищаем старые результаты и ошибки перед новой генерацией
      setResult(null);
      setErrorMessage(null);
      
      // Заменяем плейсхолдеры в промпте значениями из presetInputs (если есть активный пресет)
      let finalPrompt = prompt;
      // Если есть активный пресет, скрытый промпт и замена плейсхолдеров обрабатываются на бэкенде
      // Фронтенд отправляет только preset_id и user_inputs
      
      // Сохраняем значения полей пресета в parameters для сохранения в БД
      // Сохраняем в отдельном объекте _preset_values, чтобы они не потерялись и были легко доступны
      if (activePreset && presetInputs) {
        const presetValues = {};
        activePreset.userInputs.forEach(input => {
          const inputName = input.name;
          const inputValue = presetInputs[inputName];
          
          // Сохраняем все значения полей пресета (даже пустые строки для текстовых полей)
          if (input.type === 'text') {
            presetValues[inputName] = inputValue || '';
          }
          // Для file-полей значения уже сохранены выше через uploadedFileUrls
        });
        
        // Сохраняем значения пресета в parameters в специальном объекте
        // НЕ дублируем их в parameters по fieldName - бэкенд использует только _preset_values
        if (Object.keys(presetValues).length > 0) {
          parameters._preset_values = presetValues;
        }
      }
      
      // Логируем данные перед отправкой для отладки
      const presetIdForRequest = activePreset?.id || presetIdFromUrl || null;
      const aiModelIdForRequest = presetIdForRequest ? undefined : model.id;
      console.log('📤 Отправка данных на сервер:', {
        ai_model_id: aiModelIdForRequest,
        preset_id: presetIdForRequest,
        image_urls: imageUrls,
        image_urls_count: imageUrls.length,
        fileFields: Object.keys(fileFields),
        uploadedFileUrls: Object.keys(uploadedFileUrls)
      });

      // Создаем запрос на обработку
      const response = await axios.post('/api/services/process', {
        ai_model_id: aiModelIdForRequest,
        image_urls: imageUrls.length > 0 ? imageUrls : undefined,
        prompt: finalPrompt.trim() || null,
        parameters: parameters,
        preset_id: presetIdForRequest
      });

      const job = response.data;
      toast.success('Генерация запущена!');
      
      // НЕ изменяем URL - остаемся на странице модели
      // Позволяем пользователю продолжать редактировать параметры и генерировать снова
      // При новой генерации создается новый ID (новая задача)

      // Сохраняем ID новой активной генерации
      setActiveJobId(job.public_id);
      
      // Запускаем polling статуса в фоне
      pollJobStatus(job.public_id);
      
      // Показываем индикатор загрузки, но НЕ блокируем форму
      setIsProcessing(true);

    } catch (error) {
      console.error('Error processing:', error);
      // Показываем понятное сообщение об ошибке из бэкенда
      let errorMessage = 'Произошла ошибка при обработке. Пожалуйста, попробуйте еще раз.';
      
      if (error.response?.data) {
        const errorData = error.response.data;

        if (error.response?.status === 402) {
          errorMessage = 'Подписка закончилась. Оформите подписку для продолжения.';
          toast.error(
            <span>
              {errorMessage}{' '}
              <button type="button" className="link-btn" onClick={() => navigate('/topup')}>
                Перейти к тарифам
              </button>
            </span>
          );
          navigate('/topup');
          setErrorMessage(errorMessage);
          setIsProcessing(false);
          return;
        }

        // Обработка ошибок валидации Pydantic (422)
        if (Array.isArray(errorData.detail)) {
          // Формат: [{"type": "...", "loc": [...], "msg": "...", "input": ...}]
          const validationErrors = errorData.detail.map(err => {
            const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : 'поле';
            return `${field}: ${err.msg}`;
          }).join('; ');
          errorMessage = `Ошибка валидации: ${validationErrors}`;
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setErrorMessage(errorMessage); // Сохраняем ошибку для отображения на странице
      toast.error(errorMessage);
      setIsProcessing(false);
    } finally {
      // Сбрасываем флаг отправки в любом случае (успех или ошибка)
      // Это позволит отправить новый запрос после завершения текущего
      isSubmittingRef.current = false;
      setIsSubmitting(false); // Также сбрасываем состояние для разблокировки кнопки
    }
  };

  // Функция для перемещения результата в форму
  const moveResultToForm = useCallback(() => {
    if (!result || !result.output_image_url) {
      toast.error('Нет результата для перемещения');
      return;
    }

    const fileFieldsList = getFileFields();
    if (fileFieldsList.length === 0) {
      toast.error('В этой модели нет полей для загрузки файлов');
      return;
    }

    // Находим первое доступное file-поле
    const firstField = fileFieldsList[0];
    const fieldConfig = firstField.config;
    const isMultiple = fieldConfig.type === 'array' || fieldConfig.multiple === true || 
                      (model.supports_multiple_images && firstField.name === fileFieldsList[0]?.name);
    const maxFiles = fieldConfig?.maxItems || fieldConfig?.max || (isMultiple ? 10 : 1);

    // Получаем текущие файлы в поле
    const currentFiles = fileFields[firstField.name] || [];
    const currentArray = Array.isArray(currentFiles) ? currentFiles : (currentFiles ? [currentFiles] : []);

    // Проверяем, не превышен ли лимит файлов
    if (currentArray.length >= maxFiles) {
      toast.error(`Максимум ${maxFiles} файл${maxFiles === 1 ? '' : 'ов'} для этого поля`);
      return;
    }

    // Создаем объект результата для добавления в поле
    const resultItem = {
      id: Date.now(),
      url: result.output_image_url,
      preview: result.output_image_url,
      file: null,
      name: `result_${result.id || Date.now()}.${isVideoFile(result.output_image_url) ? 'mp4' : 'png'}`
    };

    // Добавляем результат в поле
    setFileFields(prev => {
      if (isMultiple) {
        return {
          ...prev,
          [firstField.name]: [...currentArray, resultItem]
        };
      } else {
        return {
          ...prev,
          [firstField.name]: resultItem
        };
      }
    });

    toast.success('Результат перемещен в форму');
  }, [result, fileFields, getFileFields, model]);

  const downloadResult = (url) => {
    downloadMedia(url, {
      toastId: 'download',
      onLoadingChange: setIsDownloading,
      axios: axios,
    });
  };

  const downloadAudioResults = async () => {
    const tracks = result?.audio_tracks?.length
      ? result.audio_tracks
      : [{ url: result?.output_image_url, title: '' }];
    const files = tracks.filter((track) => track?.url);
    if (!files.length) return;
    setIsDownloading(true);
    try {
      for (let index = 0; index < files.length; index += 1) {
        const track = files[index];
        const title = String(track.title || `pesnya-${index + 1}`)
          .replace(/[^\p{L}\p{N}\s_-]+/gu, '')
          .trim()
          .replace(/\s+/g, '-')
          .slice(0, 60) || `pesnya-${index + 1}`;
        await downloadMedia(track.url, {
          toastId: `download-audio-${index}`,
          axios: axios,
          extension: 'mp3',
          mimeType: 'audio/mpeg',
          filename: `${title}.mp3`,
        });
        if (index < files.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const finishSeedanceDraft = async () => {
    const sourceTaskId = result?.parameters?.evolink_task_id;
    const upscaleCost = seedanceDraftUpscaleCost(result);
    if (!sourceTaskId) {
      toast.error('Черновик нельзя догенерировать: нет идентификатора задачи');
      return;
    }
    if (upscaleCost > 0 && currentBalance < upscaleCost) {
      toast.error('Недостаточно токенов для догенерации в 1080p');
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const response = await axios.post('/api/services/process', {
        ai_model_id: model.id,
        prompt: null,
        parameters: {
          generation_mode: 'draft-to-video',
          source_task_id: sourceTaskId,
          video_duration: result?.parameters?.video_duration ?? result?.parameters?.duration,
          resolution: '1080p',
        },
      });
      toast.success('Догенерация в 1080p запущена');
      setActiveJobId(response.data.public_id);
      setIsProcessing(true);
      pollJobStatus(response.data.public_id);
    } catch (error) {
      const message = error.response?.data?.detail || 'Не удалось запустить догенерацию в 1080p';
      toast.error(typeof message === 'string' ? message : 'Не удалось запустить догенерацию в 1080p');
      setIsProcessing(false);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const draftResultParams = result?.parameters;
  const draftResultReady = isSeedance480Draft(draftResultParams);
  const draftUpscaleReady = seedanceDraftCanUpscale(result);
  const draftUpscaleCost = draftUpscaleReady ? seedanceDraftUpscaleCost(result) : 0;
  const draftUpscaleExpired = draftResultReady && !draftUpscaleReady;

  return (
    <div className="model-playground">
      <div className="playground-header">
        <button 
          className="back-btn" 
          onClick={() => {
            // Всегда используем onBack, который правильно определяет маршрут
            // (Dashboard уже передает правильный маршрут с учетом from=history)
            onBack();
          }}
          title="Назад"
        >
          <ArrowLeft size={20} />
          Назад
        </button>
        
        <div className="header-row">
          <div className="model-title-simple">
            <h1>
              {activePreset ? activePreset.title : (model.display_name || model.model_id)}
            </h1>
            {model.description && !activePreset && (
              <p className="model-description-text">
                {model.description}
              </p>
            )}
            {(headerCostDisplay.mode === 'spent' || !isVideoModel(model)) && (
              <span className="cost-badge">
                <Zap size={16} />
                {headerCostDisplay.mode === 'spent' ? (
                  <>
                    {headerCostDisplay.amount} токенов<span className="cost-badge-detail"> (потрачено)</span>
                  </>
                ) : headerCostDisplay.amount > 0 ? (
                  <>
                    {headerCostDisplay.amount} токенов {generationCostLabel(model)}
                  </>
                ) : (
                  <>
                    {isKlingMotionControlModel(model) || isSeedance25VideoEditMode(model, seedance2Mode)
                      ? 'Загрузите видео для расчета цены'
                      : '0 токенов'
                    }
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="playground-content">
        <div className="input-panel">
          <h2>Параметры генерации</h2>

          <form onSubmit={handleSubmit}>
            {model.supports_prompt && !activePreset && (
              <div className="form-group">
                <label>
                  {model.input_parameters?.prompt?.name || 'Описание'}
                  {model.input_parameters?.prompt?.required && <span className="required">*</span>}
                </label>
                {(isKlingWithElements(model) || seedance2PromptRefs) ? (
                  <div className="prompt-with-elements-wrap">
                    <div className="prompt-overlay-container">
                      <div className="prompt-textarea-suggestions-wrap" style={{ position: 'relative' }}>
                      <div aria-hidden="true" className="prompt-overlay-mirror">
                        {(() => {
                          const text = prompt || '';
                          if (!text) return null;
                          const parts = buildPromptMirrorParts(text, promptSel.start, promptSel.end);
                          return parts.map((p, i) => {
                            const classes = [
                              p.type === 'element' ? `prompt-overlay-tag prompt-element-tag ${p.className}` : '',
                              p.selected ? 'prompt-sel' : '',
                            ].filter(Boolean).join(' ');
                            return (
                              <span key={i} className={classes || undefined}>{p.content}</span>
                            );
                          });
                        })()}
                      </div>
                      <textarea
                        ref={promptTextareaRef}
                        value={prompt}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPrompt(v);
                          adjustPromptTextareaHeight(e.target);
                          updatePromptSel(e.target);
                          const pos = e.target.selectionStart;
                          const before = v.slice(0, pos);
                          const lastAt = before.lastIndexOf('@');
                          if ((isKlingWithElements(model) || seedance2PromptRefs) && lastAt !== -1) {
                            const query = before.slice(lastAt + 1);
                            const fileFieldsList = getVisibleFileFields();
                            const opts = buildPromptMediaTagOptions(
                              fileFieldsList,
                              fileFields,
                              isKlingWithElements(model) ? klingElements : null,
                              { lowercaseMediaTags: seedance2PromptRefs },
                            );
                            const filtered = query ? opts.filter(({ tag }) => tag.toLowerCase().includes(query.toLowerCase())) : opts;
                            setElementSuggestions({ show: filtered.length > 0, start: lastAt, query, selectedIndex: 0 });
                          } else {
                            setElementSuggestions(s => ({ ...s, show: false }));
                          }
                        }}
                        onSelect={(e) => updatePromptSel(e.target)}
                        onKeyUp={(e) => updatePromptSel(e.target)}
                        onKeyDown={(e) => {
                          // Shift+стрелки: обновляем подсветку сразу, не только на keyup
                          if (e.shiftKey) {
                            requestAnimationFrame(() => updatePromptSel(e.target));
                          }
                          if (!elementSuggestions.show) return;
                          const fileFieldsList = getVisibleFileFields();
                          const opts = buildPromptMediaTagOptions(
                            fileFieldsList,
                            fileFields,
                            isKlingWithElements(model) ? klingElements : null,
                            { lowercaseMediaTags: seedance2PromptRefs },
                          ).map((o) => o.tag);
                          const filtered = elementSuggestions.query ? opts.filter(o => o.toLowerCase().includes(elementSuggestions.query.toLowerCase())) : opts;
                          if (e.key === 'ArrowDown') { e.preventDefault(); setElementSuggestions(s => ({ ...s, selectedIndex: Math.min(s.selectedIndex + 1, filtered.length - 1) })); }
                          else if (e.key === 'ArrowUp') { e.preventDefault(); setElementSuggestions(s => ({ ...s, selectedIndex: Math.max(s.selectedIndex - 1, 0) })); }
                          else if (e.key === 'Enter' && filtered.length > 0) {
                            e.preventDefault();
                            const choice = filtered[elementSuggestions.selectedIndex];
                            const ta = promptTextareaRef.current;
                            if (ta) {
                              const start = elementSuggestions.start;
                              const end = ta.selectionStart;
                              const newPrompt = prompt.slice(0, start) + choice + ' ' + prompt.slice(end);
                              setPrompt(newPrompt);
                              setElementSuggestions(s => ({ ...s, show: false }));
                              setTimeout(() => { ta.focus(); ta.setSelectionRange(start + choice.length + 1, start + choice.length + 1); }, 0);
                            }
                          } else if (e.key === 'Escape') { e.preventDefault(); setElementSuggestions(s => ({ ...s, show: false })); }
                        }}
                        onMouseMove={(e) => {
                          if (e.buttons === 1) updatePromptSel(e.target);
                        }}
                        onMouseUp={(e) => updatePromptSel(e.target)}
                        onBlur={() => {
                          setPromptSel({ start: 0, end: 0 });
                          setTimeout(() => setElementSuggestions(s => ({ ...s, show: false })), 150);
                        }}
                        placeholder={
                          seedance2PromptRefs
                            ? 'Опишите сцену. Ссылайтесь на загруженные файлы: @Image1, @Video1, @Audio1 (регистр не важен).'
                            : 'Опишите, что вы хотите создать или изменить. Используйте @Element1, @Element2...'
                        }
                        style={{ minHeight: '100px', resize: 'none', overflowY: 'hidden' }}
                        required={model.input_parameters?.prompt?.required}
                        className={`prompt-textarea-with-overlay ${(model.input_parameters?.prompt?.maxLength != null && (prompt?.length || 0) > (model.input_parameters.prompt.maxLength)) ? 'input-error' : ''}`}
                      />
                    {elementSuggestions.show && (isKlingWithElements(model) || seedance2PromptRefs) && (() => {
                      const fileFieldsList = getVisibleFileFields();
                      const opts = buildPromptMediaTagOptions(
                        fileFieldsList,
                        fileFields,
                        isKlingWithElements(model) ? klingElements : null,
                        { lowercaseMediaTags: seedance2PromptRefs },
                      );
                      const filtered = elementSuggestions.query ? opts.filter(({ tag }) => tag.toLowerCase().includes(elementSuggestions.query.toLowerCase())) : opts;
                      if (filtered.length === 0) return null;
                      return (
                        <div
                          className="prompt-element-suggestions"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: 4,
                            zIndex: 10,
                          }}
                        >
                          {filtered.map(({ tag, type, num }, i) => {
                            const colorOffset =
                              type === 'Video' ? 0 : type === 'Audio' ? 30 : type === 'Image' ? 10 : 20;
                            const colorIndex = (colorOffset + num - 1) % KLING_ELEMENT_COLORS.length;
                            const colorClass = KLING_ELEMENT_COLORS[colorIndex] || 'kling-el-sky';
                            return (
                              <button
                                key={tag}
                                type="button"
                                className={`prompt-element-suggestion-item prompt-element-tag ${colorClass} ${i === elementSuggestions.selectedIndex ? 'selected' : ''}`}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const ta2 = promptTextareaRef.current;
                                  if (ta2) {
                                    const start = elementSuggestions.start;
                                    const end = ta2.selectionStart;
                                    const newPrompt = prompt.slice(0, start) + tag + ' ' + prompt.slice(end);
                                    setPrompt(newPrompt);
                                    setElementSuggestions(s => ({ ...s, show: false }));
                                    setTimeout(() => { ta2.focus(); ta2.setSelectionRange(start + tag.length + 1, start + tag.length + 1); }, 0);
                                  }
                                }}
                                onMouseEnter={() => setElementSuggestions(s => ({ ...s, selectedIndex: i }))}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                      </div>
                    </div>
                    {isSeedance25VideoEditMode(model, seedance2Mode) ? (
                      <p className="field-hint">Опишите правку. Первое видео — @video1. Например: «Отредактируй видео: убери прохожих в @video1».</p>
                    ) : model.input_parameters?.prompt?.description ? (
                      <p className="field-hint">{model.input_parameters.prompt.description}</p>
                    ) : (
                      <p className="field-hint">Введите <span className="rounded bg-surface-alpha px-1 py-[1px] font-mono text-xs text-content-strong">@</span> для ссылки на элементы (например @Element1, @Element2).</p>
                    )}
                  </div>
                ) : (
                  <>
                    <textarea
                      ref={promptTextareaRef}
                      value={prompt}
                      onChange={(e) => {
                        setPrompt(e.target.value);
                        adjustPromptTextareaHeight(e.target);
                      }}
                      placeholder={model.input_parameters?.prompt?.description || 'Опишите, что вы хотите создать или изменить...'}
                      style={{ minHeight: '100px', resize: 'none', overflowY: 'hidden' }}
                      required={model.input_parameters?.prompt?.required}
                      className={(model.input_parameters?.prompt?.maxLength != null && (prompt?.length || 0) > (model.input_parameters.prompt.maxLength)) ? 'input-error' : ''}
                    />
                  </>
                )}
                {model.input_parameters?.prompt?.maxLength != null && (
                  <>
                    <span className="field-hint">
                      {prompt?.length || 0} / {model.input_parameters.prompt.maxLength}
                    </span>
                    {(prompt?.length || 0) > model.input_parameters.prompt.maxLength && (
                      <span className="field-error">
                        {model.input_parameters.prompt.maxLengthMessage || `Не больше ${model.input_parameters.prompt.maxLength} знаков в промпте`}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Режим генерации сразу после промпта (Seedance, GPT Image 2.5 и др.) */}
            {!activePreset && model.input_parameters?.generation_mode && (
              <DynamicFormField
                fieldName="generation_mode"
                fieldConfig={model.input_parameters.generation_mode}
                value={dynamicParams.generation_mode}
                onChange={(value) => handleDynamicParamChange('generation_mode', value)}
                onValidationChange={(error) => handleValidationChange('generation_mode', error)}
              />
            )}
            {isSeedance25Model(model) && !activePreset && (
              <div className="seedance-draft-card">
                <label className="seedance-draft-row">
                  <span className="seedance-draft-copy">
                    <span className="seedance-draft-title">Режим черновика</span>
                    <span className="seedance-draft-badge">только 480p</span>
                  </span>
                  <span className="seedance-draft-switch">
                    <input
                      type="checkbox"
                      role="switch"
                      checked={seedanceDraft}
                      onChange={(event) => setSeedanceDraft(event.target.checked)}
                      aria-label="Режим черновика"
                    />
                    <span className="seedance-draft-slider" />
                  </span>
                </label>
                <p className="field-hint">
                  Сначала быстрый предпросмотр 480p — списывается как обычное видео 480p. Если результат подходит, в панели результата его можно догенерировать в 1080p: финал оплачивается отдельно по тарифу 1080p. Черновик можно догенерировать в течение 24 часов после готовности.
                </p>
              </div>
            )}
            {isSeedance25VideoEditMode(model, seedance2Mode) && (
              <p className="field-hint">Стоимость считается по длительности загруженного видео.</p>
            )}

            {/* Поля из пресета (userInputs) */}
            {activePreset && activePreset.userInputs && activePreset.userInputs.map((input) => {
              // Текстовые поля
              if (input.type === 'text') {
                return (
                  <div key={input.name} className="form-group">
                    <label>
                      {input.label}
                      {input.required && <span className="required">*</span>}
                    </label>
                    <input
                      type="text"
                      value={presetInputs[input.name] || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setPresetInputs(prev => {
                          const updated = {
                            ...prev,
                            [input.name]: newValue
                          };
                          
                          // Замена плейсхолдеров в промпте обрабатывается на бэкенде
                          // Фронтенд только обновляет значения полей
                          
                          return updated;
                        });
                      }}
                      placeholder={input.placeholder || ''}
                      required={input.required}
                    />
                    {input.description && (
                      <div className="field-description">{input.description}</div>
                    )}
                  </div>
                );
              }
              
              // File поля обрабатываются отдельно ниже
              return null;
            })}

            {/* Универсальная отрисовка всех file-полей из input_parameters */}
            {/* При работе с пресетом показываем только поля, указанные в userInputs */}
            {/* Для пресетов также показываем поля из userInputs, которых нет в модели */}
            {(() => {
              // Если есть активный пресет, собираем все file поля из userInputs
              if (activePreset && activePreset.userInputs) {
                const presetFileInputs = activePreset.userInputs.filter(input => input.type === 'file');
                const modelFileFields = getVisibleFileFields();
                const fieldsToShow = [];
                
                // Сначала добавляем поля из модели, которые есть в userInputs
                modelFileFields.forEach(({ name: fieldName, config: fieldConfig }) => {
                  const presetFileInput = presetFileInputs.find(
                    input => input.fieldName === fieldName || input.name === fieldName
                  );
                  if (presetFileInput) {
                    fieldsToShow.push({
                      fieldName,
                      fieldConfig,
                      presetFileInput
                    });
                  }
                });
                
                // Затем добавляем поля из userInputs, которых нет в модели
                presetFileInputs.forEach(presetFileInput => {
                  const fieldName = presetFileInput.fieldName || presetFileInput.name;
                  const existsInModel = modelFileFields.some(f => f.name === fieldName);
                  if (!existsInModel) {
                    // Создаем фиктивный конфиг для поля, которого нет в модели
                    fieldsToShow.push({
                      fieldName,
                      fieldConfig: {
                        type: 'file',
                        file: true,
                        description: presetFileInput.description || '',
                        required: presetFileInput.required || false
                      },
                      presetFileInput
                    });
                  }
                });
                
                return fieldsToShow.map(({ fieldName, fieldConfig, presetFileInput }) => {
                  const fieldValue = fileFields[fieldName];
                  // Определяем множественную загрузку динамически из конфига
                  // Для пресетов всегда 1 изображение
                  const isMultiple = false;
                  const maxFiles = 1;
                  // Правильно обрабатываем fieldValue: может быть массивом, объектом или undefined
                  let fieldArray = [];
                  if (fieldValue) {
                    if (Array.isArray(fieldValue)) {
                      fieldArray = fieldValue;
                    } else if (typeof fieldValue === 'object' && fieldValue.url) {
                      // Если это объект с url, преобразуем в массив
                      fieldArray = [fieldValue];
                    }
                  }
                  const hasFiles = fieldArray.length > 0;
                  
                  // Определяем тип поля для правильного отображения превью
                  const { isVideo: isVideoField, isAudio: isAudioField } = getFileFieldType(fieldName, fieldConfig);
                  
                  return (
                    <div key={fieldName} className="form-group">
                      <label>
                        <span className="field-name">
                          {presetFileInput?.label || getFieldDisplayName(fieldName, fieldConfig)}
                        </span>
                        {(presetFileInput?.required || fieldConfig.required) && <span className="required">*</span>}
                      </label>
                      <div 
                        className="image-upload-area"
                        onDrop={(e) => {
                          handleUploadAreaDrop(e, fieldName, fieldConfig, isMultiple, maxFiles);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = 'copy';
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <input
                          type="file"
                          id={`${fieldName}-input`}
                          accept={fileInputAccept(model, fieldName, fieldConfig)}
                          multiple={isMultiple}
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            handleFileSelect(fieldName, files, isMultiple, maxFiles);
                            // Сбрасываем input, чтобы можно было выбрать тот же файл снова
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                        
                        {!hasFiles && (() => {
                          // Определяем тип файла на основе имени поля и конфига
                          const { isVideo: isVideoField, isAudio: isAudioField } = getFileFieldType(fieldName, fieldConfig);
                          const maxFileSize = isSeedance25VideoField(model, fieldName)
                            ? SEEDANCE25_VIDEO_MAX_MB
                            : (fieldConfig?.maxFileSize || (isVideoField ? 100 : isAudioField ? 50 : 20));
                          
                          let uploadText, hintText;
                          if (isVideoField) {
                            uploadText = isMultiple 
                              ? translateCommon('upload_video', language) + ' (множественная загрузка)'
                              : translateCommon('upload_video', language);
                            hintText = isMultiple 
                              ? translateCommon('max_videos', language, { count: maxFiles, size: maxFileSize })
                              : translateCommon('single_video', language, { size: maxFileSize });
                          } else if (isAudioField) {
                            uploadText = isMultiple 
                              ? translateCommon('upload_audio', language) + ' (множественная загрузка)'
                              : translateCommon('upload_audio', language);
                            hintText = isMultiple 
                              ? translateCommon('max_audio', language, { count: maxFiles, size: maxFileSize })
                              : translateCommon('single_audio', language, { size: maxFileSize });
                          } else {
                            uploadText = isMultiple 
                              ? translateCommon('upload_images', language)
                              : translateCommon('upload_image', language);
                            hintText = isMultiple 
                              ? translateCommon('max_files', language, { count: maxFiles })
                              : translateCommon('single_file', language);
                          }
                          
                          return (
                            <label htmlFor={`${fieldName}-input`} className="upload-placeholder">
                              <Upload size={32} />
                              <span className="upload-main-text">
                                {uploadText}
                              </span>
                              <span className="upload-hint">
                                {hintText}
                              </span>
                            </label>
                          );
                        })()}
                        
                        {hasFiles && (
                          <div className={`selected-images ${isVideoField ? 'video-field' : ''}`}>
                            {fieldArray.map((img, index) => {
                              const fileUrl = img.url || img.preview;
                              const fileName = img.name || '';
                              const isVideo = isVideoField || isVideoFile(fileUrl) || fileName.toLowerCase().match(/\.(mp4|webm|mov|avi|mkv)$/i);
                              const isAudio = isAudioField || fileName.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac)$/i);
                              const isPrimaryVideo = isVideoField && isVideo && index === 0;
                              
                              return (
                                <div 
                                  key={img.id || index} 
                                  className={`selected-image${isPrimaryVideo ? ' video-primary' : ''}`}
                                >
                                  {isVideo ? (
                                    <video
                                      key={fileUrl}
                                      controls
                                      playsInline
                                      preload="metadata"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        if (e.clientY > rect.bottom - 48) return;
                                        setViewingImage({ url: fileUrl, isVideo: true });
                                      }}
                                      draggable={false}
                                    >
                                      <source src={fileUrl} type={formVideoMime(fileUrl, fileName)} />
                                    </video>
                                  ) : isAudio ? (
                                    <div 
                                      style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a', cursor: 'pointer' }} 
                                      onClick={() => setViewingImage(fileUrl)}
                                    >
                                      <audio 
                                        src={fileUrl} 
                                        controls
                                        style={{ width: '100%' }}
                                        draggable={false}
                                      />
                                    </div>
                                  ) : (
                                    <img 
                                      src={fileUrl} 
                                      alt={img.name || `${fieldName} image`}
                                      onClick={() => setViewingImage(fileUrl)}
                                      style={{ cursor: 'pointer' }}
                                      draggable={false}
                                    />
                                  )}
                                  <button
                                    type="button"
                                    className="remove-image-btn"
                                    onClick={() => {
                                      if (isMultiple) {
                                        setFileFields(prev => ({
                                          ...prev,
                                          [fieldName]: fieldArray.filter((_, i) => i !== index)
                                        }));
                                      } else {
                                        setFileFields(prev => {
                                          const newFields = { ...prev };
                                          delete newFields[fieldName];
                                          return newFields;
                                        });
                                      }
                                    }}
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {fieldConfig.description && (
                        <div className="field-description">{fieldConfig.description}</div>
                      )}
                    </div>
                  );
                });
              } else {
                // Обычная логика без пресета
                const fileFieldsList = getVisibleFileFields();
                let videoRefNum = 0, imageRefNum = 0;
                const refMetaList = fileFieldsList.map(({ name, config }) => {
                  const { isVideo } = getFileFieldType(name, config);
                  if (isVideo) {
                    videoRefNum++;
                    return { type: 'Video', num: videoRefNum, ref: `@Video${videoRefNum}` };
                  }
                  const ref = name === 'start_image_url' ? '@start_image' : name === 'end_image_url' ? '@end_image' : (name === 'image_url' && fileFieldsList.some(f => f.name === 'end_image_url')) ? '@start_image' : `@Image${++imageRefNum}`;
                  const num = ref === '@start_image' ? 1 : ref === '@end_image' ? 2 : imageRefNum;
                  return { type: 'Image', num, ref };
                });
                return fileFieldsList.map(({ name: fieldName, config: fieldConfig }, fieldIndex) => {
                  const refMeta = isKlingWithElements(model) ? refMetaList[fieldIndex] : null;
                  const fieldValue = fileFields[fieldName];
              // Определяем множественную загрузку динамически из конфига
              // video_url — всегда одно видео для редактирования
              const isMultiple = (fieldName === 'video_url') ? false : (activePreset ? false : (fieldConfig.type === 'array' || fieldConfig.multiple === true || 
                                (model.supports_multiple_images && fieldName === fileFieldsList[0]?.name)));
              const maxFiles = (fieldName === 'video_url') ? 1 : (activePreset ? 1 : (fieldConfig?.maxItems || fieldConfig?.max || (isMultiple ? 10 : 1)));
              // Правильно обрабатываем fieldValue: может быть массивом, объектом или undefined
              let fieldArray = [];
              if (fieldValue) {
                if (Array.isArray(fieldValue)) {
                  fieldArray = fieldValue;
                } else if (typeof fieldValue === 'object' && fieldValue.url) {
                  // Если это объект с url, преобразуем в массив
                  fieldArray = [fieldValue];
                }
              }
              const hasFiles = fieldArray.length > 0;
              
              // Определяем тип поля для правильного отображения превью
              const { isVideo: isVideoField, isAudio: isAudioField } = getFileFieldType(fieldName, fieldConfig);
              
                  return (
                    <div key={fieldName} className="form-group">
                      <label>
                        <span className="field-name">
                          {getFieldDisplayName(fieldName, fieldConfig)}
                        </span>
                        {fieldConfig.required && <span className="required">*</span>}
                      </label>
                  <div 
                    className="image-upload-area"
                    onDrop={(e) => {
                      handleUploadAreaDrop(e, fieldName, fieldConfig, isMultiple, maxFiles);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = 'copy';
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <input
                      type="file"
                      id={`${fieldName}-input`}
                      accept={fileInputAccept(model, fieldName, fieldConfig)}
                      multiple={isMultiple}
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        handleFileSelect(fieldName, files, isMultiple, maxFiles);
                        // Сбрасываем input, чтобы можно было выбрать тот же файл снова
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                    />
                    
                    {!hasFiles && (() => {
                      // Определяем тип файла на основе имени поля и конфига
                      const { isVideo: isVideoField, isAudio: isAudioField } = getFileFieldType(fieldName, fieldConfig);
                      const maxFileSize = isSeedance25VideoField(model, fieldName)
                        ? SEEDANCE25_VIDEO_MAX_MB
                        : (fieldConfig?.maxFileSize || (isVideoField ? 100 : isAudioField ? 50 : 20));
                      
                      let uploadText, hintText;
                      if (isVideoField) {
                        uploadText = isMultiple 
                          ? translateCommon('upload_video', language) + ' (множественная загрузка)'
                          : translateCommon('upload_video', language);
                        hintText = isMultiple 
                          ? translateCommon('max_videos', language, { count: maxFiles, size: maxFileSize })
                          : translateCommon('single_video', language, { size: maxFileSize });
                      } else if (isAudioField) {
                        uploadText = isMultiple 
                          ? translateCommon('upload_audio', language) + ' (множественная загрузка)'
                          : translateCommon('upload_audio', language);
                        hintText = isMultiple 
                          ? translateCommon('max_audios', language, { count: maxFiles, size: maxFileSize })
                          : translateCommon('single_audio', language, { size: maxFileSize });
                      } else {
                        uploadText = isMultiple 
                          ? translateCommon('upload_images', language)
                          : translateCommon('upload_image', language);
                        hintText = isMultiple 
                          ? translateCommon('max_files', language, { count: maxFiles })
                          : translateCommon('single_file', language);
                      }
                      
                      return (
                        <label htmlFor={`${fieldName}-input`} className="upload-placeholder">
                          <Upload size={32} />
                          <span className="upload-main-text">
                            {uploadText}
                          </span>
                          <span className="upload-hint">
                            {hintText}
                          </span>
                        </label>
                      );
                    })()}
                    {hasFiles && (
                      <div className={`selected-images ${isVideoField ? 'video-field' : ''}`}>
                        {fieldArray.map((img, index) => {
                          const fileUrl = img.url || img.preview;
                          const fileName = img.name || '';
                          const isVideo = isVideoField || isVideoFile(fileUrl) || fileName.toLowerCase().match(/\.(mp4|webm|mov|avi|mkv)$/i);
                          const isAudio = isAudioField || fileName.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac)$/i);
                          const canDrag = isMultiple && fieldArray.length > 1;
                          const isPrimaryVideo = isVideoField && isVideo && index === 0;
                          
                          const itemRef = refMeta && ((fieldConfig?.type === 'array' || fieldConfig?.multiple)
                            ? `@${refMeta.type}${refMeta.num + index}`
                            : refMeta.ref);
                          const colorOffset = refMeta ? (refMeta.type === 'Video' ? 0 : refMeta.type === 'Image' ? 10 : 20) : 0;
                          const itemColorClass = refMeta ? (KLING_ELEMENT_COLORS[(colorOffset + refMeta.num - 1 + index) % KLING_ELEMENT_COLORS.length]?.replace('kling-el-', 'el-') || 'el-sky') : null;
                          return (
                            <div
                              key={index}
                              className="selected-image-wrapper"
                              draggable={canDrag}
                              onDragStart={(e) => {
                                if (canDrag) {
                                  e.stopPropagation();
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.dataTransfer.setData('text/plain', index.toString());
                                  e.dataTransfer.setData('application/reorder', 'true');
                                  e.currentTarget.classList.add('dragging');
                                }
                              }}
                              onDragEnd={(e) => {
                                e.currentTarget.classList.remove('dragging');
                                document.querySelectorAll('.selected-image').forEach(el => {
                                  el.classList.remove('drag-over');
                                });
                              }}
                              onDragOver={(e) => {
                                if (canDrag) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.dataTransfer.dropEffect = 'move';
                                  e.currentTarget.querySelector('.selected-image')?.classList.add('drag-over');
                                }
                              }}
                              onDragLeave={(e) => {
                                e.currentTarget.querySelector('.selected-image')?.classList.remove('drag-over');
                              }}
                              onDrop={(e) => {
                                if (canDrag) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.currentTarget.querySelector('.selected-image')?.classList.remove('drag-over');
                                  const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                                  const dropIndex = index;
                                  if (dragIndex !== dropIndex && !isNaN(dragIndex)) {
                                    handleFileReorder(fieldName, dragIndex, dropIndex);
                                  }
                                }
                              }}
                            >
                            <div className={`selected-image${isPrimaryVideo ? ' video-primary' : ''}`}>
                              <div className="selected-image-media-wrap">
                                {isVideo ? (
                                  <video
                                    key={fileUrl}
                                    controls
                                    playsInline
                                    preload="metadata"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: canDrag ? 'grab' : 'pointer' }}
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      if (e.clientY > rect.bottom - 48) return;
                                      setViewingImage({ url: fileUrl, isVideo: true });
                                    }}
                                    draggable={false}
                                  >
                                    <source src={fileUrl} type={formVideoMime(fileUrl, fileName)} />
                                  </video>
                                ) : isAudio ? (
                                  <div 
                                    style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a', cursor: canDrag ? 'grab' : 'pointer' }} 
                                    onClick={() => setViewingImage(fileUrl)}
                                  >
                                    <audio 
                                      src={fileUrl} 
                                      controls
                                      style={{ width: '100%' }}
                                      draggable={false}
                                    />
                                  </div>
                                ) : (
                                  <img 
                                    src={fileUrl} 
                                    alt={img.name || `${fieldName} image`}
                                    onClick={() => setViewingImage(fileUrl)}
                                    style={{ cursor: canDrag ? 'grab' : 'pointer' }}
                                    draggable={false}
                                  />
                                )}
                                <button
                                  type="button"
                                  className="remove-image-btn"
                                  onClick={() => handleFileRemove(fieldName, index)}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            </div>
                            {refMeta && itemRef && (
                              <div className="ref-badge-outside">
                                <span className={`kling-element-badge ref-badge-on-preview rounded px-1 py-0.5 text-xs font-mono text-content-strong ${itemColorClass}`} title={`В промпте: ${itemRef}`}>
                                  {itemRef}
                                </span>
                              </div>
                            )}
                            </div>
                          );
                        })}
                        
                        {isMultiple && fieldArray.length < maxFiles && (
                          <label htmlFor={`${fieldName}-input`} className="add-more-btn">
                            <Plus size={24} />
                            <span>Добавить {isVideoField ? 'видео' : isAudioField ? 'аудио' : 'изображение'}</span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                  {refMeta && (
                    <p className="field-hint mt-1">
                      В промпте ссылайтесь как{' '}
                      {(fieldConfig?.type === 'array' || fieldConfig?.multiple) && fieldArray.length > 0
                        ? Array.from({ length: fieldArray.length }, (_, i) => {
                            const refTag = `@${refMeta.type}${refMeta.num + i}`;
                            const colorOffset = refMeta.type === 'Video' ? 0 : refMeta.type === 'Image' ? 10 : 20;
                            const colorClass = KLING_ELEMENT_COLORS[(colorOffset + refMeta.num - 1 + i) % KLING_ELEMENT_COLORS.length]?.replace('kling-el-', 'el-') || 'el-sky';
                            return <span key={refTag} className={`kling-element-badge rounded px-0.5 font-mono text-content-strong ${colorClass}`}>{refTag}</span>;
                          }).reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ', ', el]), [])
                        : (() => {
                            const colorOffset = refMeta.type === 'Video' ? 0 : refMeta.type === 'Image' ? 10 : 20;
                            const colorClass = KLING_ELEMENT_COLORS[(colorOffset + refMeta.num - 1) % KLING_ELEMENT_COLORS.length]?.replace('kling-el-', 'el-') || 'el-sky';
                            return <span className={`kling-element-badge rounded px-0.5 font-mono text-content-strong ${colorClass}`}>{refMeta.ref}</span>;
                          })()}
                    </p>
                  )}
                  {/* При наличии подсказки с рефами описание из БД не показываем, чтобы не дублировать */}
                  {!activePreset && !refMeta && fieldConfig.description && (
                    <div className="field-description">
                      {fieldConfig.description}
                    </div>
                  )}
                </div>
              );
            })}
            })()}

            {/* Секция "Элементы" для Kling v3 / o3: @Element1, @Element2 в промпте */}
            {isKlingWithElements(model) && !activePreset && (
              <div className="form-group kling-elements-section">
                <label className="text-content-strong font-normal font-sans text-sm">Элементы</label>
                <p className="field-hint mb-2">Добавьте персонажей или объекты и ссылайтесь на них в промпте как @Element1, @Element2 и т.д.</p>
                <div className="kling-elements-list">
                  {klingElements.map((el, idx) => {
                    const colorIndex = (20 + idx) % KLING_ELEMENT_COLORS.length;
                    const badgeClass = KLING_ELEMENT_COLORS[colorIndex]?.replace('kling-el-', 'el-') || 'el-sky';
                    const frontalVal = el.frontal ?? null;
                    const refList = Array.isArray(el.reference) ? el.reference : [];
                    const videoVal = el.video ?? null;
                    const setEl = (field, value) => {
                      setKlingElements(prev => {
                        let update = { [field]: value };
                        if (field === 'reference') update.video = null;
                        else if (field === 'video') update.reference = [];
                        return prev.map((e, i) => i === idx ? { ...e, ...update } : e);
                      });
                    };
                    const addRef = (item) => setEl('reference', [...refList, item]);
                    const removeRef = (i) => setEl('reference', refList.filter((_, j) => j !== i));
                    return (
                      <div key={idx} className="kling-element-card">
                        <div className="kling-element-header">
                          <span className={`kling-element-badge ${badgeClass}`}>Element {idx + 1}</span>
                          <span className="kling-element-ref-hint">В промпте: <code>@{`Element${idx + 1}`}</code></span>
                          <button type="button" className="kling-element-remove" onClick={() => setKlingElements(prev => prev.filter((_, i) => i !== idx))} aria-label="Удалить элемент">
                            <X size={14} />
                          </button>
                        </div>
                        <div className="kling-element-fields">
                          <div className="kling-element-field">
                            <span className="kling-element-label">Фронтальное изображение</span>
                            <div className="image-upload-area kling-el-upload" onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f && f.type.startsWith('image/')) { if (f.size > 10 * 1024 * 1024) { toast.error('Фронтальное изображение: макс. 10 МБ'); return; } setEl('frontal', { id: Date.now(), file: f, preview: URL.createObjectURL(f) }); } }} onDragOver={(e) => e.preventDefault()}>
                              {frontalVal && (frontalVal.preview || frontalVal.url) ? (
                                <div className="selected-image kling-el-selected">
                                  <button type="button" className="kling-el-preview-click" onClick={() => setViewingImage(frontalVal.preview || frontalVal.url)} title="Нажмите для просмотра">
                                    <img src={frontalVal.preview || frontalVal.url} alt="" draggable={false} />
                                  </button>
                                  <button type="button" className="remove-image-btn" onClick={(e) => { e.stopPropagation(); setEl('frontal', null); }}><X size={16} /></button>
                                </div>
                              ) : (
                                <label className="upload-placeholder" htmlFor={`kling-el-${idx}-frontal`}><Upload size={20} /> Выберите или перетащите изображение</label>
                              )}
                              <input type="file" id={`kling-el-${idx}-frontal`} accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files[0]; if (f) { if (f.size > 10 * 1024 * 1024) { toast.error('Фронтальное изображение: макс. 10 МБ'); e.target.value = ''; return; } setEl('frontal', { id: Date.now(), file: f, preview: URL.createObjectURL(f) }); } e.target.value = ''; }} />
                            </div>
                            <p className="kling-field-hint">Макс. 10 МБ, мин. 300×300 px, соотношение сторон 0.4–2.5</p>
                          </div>
                          <div className="kling-element-field">
                            <span className="kling-element-label">Референс: изображения (1–3) или видео — на выбор</span>
                            <div
                              className="kling-ref-grid"
                              onDrop={(e) => {
                                if (videoVal) return;
                                e.preventDefault();
                                const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                                const toAdd = Math.min(files.length, 3 - refList.length);
                                if (toAdd <= 0) return;
                                const newItems = files.slice(0, toAdd).map(f => ({ id: Date.now() + Math.random(), file: f, preview: URL.createObjectURL(f) }));
                                setEl('reference', [...refList, ...newItems]);
                              }}
                              onDragOver={(e) => e.preventDefault()}
                            >
                              {refList.map((r, ri) => (
                                <div key={ri} className="selected-image">
                                  <button type="button" className="kling-el-preview-click" onClick={() => setViewingImage(r.preview || r.url)} title="Нажмите для просмотра"><img src={r.preview || r.url} alt="" draggable={false} /></button>
                                  <button type="button" className="remove-image-btn" onClick={(e) => { e.stopPropagation(); removeRef(ri); }}><X size={16} /></button>
                                </div>
                              ))}
                              {refList.length < 3 && !videoVal && (
                                <>
                                  <label className="upload-placeholder small" htmlFor={`kling-el-${idx}-ref`}>+ изображения</label>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: 'none' }}
                                    id={`kling-el-${idx}-ref`}
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
                                      const toAdd = Math.min(files.length, 3 - refList.length);
                                      if (toAdd > 0) {
                                        const newItems = files.slice(0, toAdd).map(f => ({ id: Date.now() + Math.random(), file: f, preview: URL.createObjectURL(f) }));
                                        setEl('reference', [...refList, ...newItems]);
                                      }
                                      e.target.value = '';
                                    }}
                                  />
                                </>
                              )}
                            </div>
                            {refList.length === 0 && !videoVal && (frontalVal?.preview || frontalVal?.url) && (
                              <p className="kling-field-error">Добавьте референс: либо изображения (1–3), либо видео</p>
                            )}
                            {videoVal && <p className="kling-field-hint">Выбрано видео. Удалите его, чтобы загрузить изображения.</p>}
                            {!videoVal && <p className="kling-field-hint">1–3 изображения или одно видео. Минимум одно обязательно.</p>}
                          </div>
                          <div className="kling-element-field">
                            <span className="kling-element-label">Видео (вместо изображений)</span>
                            <div className={`image-upload-area kling-el-upload ${refList.length > 0 ? 'kling-el-disabled' : ''}`} onDrop={(e) => { if (refList.length > 0) return; e.preventDefault(); const f = e.dataTransfer.files[0]; if (f && f.type.startsWith('video/')) { handleKlingVideoSelect(f, (item) => setEl('video', item), () => {}); } }} onDragOver={(e) => e.preventDefault()}>
                              {videoVal && (videoVal.preview || videoVal.url) ? (
                                <div className="selected-image video-primary kling-el-selected kling-video-preview-wrap">
                                  <video src={videoVal.preview || videoVal.url} controls playsInline />
                                  <button type="button" className="remove-image-btn" onClick={(e) => { e.stopPropagation(); setEl('video', null); }}><X size={16} /></button>
                                  <button type="button" className="kling-el-expand-btn" onClick={() => setViewingImage({ url: videoVal.preview || videoVal.url, isVideo: true })} title="Открыть в полном размере"><Maximize2 size={16} /></button>
                                </div>
                              ) : refList.length > 0 ? (
                                <p className="kling-field-hint">Выбраны изображения. Удалите их, чтобы загрузить видео.</p>
                              ) : (
                                <label className="upload-placeholder" htmlFor={`kling-el-${idx}-video`}><Upload size={20} /> Выберите или перетащите видео</label>
                              )}
                              {refList.length === 0 && <input type="file" id={`kling-el-${idx}-video`} accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files[0]; if (f) handleKlingVideoSelect(f, (item) => setEl('video', item), () => { e.target.value = ''; }); e.target.value = ''; }} />}
                            </div>
                            <p className="kling-field-hint">Макс. 200 МБ, 720–2160 px, 3–10 сек, 24–60 FPS. Только один элемент с видео в запросе.</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="kling-add-element-btn" onClick={() => setKlingElements(prev => [...prev, { frontal: null, reference: [], video: null }])}>
                  <Plus size={16} /> Добавить элемент
                </button>
              </div>
            )}

            {/* Динамические параметры (все кроме prompt и file-полей - определяются динамически) */}
            {/* При работе с пресетом скрываем все динамические параметры, кроме критически важных */}
            {model.input_parameters && Object.entries(model.input_parameters).map(([paramName, paramConfig]) => {
              // Пропускаем, если paramConfig отсутствует
              if (!paramConfig) {
                return null;
              }
              
              // Пропускаем prompt и все поля для загрузки файлов - у них кастомная отрисовка (определяем динамически)
              if (paramName === 'prompt' || isFileField(paramConfig, paramName)) {
                return null;
              }
              if (isHiddenParam(paramConfig)) {
                return null;
              }
              if (paramName === 'generation_mode' && !activePreset) {
                return null;
              }
              const showWhen = paramConfig.show_when;
              if (showWhen && typeof showWhen === 'object') {
                const visible = Object.entries(showWhen).every(([key, expected]) => {
                  const current = dynamicParams[key] !== undefined
                    ? dynamicParams[key]
                    : model.default_parameters?.[key];
                  return current === expected;
                });
                if (!visible) return null;
              }
              if (
                isSeedance25Model(model) &&
                (isSeedance2ImageOnlyMode(seedance2Mode) || isSeedance25VideoEditMode(model, seedance2Mode)) &&
                paramName === 'aspect_ratio'
              ) {
                return null;
              }
              if (
                isSeedance25Model(model) &&
                seedanceDraft &&
                paramName === 'resolution'
              ) {
                return null;
              }
              if (
                isSeedance25VideoEditMode(model, seedance2Mode) &&
                paramName === 'duration'
              ) {
                return null;
              }
              // Пропускаем устаревшие (deprecated) поля
              const description = paramConfig?.description || '';
              const isDeprecated = description.toLowerCase().includes('deprecated') || 
                                 description.toLowerCase().includes('устарело') ||
                                 (description.toLowerCase().includes('используйте') && description.toLowerCase().includes('вместо'));
              if (isDeprecated) {
                return null;
              }
              
              // При работе с пресетом скрываем все динамические параметры
              // Оставляем только критически важные (если нужно)
              if (activePreset) {
                if (isSeedance2Unified) {
                  if (paramName !== 'aspect_ratio' && paramName !== 'generation_mode') {
                    return null;
                  }
                } else if (paramName !== 'aspect_ratio') {
                  return null;
                }
              }
              
              let fieldConfigForRender = paramConfig;
              if (isSeedance2Unified && paramName === 'resolution') {
                const allowedRes = getSeedanceResolutionOptions(model, seedance2Mode);
                fieldConfigForRender = {
                  ...paramConfig,
                  enum: allowedRes,
                };
              }

              return (
                <DynamicFormField
                  key={paramName}
                  fieldName={paramName}
                  fieldConfig={fieldConfigForRender}
                  value={dynamicParams[paramName]}
                  onChange={(value) => handleDynamicParamChange(paramName, value)}
                  onValidationChange={(error) => handleValidationChange(paramName, error)}
                />
              );
            })}

            {/* Submit Button */}
            <div className="generate-bar">
              <div className="generate-inner">
                <button
                  type="submit"
                  className="generate-btn"
                  disabled={(calculatedCost > 0 && currentBalance < calculatedCost) || hasValidationErrors || isProcessing || !!activeJobId || isSubmitting}
                >
                  {isSubmitting || isProcessing ? (
                    <>
                      <Loader className="spinning" size={20} />
                      {isSubmitting && !isProcessing ? 'Загрузка...' : 'Генерация...'}
                    </>
                  ) : (
                    <>
                      <Zap size={20} />
                      {(() => {
                        const isKlingMotionControl = isKlingMotionControlModel(model);
                        // Для kling motion-control (v2.6 / v3), если цена еще не рассчитана (0), показываем просто "Сгенерировать"
                        if (isKlingMotionControl && calculatedCost === 0) {
                          return 'Сгенерировать';
                        }
                        if (isSeedance25Model(model) && seedanceDraft) {
                          if (calculatedCost <= 0) return 'Черновик 480p';
                          return `Черновик 480p за ${calculatedCost} ${getBonusWord(calculatedCost)}`;
                        }
                        return `Сгенерировать за ${calculatedCost} ${getBonusWord(calculatedCost)}`;
                      })()}
                    </>
                  )}
                </button>
              </div>
            </div>

            {calculatedCost > 0 && currentBalance < calculatedCost && (
              <div className="insufficient-balance-warning">
                <Zap size={16} />
                <span>Недостаточно токенов. Пополните баланс.</span>
              </div>
            )}
          </form>
        </div>

        {/* Разделитель между панелями (десктоп) */}
        <div className="panel-separator" aria-hidden="true" />

        <div className="output-panel">
          <h2>Результат</h2>

          {isProcessing && (
            <div className="processing-state">
              <div className="processing-animation">
                <div className="spinner-large"></div>
              </div>
              <p>
                {model.category === 'video_generation'
                  ? 'Генерация видео…'
                  : (model.category === 'music_generation' || model.category === 'speech_generation')
                    ? 'Генерация аудио…'
                    : 'Генерация изображения…'}
              </p>
              <span className="processing-time">~{estimatedTime} секунд</span>
            </div>
          )}

          {result && !isProcessing && result.output_image_url && 
           result.output_image_url !== 'None' && 
           result.output_image_url !== 'null' &&
           result.output_image_url.trim() !== '' && (
            <div className="result-container">
              <div 
                className={`result-image${isAudioFile(result.output_image_url) ? ' result-audio' : ''}`} 
                draggable={true}
                onDragStart={(e) => {
                  // Сохраняем URL результата в dataTransfer для drag & drop
                  e.dataTransfer.setData('text/plain', result.output_image_url);
                  e.dataTransfer.setData('application/json', JSON.stringify({
                    type: isVideoFile(result.output_image_url) ? 'video' : 'image',
                    url: result.output_image_url
                  }));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={(e) => {
                  if (isAudioFile(result.output_image_url)) return;
                  if (isVideoFile(result.output_image_url)) {
                    // Для видео: проверяем координаты клика
                    if (!videoRef.current) return;
                    
                    const video = videoRef.current;
                    const rect = video.getBoundingClientRect();
                    const clickY = e.clientY - rect.top;
                    const videoHeight = rect.height;
                    
                    // Проверяем, что клик не по controls панели (нижние 60px)
                    const controlsHeight = 60;
                    const isClickOnControls = clickY > videoHeight - controlsHeight;
                    
                    // Проверяем, что клик не по интерактивным элементам
                    const target = e.target;
                    const isInteractiveElement = target.tagName === 'BUTTON' || 
                                               target.tagName === 'INPUT' ||
                                               target.tagName === 'PROGRESS' ||
                                               target.closest('button') ||
                                               target.closest('input') ||
                                               target.closest('progress');
                    
                    // Если клик не по controls и не по интерактивным элементам - переключаем воспроизведение
                    if (!isClickOnControls && !isInteractiveElement) {
                      e.preventDefault();
                      e.stopPropagation();
                      if (video.paused) {
                        video.play().catch(err => {
                          console.error('Play error:', err);
                          toast.error('Ошибка воспроизведения видео');
                        });
                      } else {
                        video.pause();
                      }
                    }
                  } else {
                    // Для изображений: открываем модальное окно только если изображение загружено
                    if (imageBlobUrl && !imageLoadError && !imageLoading) {
                      setViewingImage(imageBlobUrl || result.output_image_url);
                    }
                  }
                }}
              >
                {isAudioFile(result.output_image_url) ? (
                  <div className="audio-result">
                    {(result.audio_tracks?.length ? result.audio_tracks : [{ url: result.output_image_url, title: '' }]).map((track, index) => (
                      <div className="audio-track" key={track.url || index}>
                        {(track.title || (result.audio_tracks?.length > 1)) && (
                          <div className="audio-track-title">{track.title || `Вариант ${index + 1}`}</div>
                        )}
                        <audio src={track.url} controls preload="metadata" />
                      </div>
                    ))}
                  </div>
                ) : isVideoFile(result.output_image_url) ? (
                  <video 
                    ref={videoRef}
                    src={result.output_image_url} 
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                    onContextMenu={(e) => e.preventDefault()} // Отключаем контекстное меню
                    onClick={(e) => {
                      // Обрабатываем клик на video элементе
                      if (!videoRef.current) return;
                      
                      const video = videoRef.current;
                      const target = e.target;
                      
                      // Проверяем, что клик не по интерактивным элементам controls
                      const isInteractiveElement = target.tagName === 'BUTTON' || 
                                                 target.tagName === 'INPUT' ||
                                                 target.tagName === 'PROGRESS' ||
                                                 target.closest('button') ||
                                                 target.closest('input') ||
                                                 target.closest('progress');
                      
                      // Если клик по самому video элементу (не по controls) - переключаем воспроизведение
                      if (target.tagName === 'VIDEO' && !isInteractiveElement) {
                        const rect = video.getBoundingClientRect();
                        const clickY = e.clientY - rect.top;
                        const videoHeight = rect.height;
                        
                        // Проверяем, что клик не в нижней части (где controls панель)
                        const controlsHeight = 60;
                        const isClickOnControls = clickY > videoHeight - controlsHeight;
                        
                        if (!isClickOnControls) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (video.paused) {
                            video.play().catch(err => {
                              console.error('Play error:', err);
                              toast.error('Ошибка воспроизведения видео');
                            });
                          } else {
                            video.pause();
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <>
                    {imageLoading && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                        background: '#1a1a1a',
                        borderRadius: '12px'
                      }}>
                        <Loader className="spinning" size={32} />
                        {imageLoadProgress > 0 && imageLoadProgress < 100 && (
                          <div style={{
                            width: '200px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <div style={{
                              width: '100%',
                              height: '6px',
                              background: 'rgba(255,255,255,0.1)',
                              borderRadius: '3px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${imageLoadProgress}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #7A1BB4, #9b2ee0)',
                                borderRadius: '3px',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <span style={{
                              fontSize: '0.8rem',
                              color: '#a2a2a2',
                              fontVariantNumeric: 'tabular-nums'
                            }}>
                              Загрузка {imageLoadProgress}%
                            </span>
                          </div>
                        )}
                        {imageLoadProgress === 0 && (
                          <span style={{
                            fontSize: '0.8rem',
                            color: '#a2a2a2'
                          }}>
                            Загрузка изображения…
                          </span>
                        )}
                      </div>
                    )}
                    {imageLoadError ? (
                      <div style={{
                        width: '100%',
                        minHeight: '300px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                        padding: '2rem',
                        background: '#1a1a1a',
                        borderRadius: '12px',
                        color: '#a2a2a2'
                      }}>
                        <AlertCircle size={48} />
                        <p>Ошибка загрузки изображения</p>
                        <button
                          onClick={() => {
                            // Перезапускаем загрузку через fetch — отменяем текущий контроллер и меняем URL
                            if (imageFetchControllerRef.current) {
                              imageFetchControllerRef.current.abort();
                            }
                            setImageLoadError(false);
                            setImageLoading(true);
                            setImageLoadProgress(0);
                            imageRetryCountRef.current = 0;

                            // Очищаем предыдущий blob
                            if (imageBlobUrl) {
                              URL.revokeObjectURL(imageBlobUrl);
                              setImageBlobUrl(null);
                            }

                            const controller = new AbortController();
                            imageFetchControllerRef.current = controller;
                            const retryUrl = result.output_image_url + (result.output_image_url.includes('?') ? '&' : '?') + '_retry=' + Date.now();

                            (async () => {
                              try {
                                const response = await fetch(retryUrl, { signal: controller.signal, cache: 'no-cache' });
                                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                                const contentLength = response.headers.get('Content-Length');
                                const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
                                if (response.body && totalBytes > 0) {
                                  const reader = response.body.getReader();
                                  const chunks = [];
                                  let receivedBytes = 0;
                                  while (true) {
                                    const { done, value } = await reader.read();
                                    if (done) break;
                                    chunks.push(value);
                                    receivedBytes += value.length;
                                    setImageLoadProgress(Math.round((receivedBytes / totalBytes) * 100));
                                  }
                                  const blob = createTypedMediaBlob(chunks, retryUrl, response);
                                  if (controller.signal.aborted) return;
                                  if (blob.size < totalBytes) throw new Error('Incomplete download');
                                  const blobUrl = URL.createObjectURL(blob);
                                  setImageBlobUrl(blobUrl);
                                  setImageLoading(false);
                                  setImageLoadProgress(100);
                                } else {
                                  const rawBlob = await response.blob();
                                  if (controller.signal.aborted) return;
                                  const blob = createTypedMediaBlob(rawBlob, retryUrl, response);
                                  const blobUrl = URL.createObjectURL(blob);
                                  setImageBlobUrl(blobUrl);
                                  setImageLoading(false);
                                  setImageLoadProgress(100);
                                }
                              } catch (err) {
                                if (err.name === 'AbortError') return;
                                console.error('Ошибка повторной загрузки:', err);
                                setImageLoading(false);
                                setImageLoadError(true);
                              }
                            })();
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#7A1BB4',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                        >
                          Попробовать снова
                        </button>
                      </div>
                    ) : (
                      imageBlobUrl && (
                        <img 
                          ref={resultImageRef}
                          src={imageBlobUrl} 
                          alt="Result"
                          loading="eager"
                          style={{ 
                            display: 'block',
                            width: '100%',
                            height: 'auto'
                          }}
                        />
                      )
                    )}
                  </>
                )}
                {!isVideoFile(result.output_image_url) && !isAudioFile(result.output_image_url) && imageBlobUrl && !imageLoadError && !imageLoading && (
                  <div className="result-overlay">
                    <ImageIcon size={32} />
                    <span>Нажмите для увеличения</span>
                  </div>
                )}
              </div>

              <div className="result-actions">
                <div className="result-action-stack">
                  <button
                    className="action-btn primary"
                    onClick={() => (
                      isAudioFile(result.output_image_url) || result.audio_tracks?.length
                        ? downloadAudioResults()
                        : downloadResult(result.output_image_url)
                    )}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <>
                        <Loader size={18} className="spinning" />
                        Загрузка...
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        Скачать
                      </>
                    )}
                  </button>
                  {draftUpscaleReady && (
                    <button
                      type="button"
                      className="action-btn primary"
                      onClick={finishSeedanceDraft}
                      disabled={isSubmitting || isProcessing || !!activeJobId || (draftUpscaleCost > 0 && currentBalance < draftUpscaleCost)}
                    >
                      <Zap size={18} />
                      {draftUpscaleCost > 0
                        ? `Догенерить в 1080 за ${draftUpscaleCost} ${getBonusWord(draftUpscaleCost)}`
                        : 'Догенерить в 1080'}
                    </button>
                  )}
                  {draftUpscaleExpired && (
                    <p className="field-hint">Срок черновика истёк, догенерация в 1080p недоступна.</p>
                  )}
                </div>
                {!isAudioFile(result.output_image_url) && (
                <button
                  className="action-btn secondary"
                  onClick={moveResultToForm}
                >
                  <ArrowUp size={18} />
                  Переместить в форму
                </button>
                )}
              </div>

              {result.processing_time_seconds && (
                <div className="result-stats">
                  <span>Время генерации: {result.processing_time_seconds}с</span>
                  <span>Потрачено: {result.bonuses_spent} токенов</span>
                </div>
              )}
            </div>
          )}

          {/* Блок с ошибкой - отображается под результатом или вместо него */}
          {errorMessage && !isProcessing && (
            <div className="error-container">
              <div className="error-header">
                <AlertCircle size={20} />
                <h3>Ошибка генерации</h3>
              </div>
              <div className="error-message">
                {errorMessage}
              </div>
              <button
                className="error-dismiss-btn"
                onClick={() => setErrorMessage(null)}
                aria-label="Закрыть сообщение об ошибке"
              >
                ✕
              </button>
            </div>
          )}

          {!result && !errorMessage && !isProcessing && (
            <div className="empty-result">
              <ImageIcon size={64} />
              <p>Результат появится здесь</p>
              <span>Заполните параметры и нажмите "Сгенерировать"</span>
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {viewingImage && (
        <ImageModal
          imageUrl={viewingImage}
          onClose={() => setViewingImage(null)}
        />
      )}
    </div>
  );
}

export default ModelPlayground;

